// All graphics are generated at load time: tiles are painted procedurally,
// characters come from 16x16 pixel matrices. No external assets.
import { RNG, mix } from './rng.ts';
import * as M3 from './model3d.ts';

export const TS = 16;

export const T = {
  GROUND: 0, SAND: 1, WATER: 2, SHALLOW: 3, TREE: 4, ROCK: 5, RUBBLE: 6,
  /* RETIRED IDS (LS-226). MOUTH, HOLE, FLOOR, BLOCK, WALL, DOOR, LOCK, BARS,
   * STAIRS, IDOL, PEDESTAL, PLATE and PLATE_ON were the hollows' and caves'
   * tiles. Nothing generates or paints them now; the numbers stay so every
   * other id keeps its value in the saves and the checks. */
  MOUTH: 7, HOLE: 8, MOSS: 9,
  FLOOR: 10, BLOCK: 11, WALL: 12, DOOR: 13, LOCK: 14, BARS: 15,
  STAIRS: 16, IDOL: 17, CRACK: 18, PEDESTAL: 19,
  PLATE: 20, PLATE_ON: 21, PUSH: 22, BRAZIER: 23, BRAZIER_LIT: 24, THORN: 25,
  DEBRIS: 26, SCORCH: 27,
  /** A spore nest: it keeps sending things out until it is cut down. */
  NEST: 28,
  /** A standing stone cut by whoever dug the hollows, bearing deepmark.
   *
   *  This claimed the stone "names the charm that opens it — the one place in
   *  the game the per-seed charm order is stated before you meet it". No stone
   *  has named a charm since round 30: measured over 1,722 stones on 60 seeds,
   *  there are 9 distinct sentences and not one of them is a charm name. What a
   *  stone carries is a NOUN and a BEARING — what is over there and which way.
   *  It also claimed the stone "stands beside a tier barrier"; 272 of 1,722
   *  (15.8%) do not, because a cache marker stands wherever its cache is.
   *
   *  Solid, so it reads as masonry and cannot be walked through. */
  STONE: 29,
  /** A CAIRN: stone the diggers carried out of a hollow and STACKED rather
   *  than dumped. The spoil heaps at a mouth are what they threw away; a
   *  cairn is what they set down on purpose, out on open ground, with
   *  something worth carrying still inside it. Solid, and it opens to a seed
   *  pod — which is the point of it: until round 35 the pods charm had
   *  exactly one use above ground, opening the tier gate it was handed for.
   *  A cairn is the second, and it is optional, which is the difference
   *  between a key and a tool. */
  CAIRN: 30,
  /** THE FOOT OF A TERRACE STEP — the one tile the world has for height.
   *
   *  The overworld is cut into four terraces and the boundary between two of
   *  them is a cliff, so the tile at the foot of a step is a wall you see the
   *  FACE of. That is a different picture from a boulder, which is what it was
   *  standing in for: measured at 1:1, terrace boundaries read as flat changes
   *  of ground colour with scattered brown rocks along them, which is not
   *  height, it is litter.
   *
   *  Neighbour-aware, like the shoreline: the variant's low nibble says which
   *  sides are HIGHER ground, read from the terrace field rather than from the
   *  tiles, because height is a property of the land and not of what happens
   *  to be drawn on it. */
  CLIFF: 31,
  /** Cut grass left lying where the sickle took it (LS-176, step 4): dry
   *  straw on the soil, walkable by anything, and the most flammable thing
   *  in the wild after thorn. A line of it is a fuse. It grows back into
   *  moss on the clock (see systems.ts). Never generated; only ever laid. */
  TUFT: 34,
  /** Bones and a scuff where the night's pack ate (LS-380): pale shards on open ground; the ground takes them back in time. */
  BONES: 35,
  ROD: 36,   // a lightning rod set down (LS-381)
  ICE: 37,   // a pool frozen under the frost line (LS-381, the second element)
  FORD: 38,  // a tidal crossing between islands: bare sand at low water, sea at high (LS-383)
  /** THE OPEN SEA — the edge of the world, not a barrier with a key.
   *
   *  Drawn exactly like deep water, and wet for every art purpose, but no
   *  charm opens it. The overworld is an island, so `OPENED_BY.tide` opening
   *  `T.WATER` meant the tide charm let a player swim right round the coast
   *  and walk up onto any plateau that touched it — every cliff and every ramp
   *  in the world bypassed by one charm. Measured on the gating proof as
   *  "tier 2 region(s) reached with tide alone" across a fifth of seeds.
   *
   *  A lake or a river is still `WATER`/`SHALLOW` and the tide still opens
   *  those, which is what the charm is for. */
  SEA: 32,
  /** A RAMP: the one place a cliff can be walked up.
   *
   *  Every crossing between two plateaus is a change of tier and therefore
   *  charm-gated, which left the world with no cliff a player could simply
   *  climb. Where two plateaus of the SAME tier touch, the crossing grants
   *  nothing that tier does not already have, so it costs no charm — and it is
   *  cut as steps, so it reads as a way up rather than as a hole in a wall.
   *
   *  Walkable. The variant's low nibble says which side is higher, so the
   *  steps climb the right way. */
  RAMP: 33,
};

const SOLID = new Set([T.WATER, T.TREE, T.ROCK, T.RUBBLE,
  T.CRACK, T.PUSH, T.BRAZIER, T.BRAZIER_LIT, T.THORN,
  T.NEST, T.STONE, T.CAIRN, T.CLIFF, T.SEA, T.ROD]);
export const isSolid = (t) => SOLID.has(t);
export const isBombable = (t) => t === T.RUBBLE || t === T.CRACK || t === T.CAIRN;
/** Only an Ember-edged sickle burns these away. */
export const isThorn = (t) => t === T.THORN;
/** Wet roots, picture variation, and exposed edges belong to the whole hedge. */
export function thornVariant(tiles, x, y) {
  const neighbours = [[0, -1], [1, 0], [0, 1], [-1, 0]].map(([dx, dy]) => tiles[y + dy]?.[x + dx]);
  return Number(neighbours.includes(T.WATER)) | (((x * 7 + y * 13 + (x ^ y)) & 3) << 1)
    | neighbours.reduce((mask, kind, i) => mask | (kind !== undefined && kind !== T.THORN ? 16 << i : 0), 0);
}
// NB: `isWade`/`isSwim` used to live here, exported, never imported, and
// disagreeing with `Game.isWadeAt`/`isSwimAt` — which are the ones the game
// actually asks. Two answers to one question is how this project ships
// softlocks; there is now one.

// Sun-bleached earth against cool overgrowth — deliberately away from
// saturated NES primaries.
// Obstacle colours are deliberately pushed away from their ground so a rock
// always reads as a rock, whatever biome it stands in.
// Obstacle colours are pushed away from their ground in luma, not just hue, so
// a rock reads as a rock in every biome. Water is tinted per biome too — it is
// the highest-chroma thing on screen, so it has to carry local identity.
export const BIOMES = {
  heath:  { stoneFace: '#e4ded0', stoneEdge: '#120f1c', ground: '#7fae69', shade: '#6a9455', tuft: '#a8d68c', tree: '#20402a', tree2: '#33583a', trunk: '#412e21', rock: '#5c5a66', rock2: '#3a3843', sand: '#cbb68b',
            deep: '#24506b', mid: '#3a7fa0', foam: '#a8d8e2', shallow: '#6aa8b6', bed: '#8a9c7e', bed2: '#6e7f64' },
  dust:   { grit: '#c9b389', stoneFace: '#efe9dc', stoneEdge: '#120f1c', ground: '#c6ab7c', shade: '#a88d5e', tuft: '#e0c99d', tree: '#3f5a37', tree2: '#587a49', trunk: '#5a3f28', rock: '#8a7452', rock2: '#4e3f2a', sand: '#e2cfa4',
            deep: '#2f6478', mid: '#6aa8a8', foam: '#bfe4e6', shallow: '#7cb8bc', bed: '#c0ac82', bed2: '#a08b62' },
  // FEN, LIFTED. Its ground tile is drawn from `shade`, and at #3d6150 (luma
  // 85) a fen inlet inside a heath screen -- biomes are per tile -- read as a
  // shadow lying on the grass, 43% darker than the ground a tile away
  // (read-0, screen 5,1). Was ground #4f7a63, shade #3d6150, tuft #6f9c81.
  fen:    { stoneFace: '#e8e4d6', stoneEdge: '#120f1c', ground: '#5f8f72', shade: '#4d7a68', tuft: '#86b394', tree: '#1a352f', tree2: '#2b4b40', trunk: '#33281f', rock: '#8c9a96', rock2: '#3f4746', sand: '#9fae94',
            deep: '#1d4a48', mid: '#2f7570', foam: '#8fd0c4', shallow: '#57998f', bed: '#6d8a70', bed2: '#55705a' },
  /* THE BRIGHTEST THING ON SCREEN WAS A ROCK. `scree`'s rock was #e2e4ea —
     relative luminance 0.776 against ground at 0.285 and a player whose
     brightest single pixel is 0.525 — so an impassable wall out-read the
     floor you walk on AND the body you steer, and the eye landed on a field
     of white headstones and had to hunt for the protagonist. Same fault in
     `ash` at 0.597. Stone now sits between the ground and the player: still
     clearly stone, no longer the loudest thing in the picture. */
  /* SCREE'S STONE WAS THE SAME BRIGHTNESS AS SCREE'S GROUND.
   *
   * Round 18 pulled this rock down from `#e2e4ea` because a wall was the
   * brightest thing on screen, out-reading the player. It went too far the
   * other way and stopped at the ground's own value: measured over four seeds,
   * solid against walkable came to dRGB 24 and dLuma ONE, against 22-46 in
   * every other country. A biome where the wall and the floor are the same
   * brightness has no readable silhouette at all — reported from play as not
   * being able to tell where you can walk.
   *
   * Stone now sits well BELOW the ground rather than level with it, which is
   * the direction that cannot recreate the original fault: a dark mass reads
   * as an obstacle and never competes with the player for the eye. */
  scree:  { grit: '#8d8797', stoneFace: '#efe9dc', stoneEdge: '#120f1c', ground: '#a08f78', shade: '#82715c', tuft: '#bcab92', tree: '#3c4c38', tree2: '#54684c', trunk: '#43332a', rock: '#6d6b7c', rock2: '#3a3844', sand: '#b09f84',
            deep: '#2b5a72', mid: '#5f8fb4', foam: '#b6dfe8', shallow: '#6fadbc', bed: '#9a8f78', bed2: '#7d7360' },
  ash:    { grit: '#837c93', stoneFace: '#ddd6e6', stoneEdge: '#120f1c', ground: '#938ca4', shade: '#7d7690', tuft: '#b3abc2', tree: '#28222f', tree2: '#3a3346', trunk: '#2b232f', rock: '#453e52', rock2: '#2e2838', sand: '#a9a1b4',
            deep: '#2a4866', mid: '#4a7aa0', foam: '#b0d2e2', shallow: '#6a9db8', bed: '#7a7488', bed2: '#605c6c', deepGlow: true },
  bloom:  { stoneFace: '#e4ded0', stoneEdge: '#120f1c', ground: '#7fa065', shade: '#688751', tuft: '#c8d488', tree: '#31502f', tree2: '#4b6d45', trunk: '#523822', rock: '#a89a88', rock2: '#544c42', sand: '#d8c79b',
            deep: '#276a6a', mid: '#3f9a94', foam: '#b6ecdf', shallow: '#68bdb0', bed: '#93a878', bed2: '#75886a' },
};
/* A TILE YOU CANNOT WALK THROUGH HAS TO LOOK DIFFERENT FROM ONE YOU CAN.
 *
 * Measured — mean drawn colour of the inner 10x10 of every blocked and every
 * walkable tile, 8 seeds, 512 screens, so a tile's own dark outline is not
 * doing the work:
 *
 *     ash    dRGB   6   1.05:1      <- the two are the same colour
 *     heath  dRGB  26   1.28:1
 *     bloom  dRGB  36   1.36:1
 *     fen    dRGB  41   1.51:1
 *     scree  dRGB  49   1.43:1
 *     dust   dRGB  84   1.79:1
 *
 * In ash the only cue that a tile is solid was a 1px seam on some of its
 * edges. The cause was a palette pulling in two directions: `rock` was
 * LIGHTER than `ground` while `tree` was much darker, so the two averaged
 * back onto the floor. Ash is burnt ground, so the resolution is the obvious
 * one — pale ash-fall underfoot, charred stumps standing in it — and heath
 * gets the same treatment, an open pale moor with dark outcrops.
 *
 * A reviewer named this the cheapest fix with the largest effect in the whole
 * art lens, and it is: one biome of six could not be read at all.
 */
export const BIOME_NAMES = Object.keys(BIOMES);

// Each hollow has its own stone, its own moss AND ITS OWN WATER. All three
// used to fall through to `WATER_FALLBACK`, so every lake in every hollow was
// byte-identical to the heath's — the same near-white foam, at 5.8:1 against
// the floor, which made a channel underground read as a UI panel cut into the
// stone rather than as water. They also had no `ground/shade/tuft`, so
// `ground()` painted `fillStyle = undefined` and every tile that draws a
// ground layer under itself was silently inert down here.
/* THE WALL A HOLLOW ACTUALLY DRAWS IS `wall2`, NOT `wall`.
 *
 * `case T.WALL` reads `kind === T.WALL ? pal.wall2 : pal.wall` — the room wall
 * takes the darker of the pair and `pal.wall` goes to T.BLOCK and friends. So
 * every contrast figure ever written about these palettes described a colour
 * the wall does not use: the note on CAVE_PAL claiming "1.59:1 puts it with
 * its siblings" was comparing `wall` against `floor`, while the siblings it
 * was joining measured 1.54, 1.57 and 1.60 from `wall2`. Three rounds of
 * "hollow floors are too dark" were argued from the wrong colour.
 *
 * An art review measured the RENDERED pixels and got 1.53/1.56/1.58 against a
 * 3:1 non-text floor, on a surface that is 68-71% of everything on screen
 * underground. Raising the floor — the obvious reading of "too dark" — makes
 * it WORSE: two dark colours are compressed by the +0.05 term, so moving the
 * floor up moves it toward the wall. The wall had to come up instead.
 *
 * Both come up, keeping their own separation, because `wall` is what a
 * pushable block is drawn in and collapsing the two would make the block
 * vanish into the masonry — which the same review flagged separately.
 *
 *   drawn wall:floor   1.54 -> 2.50   1.57 -> 2.49   1.60 -> 2.51   cave 1.59 -> 2.50
 *   block:wall         1.60 -> 1.78   1.67 -> 1.86   1.64 -> 1.81   cave 1.22 -> 1.28
 */
const WATER_FALLBACK = { deep: '#24506b', mid: '#3a7fa0', foam: '#a8d8e2', shallow: '#6aa8b6', bed: '#8a9c7e' };
/* THE HULL AT EIGHT HEADINGS (LS-383; Vijay: "the boat should have 8 directional art"). The boat and the raft are
 * scenes of rods and balls, which turn about the vertical axis, rendered once per heading through the same
 * `renderScene` the item icons use: real pixels with a lit and a shaded face, twenty-eight by twenty-six, the hull's
 * centre at (14, 18). Heading 0 points east and turns clockwise on the screen. */
export const HULL_W = 28, HULL_H = 26, HULL_CX = 14, HULL_CY = 18;
const hullTurn = (a: number) => ([x, y, z]: number[]): [number, number, number] => [x * Math.cos(a) - z * Math.sin(a), y, x * Math.sin(a) + z * Math.cos(a)];
const HULL_SKIN = { base: '#5a3d22', lit: '#8a6a3a', dark: '#2a1a0c' }, DECK_SKIN = { base: '#a07a44', lit: '#d0b070', dark: '#5a3d22' }, PALE_SKIN = { base: '#c9a56a', lit: '#f0d8a0', dark: '#8a6a3a' };
export const HULL_SCENES: Record<string, (a: number) => any[]> = {
  boat: (a) => {
    const t = hullTurn(a), L = 10, Wd = 3.6;
    const rod = (p: number[], q: number[], r: number, skin: any) => ({ kind: 'rod', a: t(p), b: t(q), r, skin });
    return [
      rod([-L * 0.8, 1.2, 0], [L * 0.8, 1.2, 0], 1.6, HULL_SKIN),                   // the keel and bilge
      rod([-L * 0.75, 2.6, -Wd], [L * 0.55, 2.6, -Wd * 0.4], 1.0, HULL_SKIN),        // the port side, drawing in to the bow
      rod([-L * 0.75, 2.6, Wd], [L * 0.55, 2.6, Wd * 0.4], 1.0, HULL_SKIN),          // the starboard side
      rod([-L * 0.75, 3.4, -Wd], [L * 0.6, 3.4, -Wd * 0.35], 0.5, PALE_SKIN),        // the gunwales, pale
      rod([-L * 0.75, 3.4, Wd], [L * 0.6, 3.4, Wd * 0.35], 0.5, PALE_SKIN),
      rod([-L * 0.8, 3.2, -Wd * 0.9], [-L * 0.8, 3.2, Wd * 0.9], 0.6, HULL_SKIN),    // the transom
      { kind: 'ball', at: t([L * 0.85, 2.8, 0]), r: 1.3, skin: PALE_SKIN },           // the stem
      rod([0, 3.0, -Wd * 0.9], [0, 3.0, Wd * 0.9], 0.5, DECK_SKIN),                 // the thwart
      rod([-L * 0.5, 1.8, 0], [L * 0.5, 1.8, 0], 0.9, DECK_SKIN),                   // the deck boards between
    ];
  },
  raft: (a) => {
    const t = hullTurn(a), r: any[] = [];
    for (let i = -1.5; i <= 1.5; i++) r.push({ kind: 'rod', a: t([-8, 1.4, i * 2.6]), b: t([8, 1.4, i * 2.6]), r: 1.25, skin: i % 2 ? HULL_SKIN : DECK_SKIN });
    for (const x of [-5, 5]) r.push({ kind: 'rod', a: t([x, 2.8, -5]), b: t([x, 2.8, 5]), r: 0.6, skin: PALE_SKIN });
    return r;
  },
};
const hullCache = new Map<string, any>();
/** The hull's pixels for a heading of eight, rendered once and kept: a Sheet of colours (null where clear). */
export function hullSheet(kind: string, heading: number) {
  const k = kind + '|' + heading; let s = hullCache.get(k);
  if (!s) { s = M3.renderScene(HULL_W, HULL_H, (HULL_SCENES[kind] || HULL_SCENES.boat)(heading * Math.PI / 4), HULL_CX, HULL_H - 6, '#1a1008'); hullCache.set(k, s); }
  return s;
}
/** THE ONE SEA (LS-383, the merge): open sea took the water colours of whatever country its screen was filed under,
 * so the straits were patched in four blues with hard edges on the screen grid. The sea is one water everywhere. */
export const SEA_WATER = { ...WATER_FALLBACK, bed2: '#6e7f64' };
const wet = (pal, k) => pal[k] || WATER_FALLBACK[k];

function mkCanvas(w, h) {
  const c = document.createElement('canvas');
  c.width = w; c.height = h;
  c.getContext('2d').imageSmoothingEnabled = false;
  return c;
}

const tileCache = new Map();

/** Kinds worth painting more than once so fields don't tile visibly. */
export const VARIED = new Set([T.GROUND, T.SAND, T.MOSS, T.TREE, T.ROCK, T.DEBRIS, T.BONES]);
export const VARIANTS = 4;
/** Water picks its variant from its neighbours instead of its position. */
// ...AND THE TWO SURFACES THAT MEET IT. Measured across 512 wild screens,
// 55,879 tile seams — 109 per screen — of which only 15.4% touched an
// edge-aware kind: grass meeting sand was a square edge. `T.ROCK` and `T.TREE`
// are objects sitting ON the ground and carry their own silhouette, so the
// pairs that actually read as a hard step are GROUND/MOSS (6,251) and
// GROUND/SAND (1,963). Both scatter into their neighbour now.
/** Break a tile's straight edge into its neighbour on the sides that border
 *  something else. `mask` is the same low nibble `EDGED` builds: 1=N 2=E 4=S
 *  8=W. Two pixels deep, scattered, so the seam reads as a fringe rather than
 *  a cut — and only on the sides that HAVE a seam, so the middle of a meadow
 *  stays a meadow. */
/** The rim where a shoreline turns an inner corner — the one case a
 *  side-only mask is blind to. `deep` may be null for a thin rim. */
function rimCorner(g, diag, deep, foam) {
  if (!diag) return;
  const nib = (cx, cy, sx, sy) => {
    if (deep) {
      px(g, cx + (sx < 0 ? -1 : 0), cy, 2, 2, deep);
      px(g, cx + (sx < 0 ? -2 : 0), cy + sy * 2, 3, 1, deep);
      px(g, cx + (sx < 0 ? -1 : 0), cy + sy * 2, 1, 1, deep);
    }
    px(g, cx + sx * 2, cy, 1, 1, foam);
    px(g, cx, cy + sy * 2, 1, 1, foam);
    px(g, cx + sx, cy + sy, 1, 1, foam);
  };
  if (diag & 1) nib(TS - 1, 0, -1, 1);          // north-east
  if (diag & 2) nib(TS - 1, TS - 1, -1, -1);    // south-east
  if (diag & 4) nib(0, TS - 1, 1, -1);          // south-west
  if (diag & 8) nib(0, 0, 1, 1);                // north-west
}

/* A SHORELINE NEEDS DEPTH, NOT A DITHERED PIXEL.
 *
 * `fray` breaks the outermost row or two at even odds, which is enough where
 * the two materials are close in tone -- sand into grass, moss into ground --
 * and nowhere near enough for the loudest pair in the game. Water against
 * grass is a cyan rectangle beside a green one, and one dithered pixel between
 * them leaves the tile grid perfectly legible: measured on the marsh, the
 * change was there (22% of the crop's pixels moved) and invisible.
 *
 * Four rows, with the odds falling off inward, so the bank dissolves into the
 * water over a quarter of a tile instead of stopping at its edge.
 */
function frayDeep(g, mask, rng, colour, depth = 4) {
  if (!colour || !mask) return;
  const odds = [0.78, 0.52, 0.30, 0.14];
  for (let d = 0; d < depth; d++) {
    const p = odds[Math.min(odds.length - 1, d)];
    for (let i = 0; i < TS; i++) {
      if ((mask & 1) && rng.chance(p)) px(g, i, d, 1, 1, colour);
      if ((mask & 4) && rng.chance(p)) px(g, i, TS - 1 - d, 1, 1, colour);
      if ((mask & 2) && rng.chance(p)) px(g, TS - 1 - d, i, 1, 1, colour);
      if ((mask & 8) && rng.chance(p)) px(g, d, i, 1, 1, colour);
    }
  }
}

function fray(g, mask, rng, colour, diag = 0) {
  if (!colour || (!mask && !diag)) return;
  for (let i = 0; i < TS; i++) {
    if ((mask & 1) && rng.chance(0.5)) px(g, i, 0, 1, rng.chance(0.3) ? 2 : 1, colour);
    if ((mask & 4) && rng.chance(0.5)) px(g, i, TS - (rng.chance(0.3) ? 2 : 1), 1, 2, colour);
    if ((mask & 2) && rng.chance(0.5)) px(g, TS - (rng.chance(0.3) ? 2 : 1), i, 2, 1, colour);
    if ((mask & 8) && rng.chance(0.5)) px(g, 0, i, rng.chance(0.3) ? 2 : 1, 1, colour);
  }
  /* THE INNER CORNERS. `diag` is set only where a diagonal neighbour differs
   * while both of its sides match, so the edge has to turn INTO this tile and
   * no side dither covers it. Left alone it is the two-pixel notch that gives
   * a naive tileset away. Frayed the same way the sides are, from the corner
   * inward, so a shoreline wrapping a headland comes apart rather than
   * cutting a right angle. */
  const nib = (cx, cy, sx, sy) => {
    for (let i = 0; i < 5; i++) {
      for (let j = 0; j < 5 - i; j++) {
        if (!rng.chance(0.55 - i * 0.08)) continue;
        px(g, cx + sx * i, cy + sy * j, 1, 1, colour);
      }
    }
  };
  if (diag & 1) nib(TS - 1, 0, -1, 1);          // north-east
  if (diag & 2) nib(TS - 1, TS - 1, -1, -1);    // south-east
  if (diag & 4) nib(0, TS - 1, 1, -1);          // south-west
  if (diag & 8) nib(0, 0, 1, 1);                // north-west
}

export const EDGED = new Set([T.WATER, T.SHALLOW, T.MOSS, T.SAND, T.SEA]);

/* THINGS THAT STAND ON THE GROUND, rather than being a kind of ground.
 *
 * The distinction is about SEAMS. Two kinds of ground meeting on a straight
 * tile boundary read as a 16px step unless one of them is edge-aware, which
 * is what `EDGED` is for. An object meeting ground has its own silhouette and
 * needs nothing — a boulder is allowed to have a hard outline, because that
 * outline is the boulder.
 *
 * This set lived in the test that uses it, and paid for it three times: the
 * standing stone went outdoors and 131 stone/ground boundaries counted as
 * hard steps; the wayshrine went outdoors and 296 did; the cairn went
 * outdoors and 49 did. Each time the tile was correct and the classification
 * was missing, and each time it was missing because the place to declare it
 * was in a file nobody editing `art.js` or `world.js` would open. It is here
 * now, one screen away from the enum it classifies. */
/* THE THINGS DRAWN OVER THE TURF IN THE WILD (LS-218): each has a bare base (`pal.noBase`), so the meadow shows round it. A kind with an opaque square base must not join this set: drawn over the turf it would be the grid again. */
/* ...AND THE THICKET IS ONE OF THEM (LS-375, 2026-09-10). It was not, and that
 * is the whole of the bug reported as "a thorn thicket reads as a wooden
 * crate": the tile painted its own opaque base -- the country's floor on dry
 * ground, flat deep water when rooted in it -- so a thicket in a meadow was a
 * hard-edged SQUARE of bare floor with stems on it, and one in the shallows
 * was a dark blue box. Magnified 12x on tusk-0 it is unmistakable. Two
 * redraws had already gone at the stems (the picket fence of LS-199, the comb
 * of round 47) and the stems were never the problem. */
export const BARE_IN_WILD = new Set([T.RUBBLE, T.STONE, T.CAIRN, T.ROCK, T.BRAZIER, T.BRAZIER_LIT, T.THORN, T.DEBRIS, T.BONES, T.NEST, T.PUSH]);
export const STANDING = new Set([T.TREE, T.ROCK, T.RUBBLE, T.THORN,
  T.DEBRIS, T.SCORCH, T.TUFT, T.STONE, T.BRAZIER, T.BRAZIER_LIT, T.CAIRN,
  T.NEST, T.PUSH]);

/* Animation tables, named once each.
 *
 * Three rounds running, a tile has declared one frame count in ANIM and
 * cycled through a table of a different length in its draw code: the idol
 * declared 1 frame and read a 4-entry glow (its eye was welded shut), moss
 * declared 4 and read 3, the hollow's wall moss declared 1 and read 3 so it
 * never moved at all. The tables live here now and ANIM's `frames` is their
 * length, so the two cannot disagree. test/soak.mjs fails the build if a
 * literal table reappears inside a cyc() call.
 */
const SWAY = [0, 1, 0, -1];          // side to side: grass, canopy, thorns
const BOB = [0, -1, -2, -1];         // up and back down: floating things
const RISE = [0, 1, 2, 1];           // taller and shorter: flame, growth
const glow = (pal) => [pal.accentDim, pal.accent, pal.accentLit, pal.accent];
export const CYCLE = SWAY.length;    // every table above is this long

// Every kind moves, but at the speed its material would. Water rolls, grass
// stirs, stone only catches the light now and then.
// phase: 'wave' runs along x so a surface reads as one sheet; 'scatter'
// staggers per tile so a field shimmers; 'sync' pulses together.
export const ANIM = {
  // moving things: fire, water, and anything that signals gameplay state
  // rate 10, not 8. Water is 10.5% of every tile drawn and moved 1.172% of its
  // pixels per frame — the largest single contribution to the picture's total
  // movement once the trees were calmed. A quarter slower still rolls; it no
  // longer carries the whole screen on its own.
  /* A cliff is still ground and does not move — but a river that reaches one
   * goes over it, and a waterfall that does not run is a painting of a
   * waterfall. Only variant bit 16 draws falling water; the other variants
   * ignore `f` entirely and cost nothing but four identical cache entries. */
  [T.CLIFF]:       { frames: CYCLE, rate: 9, phase: 'wave', span: 4 },
  [T.WATER]:       { frames: CYCLE, rate: 10, phase: 'wave', span: 6 },
  [T.SEA]:         { frames: CYCLE, rate: 10, phase: 'wave', span: 6 },
  [T.SHALLOW]:     { frames: CYCLE, rate: 7, phase: 'wave', span: 5 },
  [T.BRAZIER_LIT]: { frames: CYCLE, rate: 6, phase: 'scatter' },
  // living ground: a coherent wavefront crossing the field, not per-tile grain
  [T.GROUND]:      { frames: CYCLE, rate: 22, phase: 'wave', span: 14 },
  [T.MOSS]:        { frames: CYCLE, rate: 20, phase: 'wave', span: 12 },
  [T.TREE]:        { frames: CYCLE, rate: 14, phase: 'wave', span: 7 },
  [T.SAND]:        { frames: CYCLE, rate: 26, phase: 'wave', span: 16 },
  // Stone does not twinkle, flex, or wink — but it STANDS ON GROUND, and
  // these kinds paint `ground(g, pal, rng, f)` under themselves. Declaring one
  // frame froze that layer at f = 0, so every boulder, every pile of rubble
  // and every cave mouth in a swaying field sat on a square of grass that did
  // not move with it. Same rate, phase and span as T.GROUND, so the wavefront
  // crosses them instead of breaking around them.
  [T.ROCK]:        { frames: CYCLE, rate: 22, phase: 'wave', span: 14 },
  [T.CAIRN]:       { frames: CYCLE, rate: 22, phase: 'wave', span: 14 },
  [T.RUBBLE]:      { frames: CYCLE, rate: 22, phase: 'wave', span: 14 },
  [T.CRACK]:       { frames: 1, rate: 1, phase: 'sync' },
  [T.PUSH]:        { frames: 1, rate: 1, phase: 'sync' },
  [T.BRAZIER]:     { frames: 1, rate: 1, phase: 'sync' },
  [T.THORN]:       { frames: CYCLE, rate: 18, phase: 'wave', span: 6 },
  [T.DEBRIS]:      { frames: CYCLE, rate: 22, phase: 'wave', span: 14 },
  [T.SCORCH]:      { frames: 1, rate: 1, phase: 'sync' },
  [T.TUFT]:        { frames: CYCLE, rate: 22, phase: 'wave', span: 14 },   // the straw stirs in the wind
  // it breathes, and faster than anything else down there
  [T.NEST]:        { frames: CYCLE, rate: 8, phase: 'scatter' },
};
export const animOf = (kind) => ANIM[kind] || { frames: 1, rate: 1, phase: 'sync' };

/** The frame seed deliberately excludes `frame`: the same speckles must land
 *  in the same places every frame, or the tile boils instead of animating. */
/* THE LONE ROCK, BUILT AS A SOLID (LS-199).
 *
 * Reported from play with a screenshot and four arrows on it: "what are these
 * random ledges, they really mess with my sense of depth perception". A
 * one-tile lump of rock was drawn as a filled rectangle with a lighter band
 * across its top, and that band reads as a TOP FACE: every one of them looked
 * like a step up onto a level the world does not have. Scattered over flat
 * ground, that is exactly the thing that breaks depth.
 *
 * It is built now the way the forager is built -- as solids in world space
 * through the one camera (`model3d.ts`) -- so a rock, a body and the ground
 * they share agree about where the ground is. Two or three overlapping balls
 * from this tile's own seed, one silhouette of ink around the lot, a contact
 * shadow at the foot; where the rock carries on into the next tile a ball is
 * pushed that way so a two-tile crag is one mass rather than two pebbles.
 *
 * Returned as a sheet rather than painted straight onto the tile so the soak
 * can measure the shape it makes: a step is widest at its top and a rock is
 * not. */
export function cragMass(rng, tones: { base: string; lit: string; dark: string },
  sides: { inW: boolean; inE: boolean; inN: boolean; inS: boolean }) {
  const { inW, inE, inN, inS } = sides;
  const cx = TS / 2 + (inW ? 0 : -2) + (inE ? 0 : 2);
  const cz = (inN ? 0 : -2) + (inS ? 0 : 2);
  const big = 3.9 + rng.int(2) * 0.45;
  const skin = { base: tones.base, lit: tones.lit, dark: tones.dark };
  const mass: any[] = [{ kind: 'ball', at: [cx, big * 0.62, cz], r: big, rz: big * 0.9, skin }];
  mass.push({ kind: 'ball', at: [cx - 2.2 + rng.int(2), big * 0.4, cz + 1.4], r: big * 0.62, rz: big * 0.6, skin });
  if (!inW) mass.push({ kind: 'ball', at: [cx - 6, big * 0.5, cz], r: big * 0.8, rz: big * 0.8, skin });
  if (!inE) mass.push({ kind: 'ball', at: [cx + 6, big * 0.5, cz], r: big * 0.8, rz: big * 0.8, skin });
  if (!inN) mass.push({ kind: 'ball', at: [cx, big * 0.5, cz - 6], r: big * 0.8, rz: big * 0.8, skin });
  if (!inS) mass.push({ kind: 'ball', at: [cx, big * 0.5, cz + 6], r: big * 0.8, rz: big * 0.8, skin });
  const sheet = M3.renderScene(TS, TS, mass, 0, TS - 3, '#241a24');
  // the shadow it sits in, under everything it drew
  const shade: any = { w: TS, h: TS, px: new Array(TS * TS).fill(null) };
  /* The shadow peeks out on the NEAR side. Cast dead centre it lands entirely
   * under the mass and the rock floats: two pixels of shadow past the foot is
   * the whole difference between a thing on the ground and a thing pasted on
   * it. */
  M3.groundShadow([cx, 0, cz + 3], big * 0.95, 0, TS - 3,
    (x, y, c) => { if (x >= 0 && y >= 0 && x < TS && y < TS) shade.px[y * TS + x] = c; }, '#00000030');
  for (let i = 0; i < sheet.px.length; i++) if (!sheet.px[i] && shade.px[i]) sheet.px[i] = shade.px[i];
  // one fracture, from this tile's own seed, so no two crags are twins
  const fx2 = Math.round(cx) - 1 + rng.int(3), fy2 = 6 + rng.int(4);
  if (sheet.px[fy2 * TS + fx2]) sheet.px[fy2 * TS + fx2] = tones.dark;
  return sheet;
}

export function tile(kind, pal, palKey, variant = 0, frame = 0) {
  /* THE KEY IS THE FRAME THE PAINTER USES (LS-235): keyed on the raw frame,
   * a still tile asked for with the live frame was a new canvas every frame
   * -- 2,564 of them in thirty seconds of walking, measured -- and the map
   * held them all. `frame % frames` is what the painter reads. */
  const nf = animOf(kind).frames;
  const fr = nf > 1 ? frame % nf : 0;
  const key = kind + '|' + palKey + '|' + variant + '|' + fr;
  let c = tileCache.get(key);
  if (c) return c;
  c = mkCanvas(TS, TS);
  const seed = mix(kind, 7717 + variant * 977, palKey.length * 131 + palKey.charCodeAt(0));
  const wm = /\|w([12])/.exec(palKey); WET = wm ? +wm[1] / 2 : 0;   // a wet screen's key carries its wetness: the painters' literals go through the tone too
  try { paintTile(c.getContext('2d'), kind, pal, new RNG(seed), fr, nf, variant); } finally { WET = 0; }
  if (tileCache.size > 4000) tileCache.clear();
  tileCache.set(key, c);
  return c;
}

/** How many baked tiles are being held. Read by a probe: the palette blend
 *  multiplies the key space, and a cache that grows without bound is a leak. */
export function tileCacheSize() { return tileCache.size; }

/** Blend two hex colours — the shelf needs one and `mixHex` lives in grass.ts,
 *  which imports from here. */
function mix2(a, b, t) {
  const p = (s) => [1, 3, 5].map((i) => parseInt(s.slice(i, i + 2), 16));
  const pa = p(a), pb = p(b);
  return '#' + pa.map((v, i) => Math.round(v + (pb[i] - v) * t)
    .toString(16).padStart(2, '0')).join('');
}

/* EVERY ASSET HAS A WET STATE (2026-09-11; Vijay: "every asset in the game should have a wet state"). One tone
 * does it for the tiles: while a tile is painted for a wet screen, every colour a painter puts down goes through
 * `wetTone` -- darker, cooler, a little more saturated, and the bright tones kept bright so a rock or a stone keeps
 * a sheen where it was lit. The painters themselves are untouched; the wet state is theirs by construction. */
let WET = 0;
export function wetTone(hex: string, w: number): string {
  if (!w || typeof hex !== 'string' || hex.length !== 7 || hex[0] !== '#') return hex;
  let r = parseInt(hex.slice(1, 3), 16), g = parseInt(hex.slice(3, 5), 16), b = parseInt(hex.slice(5, 7), 16);
  const l = 0.2126 * r + 0.7152 * g + 0.0722 * b;
  r = r * (1 - 0.30 * w); g = g * (1 - 0.22 * w); b = b * (1 - 0.08 * w) + 12 * w;
  if (l > 185) { const k = 0.35 * w; r = r + (223 - r) * k; g = g + (234 - g) * k; b = b + (242 - b) * k; }   // the sheen: what was lit stays lit, and cools
  const c = (v: number) => Math.max(0, Math.min(255, Math.round(v))).toString(16).padStart(2, '0');
  return '#' + c(r) + c(g) + c(b);
}
export function wetPalette(pal: any, w: number): any {
  if (!w || !pal) return pal;
  const out: any = {};
  for (const [k, v] of Object.entries(pal)) out[k] = typeof v === 'string' ? wetTone(v, w) : v;
  return out;
}
function px(g, x, y, w, h, col) { g.fillStyle = WET > 0 ? wetTone(col, WET) : col; g.fillRect(x, y, w, h); }

/** Luma the way the eye weights it, from one of this file's hex strings. */
function luma(c) {
  const p = [1, 3, 5].map((i) => parseInt(c.slice(i, i + 2), 16));
  return 0.2126 * p[0] + 0.7152 * p[1] + 0.0722 * p[2];
}

/** A STONE TONE THAT CANNOT BE MISTAKEN FOR THE GROUND IT STANDS ON.
 *
 * Six biome palettes name `ground` and `rock` independently, and three of them
 * landed close enough to be the same colour: bloom put 7 luma between them,
 * scree 36, fen 39. A wall you cannot pick out of the floor is not a wall, and
 * hand-tuning six pairs leaves the seventh palette to make the same mistake.
 *
 * So the gap is enforced rather than authored. The stone is pushed FURTHER IN
 * THE DIRECTION IT ALREADY LEANS -- fen's pale limestone terraces get paler,
 * ash's dark basalt gets darker -- because which way a country's rock reads
 * against its ground is a real choice by whoever wrote the palette, and only
 * the size of the gap was wrong. */
export function standAway(stone, groundCol, gap = 44, dirRef = null) {
  /* THE DIRECTION IS THE PALETTE'S, NOT THE TONE'S.
   *
   * Each tone used to choose for itself which way to move, and a cliff is
   * drawn in two of them -- a lit body and a shadow. In fen the lit tone is
   * pale limestone and the shadow is nearly the colour of the mud, so one went
   * up and the other went down and the mass AVERAGED ONTO THE GROUND: 23 dRGB
   * against a floor of 30, with both tones dutifully "held clear" of it.
   *
   * `dirRef` lets a caller name the tone that decides for the whole family --
   * the country's `rock` -- so a pale country's rock gets paler in shadow too
   * and the mass moves as one. */
  const ref = dirRef || stone;
  const target = luma(ref) >= luma(groundCol) ? '#f4f0e8' : '#171220';
  let out = stone;
  for (let i = 0; i < 12 && Math.abs(luma(out) - luma(groundCol)) < gap; i++) {
    out = mix2(out, target, 0.14);
  }
  return out;
}

/** THE TONES A DROP IS DRAWN IN, FOR EVERY COUNTRY, FROM ONE PLACE.
 *
 * `standAway` pushes a tone FURTHER IN THE DIRECTION IT ALREADY LEANS, so a
 * pale country keeps its pale stone. That is right for a boulder sitting in a
 * field and wrong for the drop below a plateau: in the green countries it made
 * the rock LIGHTER than the grass it falls away from, and a lit slab lying in
 * dark grass reads as a plank. An art critic given the whole island called the
 * same shapes "dominoes lying flat", and they were the cliff feet.
 *
 * A drop is the one surface in a top-down world that never catches the sky. Mix
 * it from the country's own GROUND toward the shade tone and every biome keeps
 * its hue while every face is darker than the ground above it by construction.
 * `lit` is the same mix held back -- the brink, still in shadow, just less of
 * it -- so the mass moves as one instead of averaging onto the floor.
 *
 * Both painters call this: the tile art here and `drawCliffs` in `game.ts`,
 * which hangs the face below the brink. They used to derive their own tones and
 * disagreed about which way pale countries lean. */
/** A STONE THAT IS DARKENED, NOT TINTED.
 *
 * Mixing a colour toward a fixed tone moves its HUE toward that tone, and doing
 * it enough times to open a 46-luma gap moved every country's stone most of the
 * way to the same blue-grey. On a sheet of one screen per biome the cliffs in
 * bloom, heath, fen and scree were, to the eye, the same rock -- four different
 * countries with one wall. Scaling keeps the hue exactly and only takes light
 * away, which is what a surface in shadow actually does. */
function darken(hex, k) {
  const c = hex.replace('#', '');
  const r = Math.round(parseInt(c.slice(0, 2), 16) * k);
  const g2 = Math.round(parseInt(c.slice(2, 4), 16) * k);
  const b = Math.round(parseInt(c.slice(4, 6), 16) * k);
  const h = (v) => Math.max(0, Math.min(255, v)).toString(16).padStart(2, '0');
  return '#' + h(r) + h(g2) + h(b);
}

/** THE TONES A DROP IS DRAWN IN, FOR EVERY COUNTRY, FROM ONE PLACE.
 *
 * Three cuts of this, and each fixed what the last one broke.
 *
 * `standAway` pushes a tone FURTHER IN THE DIRECTION IT ALREADY LEANS, so a
 * pale country keeps its pale stone: right for a boulder sitting in a field,
 * and in the green countries it made the drop LIGHTER than the grass it falls
 * away from. A lit slab lying in dark grass is a plank, and an art critic given
 * the whole island called those shapes "dominoes lying flat".
 *
 * Mixing the face FROM THE GROUND toward a shade tone fixes the direction and
 * loses the material: in bloom that is a dark GREEN wall, and a dark green bar
 * lying in bright green grass is a fallen log.
 *
 * Mixing the country's own STONE toward that shade tone keeps the material and
 * loses the country: enough mixing to open the gap lands every palette on the
 * same blue-grey, and the island came out with one wall in six countries.
 *
 * So: the palette's own stone, DARKENED rather than tinted, until it clears the
 * ground it falls from. Hue is the country's, the direction is always down, and
 * nothing converges.
 *
 * THE GAP IS 36, NOT 46. At 46 the darker countries bottomed out near black and
 * a wall in heath read as a hole punched in the grass rather than as rock. 36
 * is still well clear of the 30 the project holds every floor-to-wall pair to.
 *
 * Both painters call this: the tile art here and `drawCliffRun` in `game.ts`,
 * which hangs the face below the brink. They used to derive their own tones and
 * disagreed about which way pale countries lean. */
/* MEMOISED ON THE PALETTE OBJECT.
 *
 * This parses hex strings and loops up to twenty-four times pushing a tone
 * until it clears the ground, and `drawCliffs` calls it once per ROCK TILE per
 * FRAME -- on a screen with 129 of them that is eight thousand calls a second,
 * each allocating a handful of strings. The soak went from about a hundred
 * seconds to over fourteen minutes and this was the whole of it.
 *
 * A palette here is one of the objects `terracePal` hands out, and that is
 * itself cached, so the same object comes back every frame: a WeakMap keyed on
 * it costs one lookup and holds no palette alive that the cache has dropped. */
const cliffToneCache = new WeakMap();
export function cliffTones(pal) {
  const hit = cliffToneCache.get(pal);
  if (hit) return hit;
  const made = computeCliffTones(pal);
  cliffToneCache.set(pal, made);
  return made;
}
function computeCliffTones(pal) {
  // `floor` is the fallback for a palette with no `ground`: cliffs are outdoor
  // tiles, but `test/soak.ts` paints every kind in every palette
  const soil = pal.ground || pal.floor || '#7fae69';
  /* A LITTLE OF THE COUNTRY IN THE STONE.
   *
   * The convergence turned out not to be in the mixing at all -- it is in the
   * palettes. heath, scree and ash each author `rock2` at very nearly the same
   * blue-grey (3a3843 / 3a3844 / 2e2838), so four countries were already
   * sharing one wall before anything here touched them.
   *
   * A fifth of the ground's hue is enough to tell them apart -- heath's rock
   * leans green, ash's lavender, dust's tan -- and not nearly enough to make
   * the wall read as the ground, which is the failure that put a dark green
   * bar in bright green grass and got called a fallen log. */
  let face = mix2(pal.rock2 || pal.wall2 || pal.rock || pal.wall || '#3a3843',
    soil, 0.22);
  /* AND THE GAP IS BOUNDED AT BOTH ENDS.
   *
   * Only the floor was ever enforced, and the authored stones blew straight
   * past the ceiling: measured against their own ground, heath's wall sat 94
   * luma down, ash 101, dust 109. That is not a wall in shadow, it is a hole,
   * and on a sheet of one screen per biome every cliff in the green countries
   * read as a pit cut in the grass rather than as rock standing in it.
   *
   * 36 is clear of the 30 the project holds every floor-to-wall pair to; 62 is
   * about the darkest a lit surface can be against a bright one and still read
   * as a surface. */
  for (let i = 0; i < 24 && luma(soil) - luma(face) < 36; i++) face = darken(face, 0.94);
  for (let i = 0; i < 24 && luma(soil) - luma(face) > 62; i++) face = darken(face, 1.07);
  /* ...AND NEVER AS LIGHT AS THE DARKEST FLOOR IN THE COUNTRY.
   *
   * Holding a face a fixed distance under the ground BESIDE it is not enough
   * once the terraces are shaded across a range of their own: ash's floors run
   * 97 to 166 luma and its walls ran 55 to 107, so the lowest floor was darker
   * than the highest wall and brightness said nothing about where you could
   * walk. `terracePal` carries the country's level-0 ground on every palette
   * for this. Under it by a clear margin, and the rule the player learns is
   * simple: darker than any floor means you cannot go there. */
  if (pal.darkFloor) {
    for (let i = 0; i < 24 && luma(pal.darkFloor) - luma(face) < 12; i++) {
      face = darken(face, 0.94);
    }
  }
  // the lit courses in the face: the same stone with more light on it, but
  // never up to the ground's own level -- nothing on a wall that faces away
  // catches as much sky as the top does
  let lit = darken(face, 1.42);
  for (let i = 0; i < 20 && luma(soil) - luma(lit) < 15; i++) lit = darken(lit, 0.94);
  // the lit courses stay under the floor line too, or the brightest part of a
  // wall is the thing that breaks the rule the rest of it keeps
  if (pal.darkFloor) {
    // TEN, NOT FOUR. The lit courses are the binding tone -- with four the whole
    // rule cleared by two luma in bloom, which is inside the noise of any tile
    // that has grain on it. A rule the player is meant to learn needs a margin
    // they can see.
    for (let i = 0; i < 20 && luma(pal.darkFloor) - luma(lit) < 10; i++) {
      lit = darken(lit, 0.94);
    }
  }
  /* THE FACE IS IN SHADOW AND THE SHADOW IS COOL (2026-09-05, against the reference renders: "the vertical faces
   * should be a consistent cool-toned shadow colour, the top-facing surfaces brighter and warmer"). The face keeps its
   * country's stone and is pulled a fifth toward a desaturated violet; the lit courses take a little sun back. */
  face = mix2(face, '#5a5470', 0.2);
  lit = mix2(lit, '#e8d0a0', 0.16);
  return { face, lit, dark: '#241a24' };
}

/** THE BODY OF A MASSIF, SEEN FROM ABOVE.
 *
 * What this replaces was a call to `ground()` -- the walkable floor of the
 * country, tufts and all -- with the rock drawn only on the sides that have a
 * lower neighbour. An east-facing cliff tile covers four of sixteen columns,
 * so twelve columns of ordinary walkable ground survived on a tile the player
 * cannot stand on, and a whole raised massif read as a terrace you could climb
 * onto. Reported from play, looking at a scree screen: "are these supposed to
 * be ramps why can i walk heee".
 *
 * Broken rock rather than blades of grass: the texture has to say wall on its
 * own, because in the frames where this shows there is no silhouette to read
 * -- it is the middle of a mass, with more of the same on every side. Static,
 * with no frame term, so a cliff does not shimmer like foliage. */
/* THE BODY IS THE SHADOWED TONE, AND THE FLECKS ARE THE LIT ONE.
 *
 * This filled the tile with `lit` and flecked it with `face`, which is the
 * relationship inverted: `drawCliffRun` paints the faces it hangs in `face`, so
 * every piece of rock that does NOT show a face -- and that is two thirds of
 * the rock on the island -- came out PALER than the walls beside it. A pale
 * mass next to a dark wall is the plank reversal one more time, in the one
 * place the run pass never touches. */
function stoneMass(g, rng, body, fleck, dark) {
  /* THE GRAIN OF THE ROCK IS NOT BAKED HERE ANY MORE.
   *
   * A tile's art is cached on (kind, palette, variant, frame), and a cliff's
   * variant is derived entirely from the terrain -- which sides are higher,
   * whether a river falls down it. Every interior tile of a rock mass has the
   * same answer, so every interior tile was the SAME BAKED IMAGE, and nine
   * flecks in fixed positions repeated across a formation as wallpaper. At
   * 250% on a bloom cliff it is unmistakable: rows of identical pale marks
   * marching across the face at sixteen-pixel intervals, and the fracture
   * marks lining up into a dashed rule.
   *
   * `drawCliffs` scatters the grain instead, hashed on world position, so no
   * two tiles of a formation carry the same marks. What is left here is the
   * body and a few specks -- enough that a cliff tile drawn outside that pass
   * (the soak paints every kind in every palette) is not a flat rectangle. */
  px(g, 0, 0, TS, TS, body);
  for (let i = 0; i < 3; i++) {
    const w = 1 + rng.int(3), h = 1 + rng.int(2);
    px(g, rng.int(TS - w + 1), rng.int(TS - h + 1), w, h, fleck);
  }
  for (let i = 0; i < 2; i++) px(g, rng.int(TS), rng.int(TS), 1, 1, dark);
}

/** Draw with horizontal wrap, so scrolling detail never pops at the seam. */
function pxw(g, x, y, w, h, col) {
  const wrapped = ((x % TS) + TS) % TS;
  px(g, wrapped, y, w, h, col);
  if (wrapped + w > TS) px(g, wrapped - TS, y, w, h, col);
}

/** Read a table at a frame — and refuse a table that is not the cycle length.
 *
 * Frame counts and their tables have disagreed in three separate rounds. The
 * guard for it was a regex over this file looking for a literal `cyc([`, which
 * a reviewer defeated in one line by naming the constant instead. The check
 * belongs where the mismatch actually happens: a wrong-length table now throws
 * the moment the tile is painted, and test/soak.mjs paints every kind in every
 * palette at every frame. */
const cyc = (arr, f) => {
  if (!Array.isArray(arr) || arr.length !== CYCLE) {
    throw new Error(`animation table has ${arr && arr.length} entries, not ${CYCLE}`);
  }
  return arr[f % arr.length];
};

/* `base` lets MOSS lay its own floor.
 *
 * T.MOSS painted this exact ground and then scattered fourteen 2x1 tufts over
 * it — about 11% of 256 pixels — so its mean colour was the ground's mean
 * colour. Measured across six biomes, MOSS against GROUND was dRGB 5-10 and
 * 1.04-1.09:1, against this project's own stated floor of dRGB 30, on the
 * SECOND most common walkable tile in the overworld. Two tile kinds covering
 * most of the outdoors were, to the eye, one tile.
 *
 * `shade` is already in every biome palette and is already what a blade's
 * body is drawn in, so moss keeps the family it belonged to and simply reads
 * as the denser, darker patch it is meant to be from above. */
function ground(g, pal, rng, f, base = null) {
  px(g, 0, 0, TS, TS, base || pal.ground);

  /* A DRY COUNTRY'S FLOOR IS LOOSE STONE, NOT SIX BLADES OF GRASS.
   *
   * The turf field covers 0.52 of heath and 0.12 of scree by design -- a
   * country of gravel should not be a lawn -- and the consequence was that
   * scree, dust and ash had nothing on their floor at all but the palette
   * entry and a handful of tufts. On a whole-island render the scree plateau
   * came out as fifteen screens of flat mauve: a paved plaza, and the largest
   * single weak area in the picture.
   *
   * What is missing is the country's own material lying on it. Flecks in the
   * shadow and stone tones for grit, a few two-pixel pebbles with a lit
   * shoulder, and the tufts thinned to what actually grows there. Keyed off a
   * palette entry rather than off the turf table, because `grass.ts` imports
   * this file and asking it back would be a cycle. */
  const dry = !!pal.grit;
  if (dry) {
    /* GRAVEL, NOT CONFETTI. The first cut lit every pebble with a near-white
     * shoulder and put three of them on every tile: at map scale that is an
     * even scatter of bright dots over fifteen screens, which reads as snow.
     * The litter is mostly DARK -- stone lying in its own shade -- with the
     * occasional pebble catching enough light to have a top. */
    // ...and since `soil.ts` lays the country's litter in world space, the
    // tile itself carries only a trace, or the two would sum to confetti again
    for (let i = 0; i < 3; i++) {
      const x = rng.int(TS - 1), y = rng.int(TS);
      px(g, x, y, 2, 1, pal.shade);
    }
    for (let i = 0, n = rng.int(2); i < n; i++) {
      const x = rng.int(TS - 2), y = rng.int(TS - 2);
      px(g, x, y, 2, 2, pal.rock);
      if (!rng.int(3)) px(g, x, y, 1, 1, pal.grit);
    }
  }
  for (let i = 0; i < (dry ? 2 : 6); i++) {
    const x = rng.int(TS - 2), y = rng.int(TS - 2);
    // the blade stays put; only its tip stirs
    const lean = cyc(SWAY, f + i);
    px(g, x, y, 1, 2, pal.shade);
    px(g, x + 1 + lean, y + 1, 1, 1, pal.tuft);
  }
}

/** For water, the low nibble of `variant` is an edge mask (1=N 2=E 4=S 8=W)
 *  of the sides that border something else, so only the actual shoreline gets
 *  a rim; the high bits carry an interior variant. */
/** Painted directly, ignoring the declared frame count, so a check can ask a
 *  kind what it looks like at a frame ANIM says it never reaches. A kind that
 *  declares `frames: 1` and reads a CYCLE-length table looks identical to a
 *  static kind through `tile()`, because `tile()` clamps with `frame % nf` —
 *  which is how the idol's eye stayed welded shut through three rounds. */
export function paintTileAt(g, kind, pal, seedRng, f, nf, variant = 0) {
  return paintTile(g, kind, pal, seedRng, f, nf, variant);
}
function paintTile(g, kind, pal, rng, f, nf, variant = 0) {
  const edge = variant & 15;
  switch (kind) {
    case T.GROUND: ground(g, pal, rng, f); break;

    case T.MOSS: {
      ground(g, pal, rng, f, pal.shade);
      for (let i = 0; i < 22; i++) {
        const x = rng.int(TS), y = rng.int(TS);
        px(g, x + cyc(SWAY, f + i), y, 2, 1, pal.tuft);
      }
      // ...and it frays where it stops. A dither two pixels deep on each side
      // that borders something else, so grass comes apart into the ground
      // rather than ending on a ruled line.
      fray(g, variant & 15, rng, pal.ground, (variant >> 4) & 15);
      break;
    }

    case T.SAND: {
      px(g, 0, 0, TS, TS, pal.sand);
      // the same fraying, in the other direction: sand blows onto the grass
      fray(g, variant & 15, rng, pal.ground, (variant >> 4) & 15);
      for (let i = 0; i < 12; i++) {
        const x = rng.int(TS), y = rng.int(TS);
        px(g, x, y, 1, 1, pal.shade);
      }
      // Grit moves. Sand was frames: 1, and on a dust or scree screen it is
      // most of the ground — the worst-measured screen in the game had 65% of
      // its tiles static because of this one kind.
      const drift = cyc(SWAY, f);
      for (let i = 0; i < 3; i++) {
        px(g, (i * 5 + rng.int(4) + drift + TS) % TS, 3 + i * 5, 2, 1, pal.tuft);
      }
      break;
    }

    case T.SEA:
    case T.WATER: {
      /* THE SEA SHELVES AS IT NEARS THE LAND. Bits 10-11 carry how close this
       * water is to the shore, 1 being the water's edge — read from the coast
       * distance field, so the shelf is as wide as the field says rather than
       * whatever an elevation contour happened to cut.
       *
       * It has to be the BASE colour rather than a wash drawn first, because
       * the fill below covers the whole tile and would erase it. */
      const shelf = (variant >> 10) & 3;
      const deep = mix2(wet(pal, 'mid'), '#0d5f74', 0.22);   // a deeper teal under the shelf, as the references have it (2026-09-05)
      // in the wild the ground field shelves the water per pixel (groundfield.ts), so the cell keeps its deep base (LS-218)
      const base = shelf && !pal.noShelf
        ? mix2(deep, wet(pal, 'shallow'), shelf === 1 ? 0.62 : shelf === 2 ? 0.38 : 0.18)
        : deep;
      px(g, 0, 0, TS, TS, base);
      /* THE HORIZONTAL STREAKS WERE HERE, NOT IN THE SHADER.
       *
       * The crests were drawn at `y = 5` and `y = 10` — the same two rows in
       * EVERY water tile. The x wandered per tile and the y never did, so
       * across a lake the dashes lined up into two unbroken bright lines per
       * tile: horizontal streaks on a 16px pitch, over the whole body of
       * water. Three rounds of work went into the presenter looking for them
       * — sines were removed from it for making stripes, then the depth
       * banding that replaced them was warped for making stripes — and the
       * lines survived all of it, because they are painted into the art.
       * Measured by differencing the frame with the water pass on and off:
       * of a 7.66x worst-row-to-median step, the shader accounted for 1.87x.
       *
       * The crest's row now comes from the tile's own variant, so four
       * neighbouring tiles put their crests on four different rows and
       * nothing can rule a line; the marks are shorter, so even two tiles
       * that do agree read as a dash rather than as a join. The surface
       * texture proper — caustics, glints — is the presenter's job now and is
       * computed in world space, where it cannot tile at all.
       */
      // ...AND THEY ARE NOT LITTLE HORIZONTAL LINES.
      //
      // Varying the row was not the whole fault. The marks THEMSELVES were
      // horizontal bars — `pxw(x, y, 3, 1)` and `(2, 1)` — travelling
      // left to right four pixels a frame. So even scattered over the tile
      // they read as exactly what they are: short horizontal lines sliding
      // across the water, which is what was being reported.
      //
      // A crest on water seen from above is a point of light, not a dash.
      // Single pixels, scattered, drifting on their own: the surface texture
      // proper is the presenter's caustic web now, computed in world space
      // where it cannot tile, and this only has to hold up the picture when
      // there is no WebGL to run it.
      // ...AND NOTHING TRAVELS SIDEWAYS.
      //
      // The last piece of the same fault. Open water is ONE cached stamp —
      // `variant` is the shoreline bitmask, so every interior tile is the
      // same image — and `drawTiles` offsets the animation frame BY X
      // (`phase: 'wave'`), so neighbouring columns show consecutive frames.
      // A mark drawn at `x + f` therefore appears one pixel further right in
      // each successive column: a dotted line marching left to right across
      // the whole lake, which is precisely what was reported. It was true of
      // the 3px dashes and it stayed true when they became single pixels.
      //
      // So the marks do not move. They sit at fixed points and blink on and
      // off across the four frames, which reads as light catching a surface
      // and cannot draw a line in any direction. The motion in the water is
      // the presenter's caustic web, which travels in WORLD space where the
      // tile grid cannot organise it.
      const marks = 7;
      for (let i = 0; i < marks; i++) {
        const x = rng.int(16), y = rng.int(16);
        /* PERIOD FOUR, IN A FOUR-FRAME TABLE.
         *
         * This was `(i + f) % 3`, and `ANIM` declares WATER as four frames —
         * so `f = 3` reproduced `f = 0` exactly and the surface was a still
         * image for half of every cycle. Measured: byte-identical frame 3 in
         * 10 of 10 palettes and 4 of 4 variants, on 13.1% of every wild
         * screen, 20 frames out of every 40 at rate 10.
         *
         * `i * 3` keeps the marks scattered rather than blinking in ranks —
         * with a bare `(i + f) & 3` the specks switch on in diagonal bands. */
        const lit = ((i * 3 + f) & 3) !== 0;       // each blinks on its own beat
        if (!lit) continue;
        px(g, x, y, 1, 1, wet(pal, i % 3 === 0 ? 'deep' : 'foam'));
      }
      // shoreline only where the water actually ends
      const d = wet(pal, 'deep');
      /* THE FOAM IS A SURF LINE (2026-09-05, against the reference renders): a bright band on the water side of every
       * shore with a broken row of spray outside it, rather than one thin line of the water's own pale tone. Two rows
       * of foam and a dither, so a coast reads as surf from a screen away and still frays into the bank below. */
      const foam = mix2(wet(pal, 'foam'), '#e8feff', 0.55), spray = mix2(wet(pal, 'foam'), '#ffffff', 0.85);   // cyan-white surf, the reference's brightest note on the shore
      const dot = (x2: number, y2: number, i: number) => { if (((x2 * 7 + y2 * 13 + i * 5) & 3) !== 0) px(g, x2, y2, 1, 1, spray); };
      if (edge & 1) { px(g, 0, 0, TS, 1, d); px(g, 0, 1, TS, 2, foam); for (let x2 = 0; x2 < TS; x2++) dot(x2, 3, 1); }
      if (edge & 4) { px(g, 0, TS - 1, TS, 1, d); px(g, 0, TS - 3, TS, 2, foam); for (let x2 = 0; x2 < TS; x2++) dot(x2, TS - 4, 2); }
      if (edge & 8) { px(g, 0, 0, 1, TS, d); px(g, 1, 0, 2, TS, foam); for (let y2 = 0; y2 < TS; y2++) dot(3, y2, 3); }
      if (edge & 2) { px(g, TS - 1, 0, 1, TS, d); px(g, TS - 3, 0, 2, TS, foam); for (let y2 = 0; y2 < TS; y2++) dot(TS - 4, y2, 4); }
      /* AND THE RIM TURNS THE INNER CORNERS. Four straight sides leave the one
       * case a side cannot see: water wrapping a headland, where all four
       * neighbours are water and the DIAGONAL is not. The shoreline ran
       * straight past it and the land poked a hard two-pixel corner into the
       * lake. `(variant >> 4) & 15` is set only there. */
      rimCorner(g, (variant >> 4) & 15, d, wet(pal, 'foam'));
      /* ...AND THEN THE RIM IS BROKEN, like every other edged tile's.
       *
       * `fray`'s own comment says a seam should "read as a fringe rather than
       * a cut", and it had exactly two call sites: `T.MOSS` and `T.SAND`.
       * `EDGED` lists WATER and SHALLOW alongside them and their draw calls
       * painted a full-width bar and stopped. Measured on the cached 16x16
       * stamp, north edge against interior: water differed on 16 of 16 columns
       * in ONE colour, moss on 11 of 16 in three. The two kinds the suite
       * certifies as edge-aware drew the most ruled edge in the game, on
       * water-meets-land, which is the most-seen surface pair there is. */
      /* ...AND INTO THE BANK WHERE THERE IS A BANK.
       *
       * Fraying every edge into `foam` breaks the line but keeps it entirely
       * inside the water's own palette, so water-meets-water dissolves and
       * water-meets-GRASS -- the pair this comment is about -- still reads as a
       * ruled edge on the tile grid. Bits 16-19 name the dry sides; see the
       * note in `hud.ts`. */
      const wland = (variant >> 16) & 15;
      fray(g, edge & ~wland, rng, wet(pal, 'foam'));
      frayDeep(g, wland, rng, pal.ground || pal.sand || wet(pal, 'foam'));
      break;
    }

    case T.SHALLOW: {
      // the bed shows through as solid submerged stones with a lit top face
      px(g, 0, 0, TS, TS, wet(pal, 'shallow'));
      /* PEBBLES, NOT BRICKS (2026-09-27, LS-474). Three solid five-by-three rectangles a tile read, across a shoal, as a
       * scatter of brown squares on the water. Two stones now, each a rounded lump of its own size -- the middle row full,
       * the rows above and below it a pixel in at each end -- with its lit crown kept to two pixels. */
      for (let i = 0; i < 2; i++) {
        const w = 3 + rng.int(3), h = 2 + rng.int(2), x = rng.int(TS - w), y = rng.int(TS - h);
        for (let r = 0; r < h; r++) { const inset = (h === 3 && r !== 1) || (h === 2 && r === 0) ? 1 : 0; px(g, x + inset, y + r, w - inset * 2, 1, wet(pal, 'bed')); }
        px(g, x + 1, y, Math.min(2, w - 2), 1, wet(pal, 'bed2'));
      }
      /* THE STREAKS WERE STILL HERE, IN THE SHALLOWS.
       *
       * The deep-water crests were fixed — 3px bars on rows 5 and 10 that
       * travelled 4px a frame — and this line was left untouched, drawing
       * FIVE-pixel bars on rows 3 and 10 of every tile and sliding them the
       * same way. Worse than the fault that was fixed, in the tile nobody
       * looked at, and the check written for the deep-water tile tested only
       * `T.WATER`, so it could not have found this.
       *
       * Same cure: single pixels that stay where they are and blink, because
       * open water is ONE cached stamp whose animation frame `drawTiles`
       * offsets by x — so anything that moves with `f` marches across the
       * whole lake, one pixel further along in every successive column.
       */
      for (let i = 0; i < 5; i++) {
        const x = rng.int(TS), y = rng.int(TS);
        if (((i * 3 + f) & 3) === 0) continue;   // period four — see T.WATER
        px(g, x, y, 1, 1, wet(pal, 'foam'));
      }
      /* SURF BELONGS TO A SHORE (the art lens, round 50: the sea carried pale two-tile bands with surf on both
       * sides, ruled edge to edge across open water -- 233 offshore shallow tiles in one island, the longest a
       * straight run of thirty). The foam was drawn on every side that borders a DIFFERENT KIND, which in open sea
       * means the boundary between a shallow and the deep beside it: a ruled white line with nothing to break on.
       * Bits 16-19 already name the sides that are dry LAND, and that is what surf is. A shallow meeting deep water
       * is water meeting water, and wants no line at all. The tile itself is untouched, so the ford a lung-holder
       * wades across is exactly where it was. */
      const landSides = (variant >> 16) & 15;
      if (landSides & 1) px(g, 0, 0, TS, 1, wet(pal, 'foam'));
      if (landSides & 4) px(g, 0, TS - 1, TS, 1, wet(pal, 'foam'));
      if (landSides & 8) px(g, 0, 0, 1, TS, wet(pal, 'foam'));
      if (landSides & 2) px(g, TS - 1, 0, 1, TS, wet(pal, 'foam'));
      rimCorner(g, (variant >> 4) & 15, null, wet(pal, 'foam'));
      /* ...AND BROKEN INTO WHATEVER IS ACTUALLY BESIDE IT.
       *
       * This frayed every edge into `bed2`, a submerged-stone tone, because
       * the four-bit mask says only "a different kind" and cannot tell a bank
       * from open water. Water dissolving into more water is invisible, so the
       * boundary that matters -- water against GRASS -- stayed a ruled line on
       * the tile grid, and a marsh came out as a checkerboard of cyan and green
       * rectangles.
       *
       * Bits 16-19 name the dry sides. Fray those into the bank and the rest
       * into the bed, and the shore comes apart while the open water does not. */
      const land4 = (variant >> 16) & 15;
      fray(g, edge & ~land4, rng, wet(pal, 'bed2'));
      frayDeep(g, land4, rng, pal.ground || pal.sand || wet(pal, 'bed2'));
      break;
    }

    case T.TREE: {
      ground(g, pal, rng, f);
      px(g, 6, 11, 4, 5, '#241a24');
      px(g, 6, 11, 3, 4, pal.trunk);
      // THE FRINGE SWAYS, NOT THE WHOLE TREE. Translating the entire canopy by
      // a pixel moved about a tenth of the tile every step, and a tree is
      // 17.35% of every tile the game draws — measured at 2.065% of its pixels
      // per frame it was the busiest surface in the game by a distance, and
      // its share meant it dominated the picture's total movement: eight times
      // the contribution of the ground it stands on. It is also not how a tree
      // moves. The mass holds still and the outer leaves stir.
      const vx = rng.int(3) - 1, vy = rng.int(2) - 1;
      g.save();
      g.translate(vx, vy);
      px(g, 2, 1, 12, 12, '#241a24');
      px(g, 1, 3, 14, 8, '#241a24');
      px(g, 3, 2, 10, 10, pal.tree);
      px(g, 2, 4, 12, 7, pal.tree);
      px(g, 4, 3, 7, 5, pal.tree2);
      const stir = cyc(SWAY, f);
      px(g, 4 + stir, 1, 8, 2, pal.tree);
      px(g, 5 + stir, 2, 4, 2, pal.tree2);
      for (let i = 0; i < 6; i++) {
        const ty2 = 2 + rng.int(8);
        // the higher the leaf, the more it moves
        px(g, 3 + rng.int(9) + (ty2 < 6 ? stir : 0), ty2, 1, 1, pal.tuft);
      }
      px(g, 3, 10, 9, 1, '#00000030');
      g.restore();
      break;
    }

    case T.ROCK: {
      /* NO `standAway` HERE, and the reason is worth keeping: a boulder is not
       * a field. It is a small object with a hard outline, and what makes it
       * legible is the outline, not its average colour -- pushing its tones
       * around moved the mean-colour reading in fen from 26 to 25 while doing
       * nothing whatever to whether you can see a rock. `test/water.ts`
       * measures objects by the share of their own pixels that stand clear of
       * the ground instead, which reads 90% here and is the true statement. */
      /* THE BOULDER GOES THROUGH THE CAMERA TOO (LS-199). It was a stack of
       * hand-placed rectangles that happened to read as a rock; the crag
       * beside it is now solids projected through the one camera, and two
       * rocks in one picture disagreeing about the view is the whole problem
       * this pass exists to end. Same palette, same ink, same jitter -- one
       * big ball, a smaller one shouldering out of it, and the shadow it
       * sits in. */
      const body = pal.rock;
      const shadowed = pal.rock2;
      if (!pal.noBase) ground(g, pal, rng, f);
      g.translate(rng.int(3) - 1, rng.int(3) - 1);
      const putR = (x: number, y: number, c: string) => { if (x >= -2 && y >= -2 && x < TS + 2 && y < TS + 2) px(g, x, y, 1, 1, c); };
      /* Three tones or it is a blob: the body's own colour across the middle,
       * the shaded one along the foot, and a lit one mixed toward paper for
       * the shoulder -- the palettes carry a rock and its shadow but no
       * highlight, and a solid with `lit === base` came out as a flat lump. */
      const rockSkin = { base: body, lit: M3.lighten(body, 0.22), dark: shadowed };
      /* THE BOULDER FILLS ITS TILE. The water suite measures how much of the
       * tile's middle stands clear of the ground's mean colour, and a 4.9px
       * ball with a soft shadow round it came in at 59% against a floor of
       * 60 in fen -- a rock the ground could swallow. Bigger, and with its
       * shadow no longer a wash of ground tone: 5.8px, and the companion
       * lump set where the ball leaves a gap. */
      M3.groundShadow([TS / 2, 0, 1.5], 5, 0, TS - 2, putR, '#241a2450');
      /* STONE BREAKS IN FLAT PLANES (2026-09-05, against the reference prop sheet, whose boulder is faceted while
       * ours was a smooth grey lump). Two boxes cut across the ball give the mass straight edges where they meet it,
       * which is what a rock has and a ball does not -- and the box's own top face takes the light square-on, so the
       * facet reads as a plane rather than as a patch. The ball stays underneath so the silhouette is still a rock
       * and not a crate. */
      M3.blit(M3.renderScene(TS, TS, [
        { kind: 'ball', at: [TS / 2 - 0.3, 3.8, 0], r: 5.8, rz: 4.6, skin: rockSkin },
        { kind: 'box', at: [TS / 2 - 1.6, 4.6, 1.2], w: 7.5, h: 5, d: 5, skin: rockSkin },
        { kind: 'box', at: [TS / 2 + 3.4, 3.4, 2.2], w: 5, h: 4, d: 4, skin: rockSkin },
        { kind: 'ball', at: [TS / 2 + 3.2, 2.2, 2], r: 3.2, rz: 2.8, skin: rockSkin },
      ], 0, TS - 2, '#241a24'), putR);
      // the highlight catches the light rather than sitting in it
      px(g, 5 + cyc(SWAY, f), 5, 2, 1, '#ffffff30');
      break;
    }

    /* A CAIRN reads as STACKED, which is the whole job of the picture: a
     * boulder is a thing that fell there and a cairn is a thing somebody
     * built, and the only difference a 16x16 tile can carry is that the
     * courses are FLAT and they line up. So: four level courses, each one
     * narrower than the one below, each with its own lit top edge — and
     * unlike `T.ROCK` there is no random translate, because a stack that
     * jitters from tile to tile stops reading as masonry.
     *
     * Palette keys are the WILD ones with hollow fallbacks, not the other way
     * round. Four rounds of this project have shipped a prop that drew itself
     * in `pal.wall`/`pal.floor` — keys only hollows define — and rendered as
     * a black square outdoors, identical in all six biomes. This tile only
     * ever stands outdoors, so the wild names come first. */
    /* A CLIFF FACE. The variant's low nibble is which sides are HIGHER:
     * 1 north, 2 east, 4 south, 8 west. North-higher is the common case and
     * the only one that shows a full face, because in a top-down picture you
     * see the front of a drop and only the corner of its sides.
     *
     * Built like masonry rather than like a boulder: a dark undercut where it
     * meets the ground above, a banded face with vertical joints so the eye
     * reads stone strata, and a shadow pooling at the bottom where it meets
     * the ground below. No random translate — a cliff that jitters tile to
     * tile stops being one cliff. */
    /* A RAMP IS GROUND, and the SLOPE is drawn over it by `drawRamps`.
     *
     * Four versions of this tile tried to carry the slope on their own and all
     * four failed the same way: stripes read as a barcode, jambs and rungs as
     * a ladder, plain shading vanished, and rock risers as a ribbed patch. The
     * fault was the framing, not the pixels — a slope is not a third material
     * sitting between two walls, it is the ground of the UPPER level reaching
     * down to the lower one, and a tile cannot know the upper level's colour
     * because it is painted with its own.
     *
     * So the tile is ordinary ground and the pass, which can see both levels
     * and both palettes, draws the climb across it. */
    /* A RAMP IS ORDINARY GROUND, DELIBERATELY.
     *
     * Five attempts have now put something on this tile -- stripes (a
     * barcode), jambs with rungs (a ladder), plain shading (nothing at all),
     * rock risers (a ribbed patch), and cut shoulders with a grade. The last
     * of those worked as a measurement, taking a ramp from dRGB 15 to 68
     * against the ground beside it, and was rejected on sight: "can we make
     * the ramp just look like normal tiles for god's sake".
     *
     * So the tile carries no mark. What tells you a slope is there is the
     * WALL: `drawCliffs` tapers its face to nothing on both sides of the gap,
     * which is a drop shrinking to zero, and the transition pass carries the
     * terrace shading across it. Those are the same devices the rest of the
     * world is drawn with, which is the point. */
    case T.RAMP: {
      /* THE STAIR IS THE GROUND NOW, NOT A PICTURE OF ONE (2026-09-20; Vijay: "why do they look 2d? they should be a
       * 3d asset"). The scene quantises a ramp's rise into four treads, so the flight is real geometry: each tread a
       * surface the sun shades, each riser a face the shadow pass darkens and the outline pass rims. What the tile
       * carries is what a cut stair is MADE of -- dressed stone, its joints across the way you walk -- and nothing
       * that pretends to be a step. A painted flight over a modelled one was two stairs at different pitches, which is
       * what made a corner of two flights read as a plaid. */
      const { lit, dark } = cliffTones(pal);
      const body = mix2(lit, dark, 0.45), pale = mix2(lit, '#ffffff', 0.16), seam = mix2(dark, lit, 0.18);
      px(g, 0, 0, TS, TS, body);
      const highV = (variant & 1) ? 'up' : (variant & 4) ? 'down' : (variant & 2) ? 'right' : (variant & 8) ? 'left' : 'up';
      const acrossV = highV === 'up' || highV === 'down';
      // the courses of the paving: joints across the way the foot travels, a lit edge on the sun's side of each
      for (let i = 0; i < 4; i++) {
        const u = i * 4;
        if (acrossV) { px(g, 0, u, TS, 1, seam); px(g, 0, u + 1, TS, 1, pale); }
        else { px(g, u, 0, 1, TS, seam); px(g, u + 1, 0, 1, TS, pale); }
      }
      // the wear of a walked stone: a few flecks, seeded by the tile, and the kerbs along the flanks
      for (let i = 0; i < 10; i++) { const x = rng.int(TS), y = rng.int(TS); px(g, x, y, 1, 1, rng.chance(0.5) ? mix2(body, dark, 0.3) : mix2(body, pale, 0.4)); }
      if (acrossV) { px(g, 0, 0, 2, TS, mix2(dark, lit, 0.3)); px(g, TS - 2, 0, 2, TS, mix2(lit, dark, 0.22)); }
      else { px(g, 0, 0, TS, 2, mix2(dark, lit, 0.3)); px(g, 0, TS - 2, TS, 2, mix2(lit, dark, 0.22)); }
      break;
    }
    case T.CLIFF: {
      // both tones from the country's ground, so the mass moves as one -- see
      // `cliffTones`; deriving them separately left fen's face at dRGB 24
      // against its own mud, each tone dutifully "held clear" in a different
      // direction
      const { face, lit, dark } = cliffTones(pal);
      const mask = variant & 15;
      /* A CRAG IS AN OUTCROP, NOT A SHORT WALL.
       *
       * Two thirds of the CLIFF formations on a finished map are three tiles or
       * fewer, and everything below draws a WALL: a filled tile with a lit brink
       * along the top and an ink outline round it. At one tile that is a plank,
       * and three rounds of art review said so in the same words -- fallen logs,
       * dominoes lying flat, and finally, from play, over a screen of scree
       * where every blocker was a grey slab: "how have we still not fixed this".
       *
       * Height was capped for these already (see `terrain.ts`) and capping the
       * height of a plank gives a thinner plank. What was wrong is the SHAPE. A
       * lone lump of rock in a field is a boulder shouldering out of the ground,
       * so it is drawn as one: the country's floor, then a mass with a rounded
       * outline, dark where it meets the ground and lit where the top of it
       * catches the sky.
       *
       * It grows to the tile edge on any side where the rock carries on (bits
       * 32/64/128/2048), so a two- or three-tile crag is one mass rather than
       * separate boulders touching at their corners, and pulls back from the
       * sides where it does not -- which is what gives it an outline at all. */
      if (variant & 1024) {
        ground(g, pal, rng, f);
        const inW = (variant & 32) ? 0 : 2 + rng.int(2);
        const inE = (variant & 64) ? 0 : 2 + rng.int(2);
        const inS = (variant & 128) ? 0 : 2 + rng.int(2);
        const inN = (variant & 2048) ? 0 : 2 + rng.int(2);
        const x0 = inW, y0 = inN, w2 = TS - inW - inE, h2 = TS - inN - inS;
        /* AND IT IS DRAWN AS A DOME, NOT AS A BOX. Reported from play with a
         * screenshot and four arrows on it: "what are these random ledges,
         * they really mess with my sense of depth perception". They were
         * boulders in the code and rectangles on the screen -- a filled
         * rectangle, a lighter rectangle inside it and a white line across the
         * top, which on a brown floor is a crate, and worse, the lit band read
         * as a TOP FACE, so every one of them looked like a one-tile step up.
         * A step you cannot climb, scattered at random over flat ground, is
         * exactly the thing that ruins depth: the picture promises a height
         * that the world does not have.
         *
         * A rock is round. Each row is inset by a curve on the sides that are
         * free (a side where the rock carries on stays square, so two tiles
         * still make one mass), the light sits as a cap on the shoulder rather
         * than a band across the top, and the ground under the near edge takes
         * a contact shadow -- which is the thing that says "sitting on" rather
         * than "standing above". */
        /* THE MASS IS A SOLID, PROJECTED (LS-199). It was a rectangle with a
         * lit top edge, and a player put four arrows on a screenshot: "what
         * are these random ledges, they really mess with my sense of depth
         * perception". They were boulders in the code and steps on the
         * screen, because the lit band read as a TOP FACE -- a promise of a
         * height the world does not have, scattered at random over flat
         * ground. Rounding the corners by hand only made a tombstone.
         *
         * So it is built the way the forager is now built: as a solid in
         * world space, through the one camera (`model3d.ts`), which is what
         * makes a rock in a field and a body walking past it agree about
         * where the ground is. Two or three overlapping balls, sized from
         * this tile's own seed, with a contact shadow at the foot; where the
         * rock carries on into the next tile a ball is pushed out that way so
         * the mass runs on instead of ending in a pebble. */
        const put = (x: number, y: number, c: string) => { if (x >= 0 && y >= 0 && x < TS && y < TS) px(g, x, y, 1, 1, c); };
        M3.blit(cragMass(rng, { base: face, lit, dark }, { inW: !!inW, inE: !!inE, inN: !!inN, inS: !!inS }), put);
        break;
      }
      if (!(mask & 1)) {
        // the side or the back of a plateau: from above it is the plateau's
        // floor, so it wears the level above's ground -- `drawCliffs` inks the
        // edge. Only a tile with higher ground to its NORTH is a wall you see.
        // a foot (higher ground beside it) is the rim of the plateau above and
        // wears that floor; a brink (nothing higher) is already on its plateau
        ground(g, (mask & 15) && pal.up ? pal.up() : pal, rng, f);
        break;
      }
      /* A SHORT RUN IS AN OUTCROP, NOT A WALL (LS-199). Reported from play
       * with four arrows on a screenshot: "what are these random ledges, they
       * really mess with my sense of depth perception". Every arrow was on a
       * stub of wall -- one, two or three tiles of face standing in open dirt
       * -- and a stub of masonry lying in a field is a crate: its lit brink
       * reads as a TOP FACE, so it promises a step up onto a level the world
       * does not have. The small FORMATIONS were already caught (bit 1024),
       * but a big formation can still present a stub, which is why the first
       * pass at this changed almost nothing on the map.
       *
       * Bit 4096 is that run length, measured in `hud.ts` where the
       * neighbours are already being walked. The rock is drawn as a mass
       * through the same camera as every other solid; the foot, the collision
       * and every tier derived from the face stay exactly where they were. */
      if ((variant & 1) && (variant & 4096) && !(variant & 1024)) {
        ground(g, pal, rng, f);
        const putL = (x: number, y: number, c: string) => { if (x >= 0 && y >= 0 && x < TS && y < TS) px(g, x, y, 1, 1, c); };
        // a two- or three-tile stub is ONE run of rock: where the face
        // carries on, the mass runs on with it rather than ending in a pebble
        M3.blit(cragMass(rng, { base: face, lit, dark },
          { inW: !(variant & 32), inE: !(variant & 64), inN: !(variant & 2048), inS: !(variant & 128) }), putL);
        break;
      }
      stoneMass(g, rng, face, lit, dark);
      if (mask & 1) {                       // the ground to the north is higher
        /* A WALL, NOT A FENCE.
         *
         * Two earlier cuts of this failed the same way for opposite reasons.
         * The first was near-black and read as a hole punched in the grass.
         * The second answered that with evenly spaced horizontal courses and
         * vertical joints between them — which is a description of PALINGS,
         * and a run of it read as wooden fencing along the terraces.
         *
         * Rock is a MASS: one lit edge where the sun catches the brink, a body
         * that is all one tone, and irregular fracture rather than a rhythm.
         * The regularity was the fault both times, so nothing here is evenly
         * spaced and the vertical joints are gone entirely. */
        px(g, 0, 0, TS, 1, dark);           // the undercut, under the ground above
        px(g, 0, 1, TS, 2, '#ffffff26');    // the brink, catching the light
        px(g, 0, 3, TS, 10, face);          // the body of the face, one tone
        // NO FRACTURE MARKS HERE. "This tile's own seed" is the variant, which
        // for a cliff is the terrain's answer and the same for every tile along
        // a run -- so three marks at fixed heights lined up into a dashed rule
        // across the whole face. `drawCliffs` scatters them on world position.
        px(g, 0, 5 + rng.int(4), TS, 1, lit);   // one seam of paler stone
        if (variant & 128) {
          // the wall carries on below: no foot here, the rock simply continues
          px(g, 0, 13, TS, 3, face);
        } else {
          px(g, 0, 13, TS, 1, dark);        // where it meets the ground below
          g.fillStyle = 'rgba(18,14,26,0.40)';
          g.fillRect(0, 14, TS, 2);         // and the shadow it throws forward
        }
        /* THE ENDS OF A RUN ARE CORNERS. Without these the wall stops square
         * in mid-air, which is the thing a Wang set exists to prevent: the
         * tile has to know whether the face carries on beside it. */
        if (!(variant & 32)) {              // nothing to the west: left return
          px(g, 0, 1, 2, 13, dark);
          px(g, 1, 2, 1, 11, face);
        }
        if (!(variant & 64)) {              // nothing to the east: right return
          px(g, TS - 2, 1, 2, 13, dark);
          px(g, TS - 2, 2, 1, 11, face);
        }
      }
      /* THE SIDES AND THE BACK ARE THE PASS'S JOB, NOT THE TILE'S.
       *
       * Three blocks lived here: a four-pixel band of pale stone with its own
       * ink line down the east side, the same down the west, and a lit strip
       * across the south. `drawCliffs` now draws all three of those seams --
       * ink at the boundary, a lit pixel on the HIGH side of it, and an
       * occlusion gradient on the ground below -- so the tile was drawing a
       * second, differently-placed set of the same cues underneath them.
       *
       * On a hillside that steps down every tile, which is most of heath and
       * all of ash, every tile has a higher neighbour and so every tile got a
       * band: the cliffs came out as a brick wall and no amount of retuning the
       * stone tones touched it, because the grid was not in the tones. One
       * place decides where a cliff's edges are, and it is the pass that can
       * see both sides of them. */
      if (!mask) {
        /* ROCK WITH NOTHING HIGHER BESIDE IT -- the inside of a mass. It is not
         * an edge of anything, so it gets no line.
         *
         * It used to fill with the pale tone and rule one pixel of ink across
         * its top, and stacked up a hillside that is a dark line every sixteen
         * pixels: heath's cliffs came out as a brick wall, five courses of it,
         * and the mortar was this. The mass is already painted above; the only
         * thing to add is that it is a little deeper inside than at its rim. */
        px(g, 0, 0, TS, TS, face);
        for (let i = 0; i < 5; i++) {
          const w = 1 + rng.int(4), h = 1 + rng.int(2);
          px(g, rng.int(TS - w + 1), rng.int(TS - h + 1), w, h, lit);
        }
      }
      /* AND THE RIVER GOES OVER IT.
       *
       * The world has a flow field and it has terraces, so wherever a river
       * meets a step the water falls — that is not decoration added on top,
       * it is the two fields agreeing. Bit 16 is set by the draw when the
       * higher tile this face hangs from carries the river.
       *
       * Drawn as falling water rather than as a blue rectangle: three ribbons
       * at different speeds so the fall has body, a bright leading edge that
       * travels down the face, and foam pooling where it lands. */
      if (variant & 16) {
        const deep = pal.mid || '#3a7fa0';
        const pale = pal.foam || '#a8d8e2';
        const lip2 = pal.shallow || '#6aa8b6';
        /* SPILLING TOWARD WHERE THE WATER IS GOING. Bits 256 and 512 say the
         * fall carries on west or east of this tile -- see the note in
         * `drawOneTile`. Where it does, the brink and the pool run right out to
         * that edge instead of stopping two pixels short, so a cascade that
         * steps sideways as it descends joins into one sheet rather than a row
         * of separate stamps touching at their corners. */
        const west = !!(variant & 256), east = !!(variant & 512);
        const bx = west ? 0 : 2, bw = (east ? TS : TS - 2) - bx;
        px(g, bx, 0, bw, 2, lip2);            // the water at the brink
        const ribbons = [[3, 3], [7, 4], [11, 3]];
        for (let i = 0; i < ribbons.length; i++) {
          const [rx, rw] = ribbons[i];
          px(g, rx, 1, rw, 13, deep);
          // the bright edge travels down at its own speed per ribbon
          const yy = 1 + ((f * (2 + i) + i * 3) % 12);
          px(g, rx, yy, rw, 2, pale);
        }
        const px0 = west ? 0 : 1, pw = (east ? TS : TS - 1) - px0;
        px(g, px0, 12, pw, 3, deep);          // the pool it falls into
        /* ...AND A SHOULDER DOWN THE SIDE IT LEAVES BY. Widening the pool
         * alone still leaves two DIAGONAL neighbours meeting at a single
         * point. Carrying water down the last third of the edge it spills over
         * gives that meeting a six-pixel waist, which is what reads as one
         * falling sheet at this size. */
        if (west) px(g, 0, 9, 3, 7, deep);
        if (east) px(g, TS - 3, 9, 3, 7, deep);
        px(g, 2 + ((f * 2) % 4), 13, 4, 1, pale);
        px(g, 9 - ((f * 2) % 4), 14, 4, 1, pale);
      }
      break;
    }

    case T.CAIRN: {
      if (!pal.noBase) ground(g, pal, rng, f);
      const stone = pal.rock || pal.wall;
      const dark = pal.rock2 || pal.wall2;
      /* THE COURSES GO THROUGH THE CAMERA (LS-199), and they stay COURSES:
       * flat, level, each narrower than the one below, because that is the
       * whole difference between a thing that fell there and a thing somebody
       * built. What the projection adds is that each course is an ellipse
       * seen from above rather than a rectangle, so a cairn and the boulder
       * beside it now agree about the view. */
      const putC = (x: number, y: number, c: string) => { if (x >= 0 && y >= 0 && x < TS && y < TS) px(g, x, y, 1, 1, c); };
      const cSkin = { base: dark, lit: stone, dark: dark };
      M3.groundShadow([TS / 2, 0, 1.5], 6, 0, TS - 1, putC, '#00000030');
      // each course is a flat slab with its own lit top: a stack you can
      // count, which is what tells a built thing from a fallen one
      for (const i of [0, 1, 2, 3]) {
        const y = 1.4 + i * 3.1, r = 6 - i * 1.2;
        M3.blit(M3.renderScene(TS, TS, [{ kind: 'ball', at: [TS / 2, y, 0], r, rz: r * 0.7, ry: 1.5, skin: cSkin }], 0, TS - 1, '#241a24'), putC);
        M3.blit(M3.renderScene(TS, TS, [{ kind: 'ball', at: [TS / 2, y + 1, 0], r: r - 1.2, rz: r * 0.5, ry: 0.7, skin: { base: stone, lit: stone, dark: stone } }], 0, TS - 1), putC);
      }
      /* THE CAPSTONE IS NOT A FACE.
       *
       * It was `pal.stoneFace` for one draft, which looked right and meant
       * something else: that colour is the STANDING STONE's, the one pale
       * surface in the wilds, and it is pale because there is deepmark cut
       * into it. Six pixels of it on a cairn is a promise of writing on a
       * thing with no writing on it — and at 0.733 relative luminance it also
       * out-shone the player, which is the fault this project already fixed
       * once, in `T.ROCK`. The cap is stone, lit along its top edge like
       * every other course. */
      px(g, 6, 1, 4, 2, '#241a24');                   // the capstone, set on last
      px(g, 6, 1, 4, 1, stone);
      // The gap the pod goes in. Every other solid tile in this game is
      // closed; this one is visibly not, and that is the tell.
      px(g, 7, 10, 3, 3, '#00000090');
      px(g, 7 + cyc(SWAY, f), 4, 2, 1, '#ffffff28');  // and the light crosses it
      break;
    }

    case T.DEBRIS: {
      /* SPOIL IS SMALL STONES, NOT LITTLE LEDGES (LS-199). Each chip was a
       * 3x2 rectangle of ink with a 2x1 of rock laid on its top-left -- which
       * at this size is a tiny box with a LIT TOP FACE, seven of them per
       * tile, scattered over open ground. A player photographed a screen full
       * of them and drew four arrows: "what are these random ledges, they
       * really mess with my sense of depth perception". They were right, and
       * they are the reason a whole biome read as littered with crates.
       *
       * Seven little solids through the one camera instead: each one round,
       * lit on the shoulder the sun is on, dark at the foot, sitting in a
       * shadow of its own. Same count, same scatter, same seed. */
      if (!pal.noBase) ground(g, pal, rng, f);
      const putD = (x2: number, y2: number, c: string) => { if (x2 >= 0 && y2 >= 0 && x2 < TS && y2 < TS) px(g, x2, y2, 1, 1, c); };
      const dSkin = { base: pal.rock, lit: M3.lighten(pal.rock, 0.24), dark: pal.rock2 || '#241a24' };
      for (let i = 0; i < 7; i++) {
        const x = 2 + rng.int(TS - 4), y = 2 + rng.int(TS - 4), r = 1.3 + rng.int(2) * 0.5;
        M3.groundShadow([0, 0, 0.8], r * 1.1, x, y + 1, putD, '#00000026');
        M3.blit(M3.renderScene(TS, TS, [{ kind: 'ball', at: [x, r * 0.7, 0], r, rz: r * 0.8, ry: r * 0.8, skin: dSkin }], 0, y + 1, '#241a24'), putD);
      }
      break;
    }
    case T.BONES: {
      /* WHERE THE PACK ATE (LS-380). The sow leaves debris; the pack leaves
       * bones, so a screen says which of them was here without a word. Two
       * long bones and a few knuckles, pale, through the same camera as the
       * spoil so they sit on the ground rather than on the picture. */
      if (!pal.noBase) ground(g, pal, rng, f);
      const putB = (x2: number, y2: number, c: string) => { if (x2 >= 0 && y2 >= 0 && x2 < TS && y2 < TS) px(g, x2, y2, 1, 1, c); };
      const bone = UI.bone || '#e2e8ec';
      const bSkin = { base: bone, lit: M3.lighten(bone, 0.1), dark: darken(bone, 0.55) };
      // each piece is its own little scene at depth zero, blitted at its row, the way the spoil is
      const piece = (objs: any[], y: number) => M3.blit(M3.renderScene(TS, TS, objs, 0, y, '#241a24'), putB);
      /* A SKULL, TWO LONG BONES AND A RIM OF INK (round 53's art lens: the first cut was a 5x7 pale nub with no
       * ink on bare dirt). Each piece is drawn twice: the ink first, a shade wider, then the bone over it, so a
       * pale thing on pale ground keeps a one-pixel edge, the way the spoil and the items do. */
      const ink = { base: '#2a1e22', lit: '#2a1e22', dark: '#2a1e22' };
      const sx = 2 + rng.int(4), sy = 3 + rng.int(TS - 9);   // the skull, on the left or the right
      const skullX = rng.chance(0.5) ? sx : TS - 2 - sx;
      M3.groundShadow([0, 0, 0.8], 1.8, skullX, sy + 1.5, putB, '#0000002e');
      piece([{ kind: 'ball', at: [skullX, 1.1, 0], r: 2.3, rz: 1.9, ry: 2.0, skin: ink, paintOnly: true },
        { kind: 'ball', at: [skullX, 1.2, 0], r: 1.8, rz: 1.5, ry: 1.6, skin: bSkin },
        { kind: 'ball', at: [skullX - 0.7, 1.9, -1.2], r: 0.45, skin: ink },   // the eye pits, toward the camera
        { kind: 'ball', at: [skullX + 0.7, 1.9, -1.2], r: 0.45, skin: ink }], sy + 1);
      for (let i = 0; i < 2; i++) {
        const dx = 5 + rng.int(3), x = 1 + rng.int(TS - 2 - dx), y = 2 + rng.int(TS - 5);
        M3.groundShadow([0, 0, 0.8], 1.4, x + dx / 2, y + 1, putB, '#00000026');
        piece([{ kind: 'rod', a: [x, 0.6, 0], b: [x + dx, 0.6, 0], r: 1.2, skin: ink, paintOnly: true },
          { kind: 'ball', at: [x, 0.7, 0], r: 1.4, rz: 1.1, ry: 1.1, skin: ink, paintOnly: true }, { kind: 'ball', at: [x + dx, 0.7, 0], r: 1.4, rz: 1.1, ry: 1.1, skin: ink, paintOnly: true },
          { kind: 'rod', a: [x, 0.7, 0], b: [x + dx, 0.7, 0], r: 0.7, skin: bSkin },
          { kind: 'ball', at: [x, 0.8, 0], r: 1.0, rz: 0.8, ry: 0.8, skin: bSkin },
          { kind: 'ball', at: [x + dx, 0.8, 0], r: 1.0, rz: 0.8, ry: 0.8, skin: bSkin }], y + 1);
      }
      for (let i = 0; i < 2; i++) {
        const x = 2 + rng.int(TS - 4), y = 2 + rng.int(TS - 4);
        piece([{ kind: 'ball', at: [x, 0.5, 0], r: 1.2, rz: 0.9, ry: 0.9, skin: ink, paintOnly: true }, { kind: 'ball', at: [x, 0.6, 0], r: 0.8, rz: 0.6, ry: 0.6, skin: bSkin }], y + 1);
      }
      break;
    }

    case T.FORD: {
      /* A FORD (LS-383): the wet sand of a crossing the tide bares. Darker than the beach, with two ripple lines
       * and a few pale shell-flecks, so a causeway reads as a thing the sea left rather than as a road. */
      px(g, 0, 0, TS, TS, '#a89474');
      for (let i = 0; i < 2; i++) { const y = 3 + i * 7 + rng.int(3); px(g, rng.int(4), y, 8 + rng.int(6), 1, '#8c7a5e'); px(g, 2 + rng.int(4), y + 1, 5 + rng.int(4), 1, '#b8a68a'); }
      for (let i = 0; i < 3; i++) px(g, rng.int(TS), rng.int(TS), 1, 1, '#ddd2bc');
      break;
    }

    case T.ICE: {
      /* ICE (LS-381, the second element): a pool under the frost line. A pale blue-white sheet with a few dark
       * cracks and two bright glints, drawn flat, so it reads as the water gone hard rather than as sand. */
      px(g, 0, 0, TS, TS, '#c9dfe6');
      for (let i = 0; i < 5; i++) { const x = rng.int(TS), y = rng.int(TS), l = 2 + rng.int(4); px(g, x, y, l, 1, '#a9c6d0'); }
      for (let i = 0; i < 3; i++) { const x = rng.int(TS - 3), y = rng.int(TS - 3); px(g, x, y, 1, 3, '#8fb2be'); px(g, x + 1, y + 2, 2, 1, '#8fb2be'); }   // cracks
      px(g, 2 + rng.int(6), 2 + rng.int(6), 2, 1, '#f2fbff'); px(g, 8 + rng.int(6), 8 + rng.int(6), 1, 2, '#f2fbff');   // glints
      break;
    }

    case T.ROD: {
      /* A LIGHTNING ROD (LS-381): a dark iron shaft stuck in the ground with a copper tip, on a small stone foot.
       * The tallest thing on its screen by the storm's reckoning, and it looks it: one straight line in a world of
       * rounded things, which is the one shape the eye finds first. */
      ground(g, pal, rng, f);
      px(g, 5, 13, 6, 2, '#4a4652'); px(g, 6, 12, 4, 1, '#6a6674');   // the foot
      px(g, 7, 2, 2, 11, '#23202b'); px(g, 7, 3, 1, 9, '#3f3a4a');   // the shaft, lit on its left
      px(g, 7, 0, 2, 3, '#d08a4a'); px(g, 7, 0, 1, 2, '#f0b070');   // the copper tip
      break;
    }

    case T.RUBBLE: {
      /* A BOULDER ON THE TURF, NOT A TILE OF BOULDER. In the wild the ground
       * field bakes the meadow under the rubble and the turf grows over it, so
       * the sprite drawn on top keeps only its stone and shadow; its own
       * square of ground would cut a sixteen-pixel edge into the meander
       * (LS-218). Hollows still get the base: nothing is under them. */
      if (!pal.noBase) ground(g, pal, rng, f);
      /* CRACKED STONE, THROUGH THE ONE CAMERA (LS-199). This was a filled
       * rectangle with shadow strokes painted on it -- the same crate shape a
       * player arrowed on a screenshot -- and it is the tile the whole
       * breaking verb rests on, so it has to read as a boulder you could hit.
       * A big ball and a shoulder, the cracks kept exactly as they were,
       * because the cracks are what say `breakable`. */
      const putU = (x2: number, y2: number, c: string) => { if (x2 >= 0 && y2 >= 0 && x2 < TS && y2 < TS) px(g, x2, y2, 1, 1, c); };
      const uSkin = { base: pal.rock, lit: M3.lighten(pal.rock, 0.22), dark: pal.rock2 || pal.rock };
      const uDark = { base: pal.rock2 || pal.rock, lit: pal.rock, dark: mix2(pal.rock2 || pal.rock, '#241a24', 0.4) };
      M3.groundShadow([TS / 2, 0, 1.5], 6, 0, TS - 1, putU, '#00000030');
      /* A PILE, NOT A BOULDER (2026-09-05, against the reference prop sheet, whose rubble is many separate stones of
       * different sizes). One big ball with a shoulder read as the same rock as `T.ROCK` with cracks drawn on it --
       * and rubble is the thing you have already half broken. Five stones, the biggest at the back, the small ones
       * spilling toward the viewer; the cracks stay on the big one, because the cracks are what say breakable. */
      M3.blit(M3.renderScene(TS, TS, [
        { kind: 'ball', at: [TS / 2 - 0.6, 5.0, -1], r: 5.4, rz: 4.2, ry: 5.2, skin: uSkin },
        { kind: 'box', at: [TS / 2 - 1.2, 5.6, 0], w: 6.5, h: 4.4, d: 4.4, skin: uSkin },
        /* ...AND THE LOOSE ONES ARE THEIR OWN STONE. Painted in one skin the five merged into a single mass, because
         * the renderer outlines the silhouette and not the boundaries inside it; a darker cut of the same rock on
         * the fallen stones is what separates them. */
        { kind: 'ball', at: [TS / 2 + 3.6, 2.6, 2.4], r: 3.0, rz: 2.6, ry: 2.6, skin: uDark },
        { kind: 'ball', at: [TS / 2 - 4.4, 2.0, 2.8], r: 2.6, rz: 2.2, ry: 2.2, skin: uSkin },
        { kind: 'ball', at: [TS / 2 + 0.8, 1.4, 4.0], r: 2.2, rz: 1.9, ry: 1.9, skin: uDark },
      ], 0, TS - 1, '#241a24'), putU);
      px(g, 7, 3, 2, 3, '#00000060');
      px(g, 6, 6, 2, 3, '#00000060');
      px(g, 8, 9, 2, 4, '#00000060');
      px(g, 5, 10, 2, 2, '#00000045');
      px(g, 7, 12, 1, 1, '#00000055');
      // GRIT COMES OUT OF A CRACK. Every moving pixel on this tile came from
      // `ground()` underneath, and the boulder covers rows 2-14 opaquely — so
      // whenever the ground's speck landed under the stone the tile was a
      // still image, forever and identically in every world (the seed carries
      // no position). 6 of 24 biome/variant combinations and all three hollow
      // palettes at variant 2 measured at exactly zero pixels per frame.
      // Cracked stone that sheds is also the cue the whole verb rests on.
      const grit = 3 + ((f * 2 + rng.int(4)) % 9);
      px(g, 6 + rng.int(3), grit, 1, 1, '#00000055');
      break;
    }

    case T.CRACK: {
      px(g, 0, 0, TS, TS, pal.wall2);
      px(g, 0, 0, TS, 1, '#ffffff18');
      px(g, 6, 0, 3, 5, '#00000090');
      px(g, 5, 4, 3, 5, '#00000090');
      px(g, 7, 8, 3, 6, '#00000090');
      px(g, 3, 8, 3, 3, '#00000070');
      px(g, 10, 3, 2, 4, '#00000070');
      px(g, 2, 13, 12, 3, '#241a24');          // spill of loose stone
      px(g, 3, 14, 4, 2, pal.wall);
      px(g, 9, 14, 4, 2, pal.wall);
      break;
    }

    case T.PUSH: {
      // standing proud of the floor: lit top-left, shadowed bottom-right,
      // and casting onto the tile below
      /* A PUSHED STONE IS A CUBE ON PURPOSE (LS-199) -- it is cut, it is
       * meant to look moveable, and its square top is how you read that it
       * slides. What it gets from the pipeline is where the light comes from
       * and a shadow that says it sits on the floor: the top face lit, the
       * near face in the body tone, the far side dark, and the shadow cast
       * toward the viewer as every other solid casts it. */
      if (!pal.noBase) px(g, 0, 0, TS, TS, pal.floor || pal.ground);
      const putP = (x2: number, y2: number, c: string) => { if (x2 >= 0 && y2 >= 0 && x2 < TS && y2 < TS) px(g, x2, y2, 1, 1, c); };
      M3.groundShadow([TS / 2, 0, 3.5], 7, 0, TS - 1, putP, '#00000048');
      px(g, 1, 1, 14, 13, '#241a24');
      px(g, 2, 2, 12, 11, pal.wall);
      px(g, 2, 2, 12, 3, M3.lighten(pal.wall, 0.28));        // the top face, to the sky
      px(g, 2, 5, 12, 1, '#00000030');                       // its front edge
      px(g, 12, 5, 2, 8, '#00000060');                       // the shaded side
      px(g, 2, 12, 12, 1, '#00000060');
      px(g, 5, 7, 6, 4, pal.wall2);                          // the cut face
      px(g, 6, 8, 4, 2, pal.wall);
      break;
    }

    /* A TILE THAT READS A PALETTE KEY ITS PALETTE DOES NOT HAVE.
     *
     * `pal.floor`, `pal.wall` and `pal.accent` are HOLLOW palette keys. A
     * biome has `ground/shade/tuft/tree/rock/sand`, and `fillStyle =
     * undefined` is silently ignored — so every one of those fills painted
     * whatever colour was set last. Indoors the brazier was fine and nobody
     * looked at it outdoors, so round 26 put it on 34.5% of wild screens as a
     * five-colour, 59%-near-black square with no ground under it, IDENTICAL
     * in all six biomes.
     *
     * `T.SCORCH`, four cases below, already reads `pal.floor || pal.ground`
     * for exactly this reason, and the `HOLLOW_PALS` comment records the same
     * fault running the other way. Third occurrence of one bug. */
    case T.BRAZIER: {
      // frame 0, not `f`: `ANIM` declares this tile a single picture and
      // `ground()` speckles differently on each frame, so passing the live
      // counter made a cold bowl shimmer — and a check that pairs the
      // declared frame count against what is drawn caught it.
      if (!pal.noBase) ground(g, pal, rng, 0);
      if (pal.floor) px(g, 0, 0, TS, TS, pal.floor);
      // the brazier as solids (LS-199): a foot, a stem, a bowl, all through
      // the one camera, so it stands on the floor the same way the rocks do
      const bStone = pal.wall || pal.rock;
      const putB = (x: number, y: number, c: string) => { if (x >= 0 && y >= 0 && x < TS && y < TS) px(g, x, y, 1, 1, c); };
      const bSkin = { base: bStone, lit: M3.lighten(bStone, 0.2), dark: '#241a24' };
      M3.groundShadow([TS / 2, 0, 1], 5, 0, TS - 1, putB, '#00000030');
      /* THREE LEGS AND A BOWL WITH AN INSIDE (2026-09-05, against the reference prop sheet, whose brazier is a stone
       * vessel on legs rather than a lid on a post). A disc foot reads as a plinth; three legs read as a stand. And
       * a bowl is only a bowl if you can see into it: the rim stands proud and the well behind it is dark. */
      M3.blit(M3.renderScene(TS, TS, [
        { kind: 'rod', a: [TS / 2 - 3.4, 0.4, 0], b: [TS / 2 - 1.4, 5.5, 0], r: 1.1, skin: bSkin },
        { kind: 'rod', a: [TS / 2 + 3.4, 0.4, 0], b: [TS / 2 + 1.4, 5.5, 0], r: 1.1, skin: bSkin },
        { kind: 'rod', a: [TS / 2, 0.4, 2.6], b: [TS / 2, 5.5, 0.6], r: 1.1, skin: bSkin },
        { kind: 'ball', at: [TS / 2, 7.6, 0], r: 6, rz: 4, ry: 2.2, skin: bSkin },
      ], 0, TS - 1, '#241a24'), putB);
      px(g, 3, 6, 10, 1, pal.accent || pal.tuft);      // a rim, so it reads as a bowl
      px(g, 4, 7, 8, 1, mix2(bStone, '#241a24', 0.55));   // and the shaded inside of it, behind the rim
      px(g, 4, 8, 8, 2, '#2a1c14');                    // cold ash
      px(g, 6, 9, 1, 1, '#7a4a24');
      px(g, 9, 10, 1, 1, '#5a3a20');
      break;
    }

    case T.BRAZIER_LIT: {
      if (!pal.noBase) ground(g, pal, rng, f);
      if (pal.floor) px(g, 0, 0, TS, TS, pal.floor);
      const bStone2 = pal.wall || pal.rock;
      const putB2 = (x: number, y: number, c: string) => { if (x >= 0 && y >= 0 && x < TS && y < TS) px(g, x, y, 1, 1, c); };
      const bSkin2 = { base: bStone2, lit: M3.lighten(bStone2, 0.2), dark: '#241a24' };
      M3.groundShadow([TS / 2, 0, 1], 5, 0, TS - 1, putB2, '#00000030');
      // the same stand as the cold one: three legs and a bowl, or the fire changes the furniture it burns in
      M3.blit(M3.renderScene(TS, TS, [
        { kind: 'rod', a: [TS / 2 - 3.4, 0.4, 0], b: [TS / 2 - 1.4, 5.5, 0], r: 1.1, skin: bSkin2 },
        { kind: 'rod', a: [TS / 2 + 3.4, 0.4, 0], b: [TS / 2 + 1.4, 5.5, 0], r: 1.1, skin: bSkin2 },
        { kind: 'rod', a: [TS / 2, 0.4, 2.6], b: [TS / 2, 5.5, 0.6], r: 1.1, skin: bSkin2 },
        { kind: 'ball', at: [TS / 2, 7.6, 0], r: 6, rz: 4, ry: 2.2, skin: bSkin2 },
      ], 0, TS - 1, '#241a24'), putB2);
      px(g, 3, 6, 10, 1, '#ffffff28');
      const h = cyc(RISE, f);
      const lean = cyc(SWAY, f);
      px(g, 5, 4 - h, 6, 5 + h, '#c9553f');
      px(g, 6 + lean, 3 - h, 3, 4 + h, '#f2c14e');
      px(g, 7 + lean, 2 - h, 1, 2, '#fff3c8');
      px(g, 4, 8, 8, 2, '#f2c14e');
      px(g, 3, 9, 10, 1, '#c9553f');
      break;
    }

    case T.SCORCH: {
      px(g, 0, 0, TS, TS, pal.floor || pal.ground);
      for (let i = 0; i < 9; i++) {
        const x = rng.int(TS - 2), y = rng.int(TS - 2);
        px(g, x, y, 2, 2, '#241a24');
        if (i % 3 === 0) px(g, x, y, 1, 1, '#4a3a2a');
      }
      break;
    }

    case T.TUFT: {
      /* CUT GRASS, LYING WHERE IT FELL (LS-176 step 4). The soil of the moss
       * it was, with the stubble of the cut stems in it, and the cut blades
       * lying across it as straw: five little bundles of three stalks each,
       * every stalk a rod through the one camera, pale where the sun is and
       * dark under, the way a stem lying on the ground is. Dry, so the straw
       * is the sand's colour and not the grass's; this is the tile that says
       * FLAMMABLE, and pale on green is what reads as dry from a screen away.
       * The stalks stir with the wind cycle, because nothing in the wild is
       * still and cut grass least of all. */
      ground(g, pal, rng, f, pal.shade);
      for (let i = 0; i < 10; i++) px(g, rng.int(TS), rng.int(TS), 1, 1, pal.tuft);   // the stubble
      const putT = (x2: number, y2: number, c: string) => { if (x2 >= 0 && y2 >= 0 && x2 < TS && y2 < TS) px(g, x2, y2, 1, 1, c); };
      const straw = pal.sand || '#c9b27a';
      const tSkin = { base: straw, lit: M3.lighten(straw, 0.28), dark: pal.ground };
      for (let i = 0; i < 5; i++) {
        const x = 2 + rng.int(TS - 4), y = 3 + rng.int(TS - 5);
        const a0 = rng.int(628) / 100, sway = cyc(SWAY, f + i * 3) * 0.6;
        const rods: any[] = [];
        for (let k = 0; k < 3; k++) {
          const a = a0 + (k - 1) * 0.55 + sway * 0.15, len = 3 + rng.int(3);
          // a stalk lies along the ground and lifts a little at its tip
          rods.push({ kind: 'rod', a: [x, 0.5, 0], b: [x + Math.cos(a) * len, 0.6 + k * 0.3 + sway * 0.3, Math.sin(a) * len * 0.5], r: 0.55, skin: tSkin });
        }
        M3.groundShadow([x, 0, 0], 2.2, 0, y, putT, '#00000022');
        M3.blit(M3.renderScene(TS, TS, rods, 0, y, '#241a24'), putT);
      }
      break;
    }

    case T.NEST: {
      // a bulb of pale sacs on a dark stalk, pulsing: the only thing in a
      // hollow that is obviously alive, so it reads as a thing to attack
      if (!pal.noBase) px(g, 0, 0, TS, TS, pal.floor || pal.ground);
      const pulse = cyc(BOB, f);
      // the sac is a bulb on a stalk, built as solids (LS-199) so the thing
      // that is obviously alive is also obviously round
      const putN2 = (x2: number, y2: number, c: string) => { if (x2 >= 0 && y2 >= 0 && x2 < TS && y2 < TS) px(g, x2, y2, 1, 1, c); };
      M3.groundShadow([TS / 2, 0, 2], 4.6, 0, TS - 1, putN2, '#00000038');
      M3.blit(M3.renderScene(TS, TS, [
        { kind: 'rod', a: [TS / 2, 0.5, 0], b: [TS / 2, 4.5, 0], r: 2, skin: { base: pal.moss || '#4d5f42', lit: M3.lighten(pal.moss || '#4d5f42', 0.2), dark: '#241a24' } },
        { kind: 'ball', at: [TS / 2, 8.5 + pulse, 0], r: 5, rz: 4, ry: 4.4, skin: { base: '#7c5f8c', lit: '#d8b0e8', dark: '#4a3556' } },
        { kind: 'ball', at: [TS / 2 - 1.5, 10 + pulse, 2], r: 1.6, rz: 1.4, ry: 1.4, skin: { base: '#d8b0e8', lit: '#f0dcf8', dark: '#a07cb8' } },
      ], 0, TS - 1, '#241a24'), putN2);
      break;
    }
    case T.STONE: {
      /* A STANDING STONE, CUT AND WRITTEN ON.
       *
       * The art lens' standing complaint is that no screen anywhere holds a
       * LANDMARK — nothing with a silhouette you could navigate by or name.
       * Rocks and trees are scattered mass; this is the first thing in the
       * wilds that is obviously MADE, and it is deliberately taller than it is
       * wide so it breaks the flat skyline a canopy ring gives every screen.
       *
       * The marks are drawn live in `drawTiles`, which knows which charm this
       * barrier answers to; what is baked here is the stone they are cut into.
       */
      if (!pal.noBase) px(g, 0, 0, TS, TS, pal.ground || pal.floor);   // bare on the turf in the wild (LS-218)
      /* Variant 0 is the HEAD (there is more stone below it), variant 1 the
       * FOOT. `drawTiles` picks by looking at the tile beneath, the same way
       * a shoreline picks its rim.
       *
       * The face is deliberately the palest stone in the palette: the marks
       * are cut INTO it and are therefore shadow, and dark-on-pale is the
       * only pairing that reads at seven pixels. Drawn the other way round —
       * a light mark on mid stone — it was reported from play as unclear.
       */
      if (variant === 1) {
        /* THE FOOT IS PLANTED IN SOMETHING (LS-199). The base was two flat
         * bars and, seen in the world beside rocks that now sit in their own
         * shadows, the stone read as a pale panel LYING on the dirt rather
         * than standing in it -- it was the shape a player was pointing at
         * when they said the scattered blocks broke their depth. The rubble
         * it was set into is built as solids through the one camera, with a
         * shadow pooled on the near side; the FACE above is untouched,
         * because the marks are cut into it and are aligned to its pixels. */
        const putS = (x2: number, y2: number, c: string) => { if (x2 >= 0 && y2 >= 0 && x2 < TS && y2 < TS) px(g, x2, y2, 1, 1, c); };
        const sSkin = { base: pal.rock2 || '#4d4660', lit: pal.rock || '#6d6480', dark: '#241a24' };
        M3.groundShadow([TS / 2, 0, 3], 6.5, 0, TS - 1, putS, '#00000038');
        M3.blit(M3.renderScene(TS, TS, [
          { kind: 'ball', at: [TS / 2 - 3, 1.4, 1], r: 3.6, rz: 2.6, ry: 2, skin: sSkin },
          { kind: 'ball', at: [TS / 2 + 3.4, 1.2, 1.6], r: 3.2, rz: 2.4, ry: 1.8, skin: sSkin },
          { kind: 'ball', at: [TS / 2, 1.6, 2.4], r: 4.4, rz: 3, ry: 2.2, skin: sSkin },
        ], 0, TS - 1, '#241a24'), putS);
        px(g, 3, 0, 11, 13, '#120f1c');
        px(g, 4, 0, 9, 12, pal.rock2 || '#4d4660');
        px(g, 4, 0, 8, 12, pal.stoneFace || '#b8a884');
        px(g, 4, 0, 1, 12, pal.rock || '#6d6480');
      } else {
        // the head: a broken top corner, so the silhouette is not a box
        px(g, 3, 2, 11, 14, '#120f1c');
        px(g, 4, 3, 9, 13, pal.rock2 || '#4d4660');
        px(g, 4, 3, 8, 13, pal.stoneFace || '#b8a884');
        px(g, 4, 3, 1, 13, pal.rock || '#6d6480');
        px(g, 11, 3, 3, 2, pal.ground || pal.floor);
        px(g, 10, 2, 2, 2, pal.ground || pal.floor);
      }
      break;
    }
    case T.THORN: {
      // variant 1: rooted in deep water. The stalks stop short of the bottom
      // of the tile and a waterline runs under them, because there is nothing
      // down there for them to grip — which is exactly the reason a Silt Lung
      // holder can go beneath this one and not the hedge on dry ground.
      const wet = (variant & 1) === 1;   // bit 0: rooted in water; the higher bits pick one of four pictures (the art lens, round 47)
      const base = wet ? (pal.deep || '#1d3b52') : (pal.floor || pal.ground);
      const stalk = pal.moss || pal.tree || '#4f6b3a';
      const barb = pal.accent || pal.tuft || '#7fa050';
      /* A THICKET IN THE WATER STANDS IN THE WATER THE GAME PAINTS (LS-375,
       * 2026-09-10). This filled the tile with flat `pal.deep` and ruled a
       * dark line across it, and the water around it is shelved, waved and
       * lit -- so at 4x the wet variant read as a blue-grey CRATE floating on
       * the lake, which is how it was reported. Nothing was wrong with the
       * stalks. The water painter paints the water, the stalks stand in it,
       * and the tile has no edge of its own any more. */
      /* In the wild the ground behind is already painted and the turf grows
       * over it, so the thicket paints no base at all and the meadow, the
       * shore or the water shows between the stems. In a room, where nothing
       * is drawn under it, it still needs one. */
      if (!pal.noBase) px(g, 0, 0, TS, TS, base);
      const lean = cyc(SWAY, f);
      /* AN OPEN SIDE IS PULLED IN (LS-394; Vijay: "art consistency"): a hedge on the beach stood edge to edge in its
       * cell, a square of briars beside the sea. Where no bramble stands beside it the stalks and briars keep two or
       * three pixels off that edge, so the hedge ends in a silhouette and the ground shows round it. */
      const openN = !!(variant & 16), openE = !!(variant & 32), openS = !!(variant & 64), openW = !!(variant & 128);
      const foot = wet ? 5 : (openS ? 2 : 0);       // how much of the tile is water, or bare ground under an open south side
      /* THE STALKS ARE ROUND (LS-199): a hedge of flat 3px bars reads as a
       * picket fence, and this is the thing the game asks you to burn rather
       * than climb. Rods through the one camera, leaning with the wind, with
       * the barbs picked out on their lit side. */
      const putTh = (x2: number, y2: number, c: string) => { if (x2 >= 0 && y2 >= 0 && x2 < TS && y2 < TS) px(g, x2, y2, 1, 1, c); };
      /* THE OUTLINE IS THE PLANT'S OWN DARK, NOT INK (LS-375, 2026-09-10).
       * `renderScene`'s `ink` puts a one-pixel edge round every solid, and
       * thirteen stems two pixels wide each carrying that edge on both sides
       * covers the tile: rendered at 8x the thicket was a black field with
       * green palings in it, and in the game at 4x it read as a wooden crate
       * or a pallet -- reported as one. Nothing was wrong with the tangle; the
       * ink was drawing a grid over it. An outline in a dark green reads as
       * the shade between stems, and the ground shows through the gaps, which
       * is what tells a thicket from a thing. */
      const thShade = M3.darken(stalk, 0.62);
      const thSkin = { base: stalk, lit: M3.lighten(stalk, 0.26), dark: thShade };
      /* A BRAMBLE IS A BUSH (LS-394, 2026-09-12; Vijay: "those green assets along the water in particular look
       * horrible"). Thirteen stems in a cell read as reeds standing in the surf. Now it is a mass of lumps with a
       * one-pixel edge -- one silhouette against the ground -- thorn canes poking out of it and light tips on the
       * crown; it joins the bramble beside it on a closed side and keeps off an open edge, so a clump is one bush
       * and a lone one has ground all round. */
      const skin = { base: M3.darken(stalk, 0.84), lit: M3.lighten(stalk, 0.16), dark: thShade };
      const cx = TS / 2 + (openW ? 1.2 : -1.5) + (openE ? -1.2 : 1.5);
      const cz = (openN ? 1.2 : -1.5) + (openS ? -1.2 : 1.5);
      const big = (wet ? 3.0 : 3.5) + rng.int(3) * 0.35;
      const mass: any[] = [{ kind: 'ball', at: [cx, big * 0.7, cz], r: big, rz: big * 0.8, skin }];
      mass.push({ kind: 'ball', at: [cx - 2.4 + rng.int(3), big * 0.45, cz + 1.2], r: big * 0.7, rz: big * 0.6, skin });
      mass.push({ kind: 'ball', at: [cx + 1.8 - rng.int(3), big * 0.9, cz - 0.8], r: big * 0.6, rz: big * 0.55, skin });
      if (!openW) mass.push({ kind: 'ball', at: [cx - 6, big * 0.55, cz], r: big * 0.8, rz: big * 0.7, skin });
      if (!openE) mass.push({ kind: 'ball', at: [cx + 6, big * 0.55, cz], r: big * 0.8, rz: big * 0.7, skin });
      if (!openN) mass.push({ kind: 'ball', at: [cx, big * 0.55, cz - 6], r: big * 0.8, rz: big * 0.7, skin });
      if (!openS) mass.push({ kind: 'ball', at: [cx, big * 0.55, cz + 6], r: big * 0.8, rz: big * 0.7, skin });
      // thorn canes out of the mass, each its own way
      const canes = 3 + rng.int(3);
      for (let i = 0; i < canes; i++) {
        const ang = rng.int(628) / 100, len = 2.5 + rng.int(3);
        const ax = cx + Math.cos(ang) * big * 0.5, az = cz + Math.sin(ang) * big * 0.4, ay = big * 0.5 + rng.int(3);
        mass.push({ kind: 'rod', a: [ax, ay, az], b: [ax + Math.cos(ang) * len + (lean || 0) * 0.5, ay + 1.5 + rng.int(3), az + Math.sin(ang) * len], r: 0.55, skin: thSkin });
      }
      const sheet = M3.renderScene(TS, TS, mass, 0, TS - foot, thShade);
      // the shadow it sits in, under everything it drew: a thing on the ground, not pasted on it
      const shade: any = { w: TS, h: TS, px: new Array(TS * TS).fill(null) };
      // ...only where the bush ends: inside a clump every cell's shadow drew a grid of dark corners (LS-395)
      if ((globalThis as any).__noBushShadow ? false : (openS || openW)) M3.groundShadow([cx, 0, cz + 2.5], big * 0.9, 0, TS - foot, (x2: number, y2: number, c: string) => { if (x2 >= 0 && y2 >= 0 && x2 < TS && y2 < TS && (openS || y2 < TS - 3) && (openW || x2 > 2)) shade.px[y2 * TS + x2] = c; }, '#00000030');
      for (let i = 0; i < sheet.px.length; i++) if (!sheet.px[i] && shade.px[i]) sheet.px[i] = shade.px[i];
      M3.blit(sheet, putTh);
      // light tips on the crown, never in a row
      for (let i = 0; i < 4 + rng.int(3); i++) {
        const x2 = Math.round(cx - big + rng.int(Math.round(big * 2) + 1)), y2 = TS - foot - Math.round(big * 1.2) - rng.int(4);
        if (x2 >= 0 && y2 >= 0 && x2 < TS && y2 < TS && sheet.px[y2 * TS + x2]) px(g, x2, y2, 1, 1, barb);
      }
      const xs = [Math.round(cx - 2), Math.round(cx + 2)];
      if (wet) {
        /* WHERE THE STEMS ENTER THE WATER, and nothing more: the full-width
         * dark line and the second fill under it were the crate's lid and
         * floor (LS-375). Broken segments at the foot of each stem, and one
         * ripple that moves with the sway. */
        for (const x0 of xs) {
          px(g, Math.round(x0) - 1 + lean, TS - foot, 3, 1, M3.darken(stalk, 0.55));
          px(g, Math.round(x0) + lean, TS - foot + 1, 1, 1, '#8fc4cf');
        }
        const ripple = cyc(SWAY, f);
        px(g, 3 + ripple, TS - foot + 3, 3, 1, '#8fc4cf');
      }
      break;
    }

    default: px(g, 0, 0, TS, TS, pal.ground || pal.floor || '#5a4a3a');   // a kind with no painter is ground, not magenta
  }
  if (EDGED.has(kind)) cornerBridge(g, (variant >> 12) & 15, kind, pal);
}

/** GIVE A SLANTING RUN A WAIST.
 *
 * Bits 12-15 of the variant say which of this tile's four corners touch a tile
 * of the SAME stuff diagonally with land on both shared sides -- see the note
 * in `drawOneTile` that builds them. Left alone, two such tiles meet at a
 * single point and a stream running slantwise across the grid reads as a row
 * of separate beads. Reported from play, looking at the whole island: "those
 * diagonal wet tiles don't look good ... there should be thicker diagonals".
 *
 * Each of the pair fills the corner it shares, so together they make a
 * six-pixel waist across the join instead of a one-pixel pinch. It is drawn as
 * a STEPPED WEDGE rather than a square block: a square would put a hard
 * vertical and a hard horizontal edge into the middle of a diagonal, which is
 * the same fault one step smaller. The wedge's own steps are the 45 degrees
 * the run is already travelling at.
 *
 * THE COLOUR IS NAMED, NOT READ BACK. The first cut sampled the centre of the
 * tile that had just been painted, which serves all five kinds without knowing
 * anything about them -- and `getImageData` is a synchronous readback of the
 * canvas. `test/soak.ts` paints every kind in every palette at every frame, and
 * it took the suite from under three minutes to over fifteen. The body of each
 * of these five is a palette entry; asking for it costs nothing. */
const BODY_KEY = {
  [T.WATER]: 'deep', [T.SEA]: 'deep', [T.SHALLOW]: 'shallow',
  [T.MOSS]: 'shade', [T.SAND]: 'sand',
};
function cornerBridge(g, bits, kind, pal) {
  if (!bits) return;
  const col = pal[BODY_KEY[kind]];
  if (!col) return;
  const W = 6;
  for (let i = 0; i < W; i++) {
    const run = W - i;                       // the step: widest at the corner
    if (bits & 1) px(g, TS - run, i, run, 1, col);              // NE
    if (bits & 2) px(g, TS - run, TS - 1 - i, run, 1, col);     // SE
    if (bits & 4) px(g, 0, TS - 1 - i, run, 1, col);            // SW
    if (bits & 8) px(g, 0, i, run, 1, col);                     // NW
  }
}

// ------------------------------------------------------------------- font
// A 5x7 bitmap face, generated like everything else. Canvas fillText at 8px
// antialiases, then the whole backbuffer is upscaled with `pixelated`, so
// every glyph arrives as a grey smear next to razor-sharp tiles — and a
// fractional advance gives the same letter a different fringe in every
// position. Blitting our own glyphs at integer coordinates fixes both, and
// removes the dependency on a system font we cannot guarantee.

/** The face itself. Exported so a check can ask what it can and cannot write: `drawText` falls back to `'?'` for
 *  any character absent from it, silently, which has shipped an em dash and an ellipsis as question marks. */
export const FONT = {
  // --- LOWERCASE. The face was 26 capitals and nothing else, so every word in
  // the game was shouted. A lowercase set is quieter, and quiet is the voice
  // this game wants — it is a forager in a wood, not a scoreboard.
  //
  // Five wide, seven tall, x-height on rows 2-6 so lowercase shares the
  // capitals' baseline. Descenders are drawn as short tails inside the cell:
  // there is no row 7 to hang them from, and a false baseline is worse than a
  // tucked tail.
  a: ['.....', '.....', '.###.', '....#', '.####', '#...#', '.####', '.....'],
  b: ['#....', '#....', '####.', '#...#', '#...#', '#...#', '####.', '.....'],
  c: ['.....', '.....', '.###.', '#...#', '#....', '#...#', '.###.', '.....'],
  d: ['....#', '....#', '.####', '#...#', '#...#', '#...#', '.####', '.....'],
  e: ['.....', '.....', '.###.', '#...#', '#####', '#....', '.###.', '.....'],
  f: ['..##.', '.#...', '####.', '.#...', '.#...', '.#...', '.#...', '.....'],
  g: ['.....', '.....', '.####', '#...#', '#...#', '.####', '....#', '.###.'],
  h: ['#....', '#....', '####.', '#...#', '#...#', '#...#', '#...#', '.....'],
  i: ['..#..', '.....', '.##..', '..#..', '..#..', '..#..', '.###.', '.....'],
  j: ['...#.', '.....', '...#.', '...#.', '...#.', '...#.', '#..#.', '.##..'],
  k: ['#....', '#....', '#..#.', '#.#..', '##...', '#.#..', '#..#.', '.....'],
  l: ['.##..', '..#..', '..#..', '..#..', '..#..', '..#..', '.###.', '.....'],
  m: ['.....', '.....', '##.#.', '#.#.#', '#.#.#', '#.#.#', '#.#.#', '.....'],
  n: ['.....', '.....', '####.', '#...#', '#...#', '#...#', '#...#', '.....'],
  o: ['.....', '.....', '.###.', '#...#', '#...#', '#...#', '.###.', '.....'],
  p: ['.....', '.....', '####.', '#...#', '#...#', '####.', '#....', '#....'],
  q: ['.....', '.....', '.####', '#...#', '#...#', '.####', '....#', '....#'],
  r: ['.....', '.....', '#.##.', '##..#', '#....', '#....', '#....', '.....'],
  s: ['.....', '.....', '.####', '#....', '.###.', '....#', '####.', '.....'],
  t: ['.#...', '.#...', '####.', '.#...', '.#...', '.#..#', '..##.', '.....'],
  u: ['.....', '.....', '#...#', '#...#', '#...#', '#...#', '.####', '.....'],
  v: ['.....', '.....', '#...#', '#...#', '#...#', '.#.#.', '..#..', '.....'],
  w: ['.....', '.....', '#...#', '#...#', '#.#.#', '#.#.#', '.#.#.', '.....'],
  y: ['.....', '.....', '#...#', '#...#', '#...#', '.####', '....#', '.###.'],
  z: ['.....', '.....', '#####', '...#.', '..#..', '.#...', '#####', '.....'],
  A: ['.###.', '#...#', '#...#', '#####', '#...#', '#...#', '#...#', '.....'],
  B: ['####.', '#...#', '#...#', '####.', '#...#', '#...#', '####.', '.....'],
  C: ['.###.', '#...#', '#....', '#....', '#....', '#...#', '.###.', '.....'],
  D: ['####.', '#...#', '#...#', '#...#', '#...#', '#...#', '####.', '.....'],
  E: ['#####', '#....', '#....', '####.', '#....', '#....', '#####', '.....'],
  F: ['#####', '#....', '#....', '####.', '#....', '#....', '#....', '.....'],
  G: ['.###.', '#...#', '#....', '#.###', '#...#', '#...#', '.###.', '.....'],
  H: ['#...#', '#...#', '#...#', '#####', '#...#', '#...#', '#...#', '.....'],
  I: ['#####', '..#..', '..#..', '..#..', '..#..', '..#..', '#####', '.....'],
  J: ['....#', '....#', '....#', '....#', '#...#', '#...#', '.###.', '.....'],
  K: ['#...#', '#..#.', '#.#..', '##...', '#.#..', '#..#.', '#...#', '.....'],
  L: ['#....', '#....', '#....', '#....', '#....', '#....', '#####', '.....'],
  M: ['#...#', '##.##', '#.#.#', '#...#', '#...#', '#...#', '#...#', '.....'],
  N: ['#...#', '##..#', '#.#.#', '#..##', '#...#', '#...#', '#...#', '.....'],
  O: ['.###.', '#...#', '#...#', '#...#', '#...#', '#...#', '.###.', '.....'],
  P: ['####.', '#...#', '#...#', '####.', '#....', '#....', '#....', '.....'],
  Q: ['.###.', '#...#', '#...#', '#...#', '#.#.#', '#..#.', '.##.#', '.....'],
  R: ['####.', '#...#', '#...#', '####.', '#.#..', '#..#.', '#...#', '.....'],
  S: ['.####', '#....', '#....', '.###.', '....#', '....#', '####.', '.....'],
  T: ['#####', '..#..', '..#..', '..#..', '..#..', '..#..', '..#..', '.....'],
  U: ['#...#', '#...#', '#...#', '#...#', '#...#', '#...#', '.###.', '.....'],
  V: ['#...#', '#...#', '#...#', '#...#', '#...#', '.#.#.', '..#..', '.....'],
  W: ['#...#', '#...#', '#...#', '#...#', '#.#.#', '##.##', '#...#', '.....'],
  X: ['#...#', '#...#', '.#.#.', '..#..', '.#.#.', '#...#', '#...#', '.....'],
  Y: ['#...#', '#...#', '.#.#.', '..#..', '..#..', '..#..', '..#..', '.....'],
  Z: ['#####', '....#', '...#.', '..#..', '.#...', '#....', '#####', '.....'],
  0: ['.###.', '#...#', '#..##', '#.#.#', '##..#', '#...#', '.###.', '.....'],
  1: ['..#..', '.##..', '..#..', '..#..', '..#..', '..#..', '.###.', '.....'],
  2: ['.###.', '#...#', '....#', '...#.', '..#..', '.#...', '#####', '.....'],
  3: ['####.', '....#', '....#', '.###.', '....#', '....#', '####.', '.....'],
  4: ['...#.', '..##.', '.#.#.', '#..#.', '#####', '...#.', '...#.', '.....'],
  5: ['#####', '#....', '####.', '....#', '....#', '#...#', '.###.', '.....'],
  6: ['.###.', '#...#', '#....', '####.', '#...#', '#...#', '.###.', '.....'],
  7: ['#####', '....#', '...#.', '..#..', '.#...', '.#...', '.#...', '.....'],
  8: ['.###.', '#...#', '#...#', '.###.', '#...#', '#...#', '.###.', '.....'],
  9: ['.###.', '#...#', '#...#', '.####', '....#', '#...#', '.###.', '.....'],
  ' ': ['.....', '.....', '.....', '.....', '.....', '.....', '.....', '.....'],
  '-': ['.....', '.....', '.....', '#####', '.....', '.....', '.....', '.....'],
  '.': ['.....', '.....', '.....', '.....', '.....', '.....', '..#..', '.....'],
  /* AN ELLIPSIS, BECAUSE `fitCaption` HAS BEEN WRITING ONE SINCE ROUND 49 (2026-09-06). Any character absent from
   * this table falls through to `FONT['?']`, so every caption the game has ever trimmed -- the weapon's line in the
   * bar, the bag's cursor caption, the win screen's numbers -- ended in a QUESTION MARK where it meant to say `there
   * was more`. Three dots on the baseline, in the width the rest of the face uses. */
  '\u2026': ['.....', '.....', '.....', '.....', '.....', '.....', '#.#.#', '.....'],
  ',': ['.....', '.....', '.....', '.....', '.....', '..#..', '.#...', '.....'],
  ':': ['.....', '..#..', '..#..', '.....', '..#..', '..#..', '.....', '.....'],
  '/': ['....#', '....#', '...#.', '..#..', '.#...', '#....', '#....', '.....'],
  "'": ['..#..', '..#..', '.....', '.....', '.....', '.....', '.....', '.....'],
  '!': ['..#..', '..#..', '..#..', '..#..', '..#..', '.....', '..#..', '.....'],
  // The set stopped at what the game happened to say. A string with a
  // character the face does not know draws nothing and takes no width, so the
  // line silently loses a letter — which is a bug you find by reading, not by
  // running. These close the printable set the HUD can reach.
  ';': ['.....', '..#..', '..#..', '.....', '..#..', '..#..', '.#...', '.....'],
  '<': ['...#.', '..#..', '.#...', '#....', '.#...', '..#..', '...#.', '.....'],
  '>': ['.#...', '..#..', '...#.', '....#', '...#.', '..#..', '.#...', '.....'],
  '&': ['.##..', '#..#.', '#.#..', '.#...', '#.#.#', '#..#.', '.##.#', '.....'],
  '#': ['.#.#.', '#####', '.#.#.', '#####', '.#.#.', '.....', '.....', '.....'],
  '?': ['.###.', '#...#', '....#', '..##.', '..#..', '.....', '..#..', '.....'],
  '+': ['.....', '..#..', '..#..', '#####', '..#..', '..#..', '.....', '.....'],
  '=': ['.....', '.....', '#####', '.....', '#####', '.....', '.....', '.....'],
  '(': ['...#.', '..#..', '.#...', '.#...', '.#...', '..#..', '...#.', '.....'],
  ')': ['.#...', '..#..', '...#.', '...#.', '...#.', '..#..', '.#...', '.....'],
  '~': ['.....', '.....', '.#..#', '#.#.#', '#..#.', '.....', '.....', '.....'],
  '*': ['.....', '.#.#.', '..#..', '#####', '..#..', '.#.#.', '.....', '.....'],
  '%': ['#...#', '#..#.', '...#.', '..#..', '.#...', '.#..#', '#...#', '.....'],
  'x': ['.....', '.....', '#...#', '.#.#.', '..#..', '.#.#.', '#...#', '.....'],
};

/* EIGHT ROWS, BECAUSE FIVE OF THEM CANNOT HOLD A DESCENDER.
 *
 * This was 7: two blank rows then five of x-height, and `g p q y j` were
 * crushed into those five. `o`'s bowl is five rows tall; `g`'s was THREE,
 * with its tail folded up inside the x-height. An art reviewer read the
 * result off the screen: `vigor` in the HUD on every frame the game draws,
 * `map` on a touch button, `sap flask` in the shop, `playground` in the bar.
 *
 * The eighth row sits below the baseline and is blank for every other glyph,
 * so nothing else moves; the five with tails now have a proper bowl and drop
 * their tail into it. `validateFont` enforces the row count, which is why
 * this could not be done by halves.
 */
export const GLYPH_W = 5, GLYPH_H = 8, GLYPH_ADVANCE = 6;

const glyphCache = new Map();

function glyph(ch, colour, scale) {
  const key = ch + '|' + colour + '|' + scale;
  let c = glyphCache.get(key);
  if (c) return c;
  const rows = FONT[ch] || FONT[ch.toUpperCase()] || FONT['?'];
  c = mkCanvas(GLYPH_W * scale, GLYPH_H * scale);
  const g = c.getContext('2d');
  g.fillStyle = colour;
  for (let y = 0; y < GLYPH_H; y++) {
    for (let x = 0; x < GLYPH_W; x++) {
      if (rows[y][x] === '#') g.fillRect(x * scale, y * scale, scale, scale);
    }
  }
  glyphCache.set(key, c);
  return c;
}

export function textWidth(str, scale = 1) {
  return str.length * GLYPH_ADVANCE * scale - scale;
}

export function drawText(g, str, x, y, colour = '#efe7d8', scale = 1) {
  let cx = Math.round(x);
  const cy = Math.round(y);
  for (const ch of String(str)) {
    if (ch !== ' ') g.drawImage(glyph(ch, colour, scale), cx, cy);
    cx += GLYPH_ADVANCE * scale;
  }
  return cx;
}

export function validateFont() {
  for (const [ch, rows] of Object.entries(FONT as Record<string, any>)) {
    if (rows.length !== GLYPH_H) throw new Error('glyph ' + ch + ' rows ' + rows.length);
    rows.forEach((r, i) => {
      if (r.length !== GLYPH_W) throw new Error('glyph ' + ch + ' row ' + i + ' len ' + r.length);
    });
  }
  return true;
}

/* ------------------------------------------------------------------- UI
 *
 * THERE WAS NO PALETTE. 286 distinct hex colours across the source, 209 of
 * them used exactly ONCE — and while most of those are the biome and hollow
 * palettes, which are supposed to be many and distinct, 39 were invented ad
 * hoc in `game.js` and `entities.js`: a HUD plate here, a map marker there, a
 * puff tint that appears in one call and nowhere else in the codebase.
 *
 * These are that set, named. A colour with a name can be reused, matched and
 * changed everywhere at once; a hex literal in a draw call can only be found
 * by grepping for it.
 */
export const UI = {
  // the frame the game sits in
  hud: '#191622', hudLine: '#2b2637', slot: '#241a24',
  /* the world's own inks: the shade and lift of a terrace face, the shadow at a wall's foot, and the fallbacks a
   * palette may lack.
   *
   * A WARM `ink: '#241a24'` STOOD HERE AND WAS NEVER ONCE READ (2026-09-06). The same object declares `ink` again
   * thirty lines down as the UI's near-black `#0e0c12`, and in an object literal the later property wins -- so every
   * `UI.ink` in the game, including the ledge line this one was written for, has always been the cold one. The
   * duplicate is gone rather than renamed: nothing has ever depended on the warm value, so keeping it would only
   * offer a second `ink` to reach for by mistake. `tsc` had been reporting it as TS1117 the whole time. */
  shadeTone: '#232a3c', liftTone: '#fff2cf', footShadow: '#0c0a12',
  groundFallback: '#7fae69', stoneFaceFallback: '#efe9dc',
  slotEmpty: '#241f2e', slotFill: '#332c40', gauge: '#2f3a4a',
  // things that are yours
  pods: '#a9d16a', podsDim: '#4d6630', tide: '#8fc4cf', tideDim: '#3d6470',
  ember: '#ffb85c', emberDim: '#7a5526', emberBar: '#2f2a1a',
  // the map
  mapBack: '#12141a', mapGrid: '#858c99', mapEdge: '#626873',
  here: '#efe7d8',
  // water, seen from inside and above
  underDeep: '#6fa8b8', underPale: '#8fc4cf', foamLit: '#cfeef6',
  // warm and cold accents nothing else owns
  warm: '#e8b76a', hot: '#e8a63a', bloom: '#dd8fa6', bloomPale: '#f6d8e2',
  cold: '#8fa0a8', deep: '#16202a', deeper: '#101a20', tidepool: '#1b2c30',
  dusk: '#a89fbc', stone: '#b8a884', slate: '#5a6470', teal: '#3f8f96',
  parchment: '#a49a88',
  /* 3.82:1 ON THE SHOP FLOOR. This is the colour a price you cannot afford is
     drawn in — and a price you cannot afford is still a price you have to
     read, because it is how you learn what to save for. The HUD's readouts
     were raised over the 4.5:1 floor a round earlier and this one was missed:
     it lives in the cave-dressing draw, not the HUD. 4.60:1 now, and still
     visibly the dimmer of the two states against the gold you can afford. */
  parchmentDim: '#998d78',
  // shadows, in the two weights the game uses
  shadow: '#241a2460', shadowHard: '#241a24',

  // ...and the rest of what the draw calls had been inventing. Forty-four
  // further literals were shared between `game.js` and `entities.js` with no
  // name anywhere — a spore purple written out four times, a warning red five,
  // the parchment of the title screen twice. Naming them is what makes it
  // possible to say the game has a palette at all.
  ink: '#0e0c12', inkSoft: '#2c2a36', paper: '#fdf6e6', paperDim: '#efe7d8',
  muted: '#6e6878',
  // 7f7a8c reads 3.98:1 on the title's ground, under the 4.5:1 floor, and
  // this is the colour of the line that reports your records. It is the
  // dim member of a pair — `muted` is the dimmer one and stays where it is,
  // for fills rather than text.
  mutedLit: '#8f8a9e', violet: '#4c4560', violetLit: '#514764',
  spore: '#d8b0e8', sporeDim: '#7c5f8c', sporeLit: '#e6dcff', wisp: '#a795d8',
  wispDim: '#8f7fc4', bone: '#e2e8ec', steel: '#96a2ac', sky: '#cfe8ff',
  glass: '#8fd6ff', gold: '#f2c14e', goldDim: '#b8862f', goldLit: '#ffe9a8',
  goldPale: '#fff3c8', flame: '#ff8c3c', flameDeep: '#c85820', rust: '#c8813c',
  // 4.12:1 ON THE HUD, under the 4.5:1 floor — and this is the colour of the
  // one line that tells a player their save has been set aside. A warning
  // nobody can read is not a warning. Lightened just past the floor so it is
  // still unmistakably the alarm colour: 4.61:1.
  danger: '#d25e48', leaf: '#8fd06a', leafDim: '#3f6b34', leafLit: '#6f8f4a',
  bark: '#6b432a', barkLit: '#8a5c38', barkPale: '#8a6b3a', linen: '#cfc0a0',
  linenDim: '#8a7f68', brass: '#6b5a2a', shade: '#3a2b33', mint: '#7fd6cf',
  seaDim: '#3d7f96',
  // the map's own greys, which exist only there
  mapMoss: '#3f4a38', mapAsh: '#4a2b2b', mapDust: '#4a3f22', mapFen: '#3a3352',
  mapScree: '#2f2c3c', mapHeath: '#38344a', mapBloom: '#4a5a48', mapSand: '#5c5238',
};

// ---------------------------------------------------------------- sprites

const P = {
  o: '#241a24', c: '#dc9548'   /* the tunic, two steps up (LS-234): the forager sat at the ground's own value and read only by his outline */, C: '#9c5e2a', k: '#2fa8a0', K: '#1d7a74',
  s: '#e6b78a', b: '#6b432a', m: '#e2e8ec', M: '#96a2ac', w: '#f6f2e8',
  d: '#3a2b33', e: '#ffe9a8', y: '#f2c14e', Y: '#bd8a2c',
  g: '#79a35c', G: '#4a6f43',
  n: '#b9b2a4', N: '#6f6a5e', v: '#9a7bd8', V: '#5f4a96',
  // the spore purple: the Broodmother's sac and the sporelings she plants.
  // This pair used to be declared twice — an unused pink first, then this —
  // and the second silently won, which is a colour decided by line order.
  p: '#d8b0e8', P: '#7c5f8c',
  t: '#7fd6cf', r: '#c9553f', R: '#8d3529', u: '#cfc0a0',
};

/** The sprite palette. Exported so a check can ask what colours a BODY is
 *  really painted in, instead of being handed a hand-kept list that drifts
 *  the first time somebody repaints the hero. */
export const PALETTE = P;


// Palette swaps let one matrix serve three differently-coloured wardens.
export const ALT = {
  w1: { n: '#c0a3d8', N: '#7a5f96', e: '#e8d0ff' },
  w2: { n: '#d8bb85', N: '#96733f', e: '#ffe9a8' },
  w3: { n: '#96c2cf', N: '#4f7f8e', e: '#d6f6ff' },
  // ...and the same Warden with its guard changed. A lit core, the eyes gone
  // white, and the stone brought up hot: a standing signal for a state that
  // previously announced itself once and then looked exactly as it had before.
  //
  // The rage used to be one orange-brown for all three (#d8a08c / #e0a074 /
  // #d09a8c, within 12 degrees of hue of each other), which threw away the
  // one thing that told the three Wardens apart. The heat is carried by the
  // shared lit core and the white eyes; each Warden keeps its own hue and
  // simply burns brighter in it.
  w1r: { n: '#e2a8f4', N: '#8a4fb4', e: '#ffffff', y: '#ff8c3c' },
  w2r: { n: '#f4cf86', N: '#b4842c', e: '#ffffff', y: '#ff8c3c' },
  w3r: { n: '#a6e6f4', N: '#3f96b4', e: '#ffffff', y: '#ff8c3c' },
};

/** The sprite sheet, as row masks. Exported so a check can measure it. */
/** Every baked body in the game, as rows of characters — one key per sprite
 *  and frame (`forager_down_0`, `warden32_1`). Typed as a record rather than
 *  left as `{}`, because the whole point of the table is that a name maps to
 *  a matrix, and `{}` says the opposite. */
import { SPRITES, FRAMES } from './sprites/index.ts';
export { FRAMES };

export const S: Record<string, string[]> = {};

// --- forager: hooded, ochre cloak, teal scarf
S.forager_down_0 = [
  '................',
  '......oooo......',
  '.....oCCCCo.....',
  '...oCccccccCo...',
  '...oCcsssscCo...',
  '...oCsossosCo...',
  '...oCcsssscCo...',
  '..oCkkkkkkkkCo..',
  '..oCccccccccCo..',
  '..oCccccccccCoo.',
  '..oCcbbbbbbcCoMo',
  '..oCccccccccCoMo',
  '..oCccccccccCoo.',
  '..ooccccccccoo..',
  '...obbo..obbo...',
  '...bbb....bbb...',
];
S.forager_up_0 = [
  '................',
  '......oooo......',
  '.....oCCCCo.....',
  '...oCccccccCo...',
  '...oCcCCCCcCo...',
  '...oCcCCCCcCo...',
  '...oCccCCccCo...',
  '..oCkkkkkkkkCo..',
  '..oCccccccccCo..',
  '.ooCccccccccCo..',
  'oMoCcbbbbbbcCo..',
  'oMoCccccccccCo..',
  '..oCccccccccCo..',
  '..ooccccccccoo..',
  '...obbo..obbo...',
  '...bbb....bbb...',
];
S.forager_side_0 = [
  '................',
  '.....oooo.......',
  '....oCCCCo......',
  '..oCcccccccCo...',
  '..oCcccsssscc...',
  '..oCccsossscc...',
  '..oCcccsssscc...',
  '..oCkkkkkkkCo...',
  '..oCcccccccCo...',
  '..oCcccccccCoo..',
  '..oCcbbbbbcCoMo.',
  '..oCcccccccCoMo.',
  '..oCcccccccCo...',
  '..oocccccccco...',
  '...obbo.obbo....',
  '...bbb...bbb....',
];

/* A MUSHROOM, so the cap reads before the colour does. Silhouette overlap
 * with the bramble measured 0.80 IoU: two rounded blobs of the same size. A
 * wide cap on a narrow stalk is a shape nothing else here makes. */
/* TALL AND NARROW — the only thing in the bestiary that is.
 *
 * It was a 15x12 cap sitting on a stub, aspect 1.25, and it measured 0.62 IoU
 * against the bramble and 0.58 against the wisp: a wide blob among wide blobs.
 * A mushroom's whole shape is a cap ON A STEM, so the stem now runs the full
 * height of the cell and the cap narrows to sit on it. 12 wide, 15 tall,
 * aspect 0.80 — nothing else here is taller than it is wide. */
S.sporeling_0 = [
  '................',
  '....oooooooo....',
  '..oopppppppppo..',
  '.oppppppppppppo.',
  '.oppPpppppPppo..',
  '.oppppppppppppo.',
  '..oPPPPPPPPPPo..',
  '...ooouuuuooo...',
  '.....ouuuuo.....',
  '.....uodduo.....',
  '.....ouuuuo.....',
  '......ouuo......',
  '......ouuo......',
  '......ouuo......',
  '.....oouuoo.....',
  '.....oooooo.....',
];

// --- bramble: thorny four-legged chaser
/* THORNS, so the outline is spiked rather than rounded. It measured 0.80 IoU
 * against the sporeling and 0.76 against the charger — a rounded blob among
 * rounded blobs. Spikes at the shoulders and a ragged skirt give it an edge
 * nothing else in the bestiary has. */
S.bramble_0 = [
  '..o..o....o..o..',
  '..oGo.o..o.oGo..',
  '.oGGGoGooGoGGGo.',
  'oGGGGGGGGGGGGGGo',
  '.oGGgGGGGGGgGGo.',
  '..oGGgeGGegGGo..',
  '.oGGGGGGGGGGGGo.',
  'oGGGgggggggGGGGo',
  '.oGGGGGGGGGGGGo.',
  '..oGgGGGGGGgGo..',
  '.oGGGGGGGGGGGGo.',
  'o.oGGGGGGGGGGo.o',
  '.o..oGGGGGGo..o.',
  '..o.o.o..o.o.o..',
  '................',
  '................',
];

// --- wisp: drifting light with trailing veils
S.wisp_0 = [
  '................',
  '................',
  '......oooo......',
  '.....ovvvvo.....',
  '....ovvvvvvo....',
  '...ovvtttvvvo...',
  '...ovvtwwtvvo...',
  '...ovvtttvvvo...',
  '....ovvvvvvo....',
  '.....ovvvvo.....',
  '..o...ovvo...o..',
  '.oVo..oVVo..oVo.',
  '..o....oo....o..',
  '................',
  '................',
  '................',
];
S.wisp_1 = [
  '................',
  '................',
  '................',
  '......oooo......',
  '.....ovvvvo.....',
  '....ovvttvvo....',
  '....ovtwwtvo....',
  '....ovvttvvo....',
  '.....ovvvvo.....',
  '...o..ovvo..o...',
  '..oVo..oo..oVo..',
  '...o.......o....',
  '................',
  '................',
  '................',
  '................',
];

// --- shellback: armoured, its plate blocks hits from the front
/* IT HUGS THE GROUND, and that is the whole point of it.
 *
 * Measured against the rest of the bestiary it was 0.69 IoU with the bramble
 * and 0.68 with the charger — a near-square 14x13 among near-square blobs,
 * aspect 1.08 where sporeling is 1.25, wisp 1.27, bramble 1.14. Six of the
 * ten pairs in the bestiary shared a shape.
 *
 * The axis nothing was using is WHERE IN THE CELL a body sits. The wisp
 * floats near the top; the sporeling stands full height. So the shellback
 * takes the floor: sixteen wide, seven tall, nothing at all above the
 * mid-line, and a hard flat base with no legs under it. At a glance in
 * greyscale it is a slab, and the only slab. */
S.shellback_0 = [
  '................',
  '................',
  '................',
  '................',
  '................',
  '................',
  '....oooooooo....',
  '..oonnnnnnnnoo..',
  '.onNNNNNNNNNNno.',
  'onNnnnnnnnnnnNno',
  'onNnNNNNNNNNnNno',
  'onNnnnnnnnnnnNno',
  'onNNNNNNNNNNNNno',
  'oonneennnneennoo',
  '.oooooooooooooo.',
  '................',
];

// --- charger: lowered horns, runs in straight lines
/* LONG AND LOW — a thing built to run at you. It was a rounded box like
 * everything else: 0.76 IoU against the bramble, 0.75 against the shellback.
 * Wider than the cell is tall, with a snout in front and nothing above the
 * shoulder line. */
/* DAYLIGHT UNDER IT — a thing on legs, not a slab.
 *
 * Long and low was right and not enough: against a shellback that now takes
 * the floor it still measured 0.55 IoU, and 0.59 against the bramble, because
 * both are solid rectangles of body. What separates a runner from a shell is
 * that you can see the ground THROUGH it. The belly lifts, two legs carry it
 * with a gap between them, and the head drops below the shoulder line in
 * front — so the outline is a wedge with holes in it rather than a brick. */
S.charger_0 = [
  '................',
  '................',
  '................',
  '................',
  '.....ooooooo....',
  '...ooRRRRRRRuoo.',
  '..oRRRrrrrrRRRRo',
  '.oRRRRRRRRRRRerR',
  'ooRRRRrrrrrRRRRo',
  'oRRRRRRRRRRRRuo.',
  'ooRRRoooooRRRo..',
  '.oRRo.....oRRo..',
  '.oRRo.....oRRo..',
  '.oooo.....oooo..',
  '................',
  '................',
];


// Second frames shift the whole body down one row and change the feet. Only
// swapping the bottom two rows left the torso pixel-identical, which reads as
// feet twitching under a stationary box rather than as walking.
const BLANK = '................';
/* A SECOND FRAME HAS TO BE A SECOND POSE.
 *
 * This returned frame 0 shifted down one row with new feet, and an examiner
 * measured what that is worth: 6 to 10 pixels of 256 differ — 97% identical.
 * Five of the eight bodies in the game animated that way, including the
 * player, so a walk cycle was a 1px bob and nothing else. (The shellback was
 * measured at 100% identical and given an authored second frame; the note
 * above it states the finding and fixes one of the six.)
 *
 * The cure is the oldest one in animation: SQUASH. A body carrying its weight
 * onto one foot is momentarily shorter and wider, so the shifted rows also
 * spread outward by a pixel through the middle of the body — where a torso
 * actually gives — while the head and the feet keep their width. That is a
 * real second pose, it costs no authored art, and it applies the same way to
 * everything that walks.
 */
function bobbed(rows, feet) {
  const shifted = rows.slice(0, 15 - feet.length);
  // the band that gives: the middle of what is left, never the head or feet
  const lo = Math.floor(shifted.length * 0.42), hi = Math.floor(shifted.length * 0.78);
  const edges = (row) => {
    let first = -1, last = -1;
    for (let x = 0; x < row.length; x++) {
      if (row[x] !== '.' && row[x] !== ' ') { if (first < 0) first = x; last = x; }
    }
    return [first, last];
  };
  const out = shifted.map((row, i) => {
    const [first, last] = edges(row);
    if (first < 0) return row;
    const cells = row.split('');
    if (i >= lo && i <= hi) {
      // The middle gives outward — and where there is nothing to widen INTO,
      // it gives inward instead. A body drawn to the edge of its cell (the
      // charger is 16 wide) had no room to spread, so its second frame fell
      // back to a bare one-row nudge: 10 pixels of 256, the exact fault the
      // squash exists to fix, reappearing the moment a sprite got wider.
      if (first < 1 || last > cells.length - 2) {
        if (last - first < 3) return row;
        cells[first] = '.';
        cells[last] = '.';
        return cells.join('');
      }
      cells[first - 1] = row[first];
      cells[last + 1] = row[last];
      return cells.join('');
    }
    // ...AND WHAT GOES OUT SOMEWHERE COMES IN SOMEWHERE ELSE. The two rows
    // above the squash are the shoulders, and they draw in as the body
    // compresses. Widening alone is a body inflating; widening against a
    // narrowing is a body taking its weight.
    if (i === lo - 1 || i === lo - 2) {
      if (last - first < 3) return row;
      cells[first] = '.';
      cells[last] = '.';
      return cells.join('');
    }
    return row;
  });
  return [BLANK].concat(out, feet);
}
S.forager_down_1 = bobbed(S.forager_down_0, ['..bbb......bbb..']);
S.forager_up_1 = bobbed(S.forager_up_0, ['..bbb......bbb..']);
S.forager_side_1 = bobbed(S.forager_side_0, ['..bbb...bbb.....']);
S.sporeling_1 = bobbed(S.sporeling_0, ['....o.o..o.o....', BLANK]);
S.bramble_1 = bobbed(S.bramble_0, ['..o.o......o.o..', BLANK]);
// An armoured thing does not hop. Measured, the bobbed version was 100%
// pixel-identical to frame 0 shifted one row — no authored second frame at
// all. This one keeps the shell still and moves the ridge and the legs, which
// is what a crawl looks like.
/* The second frame is the shell SETTLING — it draws down a row and spreads,
 * the way a low armoured thing shifts its weight without lifting anything. */
S.shellback_1 = [
  '................',
  '................',
  '................',
  '................',
  '................',
  '................',
  '................',
  '...oooooooooo...',
  '..oonnnnnnnnoo..',
  '.onNNNNNNNNNNno.',
  'onNnnnnnnnnnnNno',
  'onNnNNNNNNNNnNno',
  'onNNNNNNNNNNNNno',
  'oonneennnneennoo',
  'oooooooooooooooo',
  '................',
];
S.charger_1 = bobbed(S.charger_0, ['..o.o......o.o..', BLANK]);
const spriteCache = new Map();

/* -------------------------------------------------------------- particles
 *
 * Every particle in the game was one `fillRect` shrinking from five pixels to
 * one. Four shapes, four frames each, drawn from the same kind of row mask as
 * every sprite and tinted per instance — so a spark reads as a spark, water
 * throws droplets, and smoke comes apart instead of getting smaller.
 *
 * `#` is the tint; `o` is the tint at a third, which is what gives an 8x8
 * shape any interior at all.
 */
export const PARTICLES = {
  // a struck spark: a point, a cross, a star coming apart, a ghost of one
  spark: [
    ['........', '........', '...##...', '...##...', '........', '........', '........', '........'],
    ['........', '...o....', '..####..', '..####..', '...o....', '........', '........', '........'],
    ['...o....', '..#..#..', '.#....#.', 'o......o', '.#....#.', '..#..#..', '...o....', '........'],
    ['..o...o.', '........', 'o......o', '........', '........', 'o......o', '..o...o.', '........'],
  ],
  // smoke: a blob that swells, thins and breaks
  smoke: [
    ['........', '........', '..###...', '..###...', '..##....', '........', '........', '........'],
    ['........', '..###...', '.#####..', '.#####..', '.####o..', '..oo....', '........', '........'],
    ['..oo....', '.o###o..', 'o##o##o.', 'o#o..#o.', '.o##o#..', '..o#o...', '...o....', '........'],
    ['.o..o...', 'o..o..o.', '.o....o.', 'o..o....', '....o..o', '.o...o..', '........', '........'],
  ],
  // water thrown up: a bead, a crown, droplets, a scatter
  splash: [
    ['........', '........', '...##...', '..####..', '...##...', '........', '........', '........'],
    ['........', '..#..#..', '.#.##.#.', '.#####..', '..###...', '........', '........', '........'],
    ['.#....#.', '..o..o..', '...##...', '..o##o..', '...oo...', '........', '........', '........'],
    ['#......#', '........', '.o....o.', '........', '...oo...', '........', '........', '........'],
  ],
  // fire: a teardrop that licks and gutters
  ember: [
    ['........', '........', '...#....', '..###...', '..###...', '...#....', '........', '........'],
    ['........', '...#....', '..###...', '.#####..', '.#####..', '..###...', '...o....', '........'],
    ['...o....', '..###...', '.##o##..', '.#o.o#..', '..###...', '...#....', '........', '........'],
    ['..o.o...', '...#....', '..#o#...', '..o#o...', '...o....', '........', '........', '........'],
  ],
};
/* A FLAME WAS THREE RECTANGLES. `Flame` — the fire that creeps through thorn,
 * lights braziers and sets off pods — drew three nested `fillRect`s with a
 * three-frame height wobble, in a file whose particle docstring declares that
 * shape abolished. It is the longest-lived visual in the game (a fire burns
 * for as long as there is thorn to take) and it was the least drawn.
 *
 * Ten rows rather than eight, because a flame is taller than it is wide, and
 * three tones: `:` the outer lick, `#` the body, `*` the white core. */
export const FLAME = [
  ['........', '........', '........', '....:...', '...:#:..',
   '..:#*#:.', '..:###:.', '..:###:.', '...:::..', '........'],
  ['........', '....:...', '...:#:..', '...:#:..', '..:#*#:.',
   '..:#*#:.', '.:#####.', '.:#####.', '..:::::.', '........'],
  ['...:....', '...:#...', '..:#*:..', '..:#*#:.', '.:#*##:.',
   '.:####:.', '.:####:.', '..####:.', '..::::..', '........'],
  ['.:......', '....:...', '...:#:..', '..:##:..', '..:#*#..',
   '.:####:.', '.:####:.', '..###:..', '..:::...', '........'],
];

/* ...AND THE ENEMY TELEGRAPH WAS ONE. A spawning enemy was announced by a
 * single `fillRect` shrinking from 16px to 2px, alternating paper and gold —
 * measured at a mean of 2.4 and a maximum of 5 flat squares on screen at
 * once, and it is the first thing a player sees of every fight in the game.
 *
 * A ring of dashes closing on the arrival point, generated rather than hand
 * drawn because it is pure geometry and eight spokes of it by hand is eight
 * spokes of typing errors. Baked once per frame into the same cache as
 * everything else, so it is a sheet at the point of use. */
const SPAWN_R = [7, 5.5, 4, 2.5, 1];
const SPOKES = 8;

const PARTICLE_CACHE = new Map();

/** One frame of the arrival telegraph, 16x16, tinted. */
export function spawnRing(frame, colour) {
  const f = Math.max(0, Math.min(SPAWN_R.length - 1, frame | 0));
  const key = 'ring' + f + colour;
  let c = PARTICLE_CACHE.get(key);
  if (c) return c;
  c = mkCanvas(16, 16);
  const g = c.getContext('2d');
  g.fillStyle = colour;
  const r = SPAWN_R[f];
  // the spokes turn as they close, so the ring reads as rotating rather than
  // merely smaller — a shrinking shape with no motion in it is a shrinking box
  const turn = f * 0.39;
  for (let i = 0; i < SPOKES; i++) {
    const a = turn + (i * Math.PI * 2) / SPOKES;
    const x = 8 + Math.cos(a) * r, y = 8 + Math.sin(a) * r;
    g.globalAlpha = 1;
    g.fillRect(Math.round(x) - 1, Math.round(y) - 1, 1, 1);
    // a faint trailing speck one step further out, so the ring has a direction
    if (f < SPAWN_R.length - 1) {
      const o = 8 + Math.cos(a - 0.5) * (r + 1.6), q = 8 + Math.sin(a - 0.5) * (r + 1.6);
      g.globalAlpha = 0.34;
      g.fillRect(Math.round(o) - 1, Math.round(q) - 1, 1, 1);
    }
  }
  // the last frame is the arrival itself: a hollow core, not a filled square
  if (f === SPAWN_R.length - 1) {
    g.globalAlpha = 1;
    for (const [dx, dy] of [[-1, -2], [0, -2], [-2, -1], [1, -1], [-2, 0], [1, 0],
      [-1, 1], [0, 1]]) g.fillRect(7 + dx, 7 + dy, 1, 1);
  }
  g.globalAlpha = 1;
  PARTICLE_CACHE.set(key, c);
  return c;
}

/** One frame of the flame, 8x10, in its three fixed tones. */
export function flameSprite(frame) {
  const f = Math.max(0, Math.min(FLAME.length - 1, frame | 0));
  const key = 'flame' + f;
  let c = PARTICLE_CACHE.get(key);
  if (c) return c;
  c = mkCanvas(8, 10);
  const g = c.getContext('2d');
  const tone = { ':': UI.flame, '#': UI.gold, '*': UI.paper };
  for (let y = 0; y < 10; y++) {
    for (let x = 0; x < 8; x++) {
      const ch = FLAME[f][y][x];
      if (!tone[ch]) continue;
      g.globalAlpha = ch === ':' ? 0.82 : 1;
      g.fillStyle = tone[ch];
      g.fillRect(x, y, 1, 1);
    }
  }
  g.globalAlpha = 1;
  PARTICLE_CACHE.set(key, c);
  return c;
}


/** One frame of one particle, tinted. Cached like every other sprite: the
 *  colours are few and the frames are four, so the sheet stays small. */
export function particle(kind, frame, colour) {
  const rows = PARTICLES[kind] || PARTICLES.smoke;
  const f = Math.max(0, Math.min(rows.length - 1, frame | 0));
  const key = kind + f + colour;
  let c = PARTICLE_CACHE.get(key);
  if (c) return c;
  // A PARTICLE NEEDS AN EDGE, BECAUSE IT IS DRAWN OVER EVERYTHING.
  //
  // These are small, bright and transient, and they land on whatever happens
  // to be underneath — which since the ground-cover layer arrived is usually
  // a field of lime grass in the same brightness range. A gold spark on lit
  // grass is a gold spark you cannot see. Every sprite in this game that
  // stands on the ground already carries a dark outline for exactly this
  // reason; the particles were the one layer that did not.
  //
  // The outline is the shape DILATED by one pixel in the four cardinal
  // directions, drawn underneath — so it costs nothing per frame (this is
  // cached per kind/frame/colour) and follows any mask without being drawn
  // by hand. One pixel bigger all round, so the 8x8 cell grows to 10x10 and
  // the draw offset moves with it.
  c = mkCanvas(10, 10);
  const g = c.getContext('2d');
  g.fillStyle = PARTICLE_EDGE;
  for (const [ox, oy] of outlineOf(rows[f])) g.fillRect(ox, oy, 1, 1);
  for (let y = 0; y < 8; y++) {
    for (let x = 0; x < 8; x++) {
      const ch = rows[f][y][x];
      if (ch === '.' || ch === undefined) continue;
      g.globalAlpha = ch === 'o' ? 0.34 : 1;
      g.fillStyle = colour;
      g.fillRect(x + 1, y + 1, 1, 1);
    }
  }
  g.globalAlpha = 1;
  PARTICLE_CACHE.set(key, c);
  return c;
}

/** The raw row mask of a sprite, so a check can compare two frames without a
 *  2D context — there is none in node, and the alternative is a check that
 *  greps the source, which cannot see a mask built by a helper. */
export function spriteRows(name) {
  return S[name] || null;
}

/* WHICH BODY A FACING ACTUALLY HAS ART FOR.
 *
 * An art review counted it: `flip` was applied to exactly two of the eleven
 * actors, no ENEMY had up or down art at all, and only the hero had
 * directional bodies. So a charger aimed north and a charger aimed south were
 * the same pixels, and the one thing a player has to know about a charger —
 * which line it has picked — was not on the screen. `charger` and `shellback`
 * are the two that commit to a direction; they have `_up` and `_down` sets in
 * `src/sprites/` now.
 *
 * This is a LOOKUP, not a list. A caller hands over the body name it already
 * computed and the facing it already knows, and gets back the directional set
 * if one was authored and the same name if one was not — so adding `_up`
 * frames for a third actor is a new file and an import, with no second place
 * to remember to edit. The alternative is a set of type names living in the
 * draw call, which is how the old `flip` condition came to name two types by
 * hand and stay that way through four rounds of art work.
 *
 * `left` and `right` are deliberately not resolved here: those are one body
 * and a flip, which is what `flip` is for, and giving them their own sets
 * would double the sheet to say nothing new.
 */
export function facing(art, dir) {
  if (dir !== 'up' && dir !== 'down') return art;
  const named = art + '_' + dir;
  return S[named + '_0'] ? named : art;
}

/** The one-pixel dilation of a particle mask, in the 10x10 cell's own
 *  coordinates. Pure, and exported, so a check can ask whether a particle has
 *  an edge without needing a 2D context — there is none in node, and a check
 *  that greps the source instead is not a check. */
export function outlineOf(mask) {
  const lit = (x, y) => {
    if (x < 0 || y < 0 || x > 7 || y > 7) return false;
    const ch = mask[y][x];
    return ch !== '.' && ch !== undefined;
  };
  const out = [];
  for (let y = -1; y <= 8; y++) {
    for (let x = -1; x <= 8; x++) {
      if (lit(x, y)) continue;
      // 8-connected: a particle mask is mostly diagonals, and a 4-neighbour
      // dilation leaves every diagonal step open, so the shape still leaks
      // its own colour into whatever is under it.
      let touches = false;
      for (let dy = -1; dy <= 1 && !touches; dy++) {
        for (let dx = -1; dx <= 1 && !touches; dx++) {
          if ((dx || dy) && lit(x + dx, y + dy)) touches = true;
        }
      }
      if (touches) out.push([x + 1, y + 1]);
    }
  }
  return out;
}

/** The colour a particle is outlined in: near-black, a touch warm so it does
 *  not read as a hole in the picture. Dark enough to separate a spark from
 *  lit grass, which is the brightest thing it will ever land on. */
export const PARTICLE_EDGE = '#120f1c';

function bake(rows, tint, alt) {
  // sized from the rows themselves: the Warden is 32x32 art, not 16x16 art
  // with a `scale: 2` on the draw call
  const n = rows.length;
  const c = mkCanvas(n, n);
  const g = c.getContext('2d');
  const pal = alt ? { ...P, ...ALT[alt] } : P;
  for (let y = 0; y < rows.length; y++) {
    const row = rows[y];
    for (let x = 0; x < row.length; x++) {
      const ch = row[x];
      if (ch === '.' || ch === ' ') continue;
      const col = tint || pal[ch];
      if (!col) continue;   // a letter the palette does not know draws nothing, not magenta (the art lens, 2026-09-05: a pink square on seed 7)
      g.fillStyle = col;
      g.fillRect(x, y, 1, 1);
    }
  }
  return c;
}

/** The colour a BODY is outlined in. Same argument as `PARTICLE_EDGE` and the
 *  same near-black, because the job is identical: guarantee a boundary against
 *  whatever happens to be behind it. */
export const BODY_EDGE = '#120f1c';
/** The sunlit top of a body's outline — see `spriteOutline`.
 *
 *  Chosen against the darkest ground in the game rather than as far from the
 *  dark outline as possible: the ash and scree countries sit at luma 98-113,
 *  so a rim near 150 clears them by forty and still reads as a lit edge. The
 *  first cut was nearly white, which separated perfectly and turned every
 *  character in the game into someone wearing a white cap. */
export const BODY_RIM = '#9c96ac';

/**
 * A one-pixel dark silhouette to draw UNDER a sprite, at (x-1, y-1).
 *
 * WHY A BODY NEEDS ONE. The player has to read against every ground in the
 * game, and those grounds span relative luminance 0.30 (ash) to 0.64 (dust) —
 * so no single body colour can clear the WCAG 1.4.11 non-text floor of 3:1
 * everywhere. Measured on the shipped art, the median player pixel against the
 * median background was 1.05:1 in ash, 1.22 scree, 1.37 fen, 1.89 bloom and
 * 2.06 dust: every biome under the floor, and the darkest of them barely
 * distinguishable at all.
 *
 * An outline fixes it for all of them at once, because it is measured against
 * the BACKGROUND rather than against the body: `BODY_EDGE` is 0.0054 relative
 * luminance, which is 6.3:1 on ash and 12.4:1 on dust — the two extremes.
 *
 * Cached per name/flip/alt like the sprite itself, so it costs one extra
 * `drawImage` per body per frame and nothing else.
 */
/** The cells of a sprite's outline, in the (n+2) grid the outline canvas uses.
 *
 *  Pure and exported for the same reason `edgeBite` is: baking needs a 2D
 *  context, node has none, and a check that greps the source for the code is
 *  not a check. With this, "the silhouette is completely enclosed" is a
 *  property a test can evaluate rather than a claim in a comment. */
export function outlineCells(rows) {
  const n = rows.length;
  const lit = (x, y) => {
    if (x < 0 || y < 0 || x >= n || y >= n) return false;
    const ch = rows[y][x];
    return ch !== '.' && ch !== ' ' && ch !== undefined;
  };
  const out = [];
  // 8-connected, like the particle outline: a 4-neighbour dilation leaves
  // every diagonal step of a silhouette open and the body leaks its own colour
  // into the ground through the gap.
  for (let y = -1; y <= n; y++) {
    for (let x = -1; x <= n; x++) {
      if (lit(x, y)) continue;
      let touches = false;
      for (let dy = -1; dy <= 1 && !touches; dy++) {
        for (let dx = -1; dx <= 1 && !touches; dx++) {
          if ((dx || dy) && lit(x + dx, y + dy)) touches = true;
        }
      }
      if (touches) out.push([x + 1, y + 1]);
    }
  }
  return out;
}

export function spriteOutline(name, flip = false, alt = null) {
  const key = 'O' + name + (flip ? 'F' : '') + (alt || '');
  let c = spriteCache.get(key);
  if (c) return c;
  const rows = S[name];
  if (!rows) throw new Error('no sprite ' + name);
  const n = rows.length;
  c = mkCanvas(n + 2, n + 2);
  const g = c.getContext('2d');
  /* A DARK OUTLINE SEPARATES A BODY FROM BRIGHT GROUND AND FROM NOTHING ELSE.
   *
   * `BODY_EDGE` is nearly black, which is the right answer over grass or sand
   * and no answer at all over the ash and scree countries, whose ground is as
   * dark as the body standing on it. Measured over five seeds and three
   * screens each: the player's mean luma is 99-119 and the ground's is 94-160,
   * and the worst case is a difference of TWO — a body at 111 on ground at
   * 113, which is invisible however carefully it is drawn.
   *
   * The fix is not to repaint the body but to light its rim. The cells along
   * the TOP of the silhouette — the ones with body directly beneath them —
   * take a pale edge, and everything else keeps the dark one. That is the
   * standard solution and it costs nothing at runtime because the outline is
   * baked once per sprite: over pale ground the dark underside reads, over
   * dark ground the lit top reads, and there is no ground that defeats both.
   *
   * It applies to every body, not only the player, because one light
   * direction across all of them is the whole point. */
  const litAt = (x, y) => {
    if (x < 0 || y < 0 || x >= n || y >= n) return false;
    const ch = rows[y][x];
    return ch !== '.' && ch !== ' ' && ch !== undefined;
  };
  for (const [ox, oy] of outlineCells(rows)) {
    // the cell sits at (ox-1, oy-1) in sprite space; body directly below it
    // means this is the sunlit top edge
    g.fillStyle = litAt(ox - 1, oy) ? BODY_RIM : BODY_EDGE;
    g.fillRect(ox, oy, 1, 1);
  }
  if (flip) {
    const f = mkCanvas(n + 2, n + 2);
    const fg = f.getContext('2d');
    fg.translate(n + 2, 0); fg.scale(-1, 1);
    fg.drawImage(c, 0, 0);
    c = f;
  }
  spriteCache.set(key, c);
  return c;
}

export function sprite(name, flip = false, white = false, alt = null) {
  const key = name + (flip ? 'F' : '') + (white ? 'W' : '') + (alt || '');
  let c = spriteCache.get(key);
  if (c) return c;
  const rows = S[name];
  if (!rows) throw new Error('no sprite ' + name);
  let base = bake(rows, white ? '#fdf6e6' : null, alt);
  if (flip) {
    const n = rows.length;
    const f = mkCanvas(n, n);
    const g = f.getContext('2d');
    g.translate(n, 0); g.scale(-1, 1);
    g.drawImage(base, 0, 0);
    base = f;
  }
  spriteCache.set(key, base);
  return base;
}

export function validateSprites() {
  validateFont();
  for (const [name, rows] of Object.entries(S as Record<string, any>)) {
    const n = rows && rows.length;
    if (n !== 16 && n !== 32) throw new Error(name + ' has ' + n + ' rows');
    rows.forEach((r, i) => {
      if (r.length !== n) throw new Error(name + ' row ' + i + ' len ' + r.length + ', want ' + n);
      for (const ch of r) if (ch !== '.' && !P[ch]) throw new Error(name + ' row ' + i + ' bad char ' + ch);
    });
  }
  return true;
}

// --------------------------------------------------------- item / hud art

// A bigger pickup used to mean a bigger PIXEL: `drawLeaf` scaled its 8x8 mask
// by `round(size / 8)`, so the sap drop and the vigor upgrade were drawn in
// 2x2 blocks — the only double-density art left in the playfield, sitting on
// a 1px grid of grass, next to a 1px glim, held by a 1px player. Size is a
// different MASK now, never a different pixel. See `blockUniform` in the soak
// suite, which fails any playfield pickup whose coverage survives 2x2
// quantisation.
const hex2rgb = (h) => [parseInt(h.slice(1, 3), 16), parseInt(h.slice(3, 5), 16), parseInt(h.slice(5, 7), 16)];
const rgb2hex = (c) => '#' + c.map((v) => Math.max(0, Math.min(255, Math.round(v))).toString(16).padStart(2, '0')).join('');
const blend = (a, b, t) => { const A = hex2rgb(a), B = hex2rgb(b); return rgb2hex(A.map((v, i) => v + (B[i] - v) * t)); };

/** The pickup leaf, at one pixel per pixel: 12 wide, 13 tall, midrib running
 *  from the stem at the top right down to the tip at the bottom left. */
const LEAF = [
  '........o...',
  '.....oooso..',
  '...oogllvgo.',
  '..oglllgvgo.',
  '.oglllgvggo.',
  'oglllgvgggo.',
  'ogllgvgggo..',
  'ogllvgggo...',
  'oglvgggo....',
  '.ogvggo.....',
  '.ogvgo......',
  '..ogo.......',
  '...o........',
];
export const LEAF_W = 12, LEAF_H = LEAF.length;

/** Vigor: a leaf pip that wilts as you take damage. `size` picks the mask —
 *  8 or less is the HUD pip, anything larger is the world pickup. */
export function drawLeaf(g, x, y, size, fill, rim) {
  if (size > 8) {
    const flat = !rim || rim === fill;
    const col = {
      o: rim || fill,
      g: fill,
      l: flat ? fill : blend(fill, '#ffffff', 0.34),
      v: flat ? fill : blend(fill, '#241a24', 0.34),
      s: flat ? (rim || fill) : blend(fill, '#241a24', 0.5),
    };
    for (let ry = 0; ry < LEAF.length; ry++) {
      for (let rx = 0; rx < LEAF_W; rx++) {
        const ch = LEAF[ry][rx];
        if (ch === '.') continue;
        g.fillStyle = col[ch];
        g.fillRect(x + rx, y + ry, 1, 1);
      }
    }
    return;
  }
  g.fillStyle = rim || fill;
  g.fillRect(x + 1, y, 6, 1);
  g.fillRect(x, y + 1, 8, 4);
  g.fillRect(x + 1, y + 5, 6, 1);
  g.fillRect(x + 2, y + 6, 4, 1);
  g.fillRect(x + 3, y + 7, 2, 1);
  if (rim) {
    g.fillStyle = fill;
    g.fillRect(x + 2, y + 1, 4, 4);
    g.fillRect(x + 1, y + 2, 6, 2);
  }
}

/** Glim: a four-point spark, the world's currency. */
/* THE ITEMS' ICONS (LS-225). Vijay: "the icons suck make them better", then
 * "use the 3d asset pipeline". So an icon is not a picture of an item: it is
 * the item, built as solids and put through the one camera (`model3d.ts`)
 * like every rock, trunk and body in the world, then rasterised into a
 * fourteen-pixel sheet once and kept. A sickle is a wooden rod and an arc of
 * tapering steel; an axe is a haft and a box with a lit top; a torch is a
 * stick, a binding and a flame with a bright core; a skull is a ball, a jaw
 * and two dark sockets. The palette and the ink are the world's own, so the
 * hotbar and the ground agree about what a thing looks like. */
/* A WIDER LIGHT ON THE ICONS (the judge, 2026-09-05: "lacks the consistent light source, rich colour palettes and
 * clear material definition"). Steel gets a near-white specular and a cool, dark shadow; wood's shadow is hue-shifted
 * warmer and redder rather than simply darker. The solid renderer shades between these three by the surface normal,
 * so widening the range is what gives every icon its depth at once. */
const WOOD = { base: '#8a6b3a', lit: '#d8ac62', dark: '#452a14' };
const STEEL = { base: '#b4b9c4', lit: '#fbfdff', dark: '#535a6a' };
/* THE BAG'S ICONS, AGAINST THEIR OWN REFERENCE RENDERS (2026-09-05; Vijay: "use gemini references for every single
 * sprite in the game"). Gemini was given our fourteen icons, told what each one IS, and asked for the shipped
 * version; the sheet that came back is the target. What it said, item by item: the sickle read as a HAMMER (a thick
 * arc over a centred handle) and wants a thin C-blade over a short handle held down and left; the spear's head was
 * at the bottom of the shaft; the axe's head sat halfway up the haft, which reads as a FLAG; the pod hung the wrong
 * way up; the silt lung and the emberwake were drawn half outside the cell; the hide, the plate and the mantle were
 * three coloured ellipses; the treads were two pale boxes with a bar under them. A silhouette first, then one
 * light source up and to the left -- which is what the solid renderer gives for free once the shape is right.
 *
 * The cell is 14x14 with the origin at its BOTTOM left: y counts upward, z toward the viewer. */
const ICON_SCENES: Record<string, () => M3.Solid[]> = {
  /* THE SECOND RANK OF WEAPONS (LS-335, 2026-09-15). The fight had four -- sickle, spear, axe, torch -- and gear is
   * the one place a number changes how a fight feels without touching the fight. Each of these is the same four
   * verbs the world already answers (cut, pierce, crush, burn) at a different point on reach, speed, damage and
   * wear, so each is a DECISION rather than a bag row: a knife shreds what is slow and cannot reach a charge, a
   * pike outranges everything and is hopeless once something is inside it. Built from rods and balls, which is what
   * this renderer lights well -- boxes came out as planks the last time (the clasp, LS-335 batch one). */
  /* THE THREE MATERIALS (LS-335, 2026-09-15). A material has to read as a HANDFUL of something rather than as a
   * tool: no handle, no point, nothing aimed. Balls and stubby rods, which is what this renderer lights well. */
  /* FIVE CHARMS (LS-335, 2026-09-16): a charm is a small thing on a cord, so each has a loop at its head. */
  stoneheart: () => [   // a heart-shaped river stone, dark, with a pale vein
    { kind: 'ball', at: [5.6, 6.4, 0], r: 2.6, ry: 2.6, rz: 1.8, skin: { base: '#5a5560', lit: '#9a94a2', dark: '#26232c' } },
    { kind: 'ball', at: [8.4, 6.4, 0], r: 2.6, ry: 2.6, rz: 1.8, skin: { base: '#5a5560', lit: '#9a94a2', dark: '#26232c' } },
    { kind: 'ball', at: [7.0, 4.0, 0], r: 2.4, ry: 2.2, rz: 1.6, skin: { base: '#5a5560', lit: '#9a94a2', dark: '#26232c' } },
    { kind: 'rod', a: [5.4, 5.0, 1.4], b: [8.6, 7.6, 1.4], r: 0.4, skin: { base: '#d8d2dc' } },
    { kind: 'rod', a: [7.0, 9.0, 0], b: [7.0, 12.6, 0], r: 0.45, skin: { base: '#8a6b3a', dark: '#452a14' } },
  ],
  crow: () => [   // a crow's black feather, hung by its quill
    { kind: 'ball', at: [6.6, 6.0, 0], r: 2.0, ry: 5.0, rz: 1.0, skin: { base: '#1e2230', lit: '#4a5270', dark: '#0a0c14' } },
    { kind: 'rod', a: [6.6, 1.2, 0.6], b: [6.8, 10.6, 0.6], r: 0.45, skin: { base: '#8a8ea0', lit: '#d0d4e0' } },
    { kind: 'rod', a: [6.8, 10.6, 0], b: [7.0, 13.0, 0], r: 0.4, skin: { base: '#8a6b3a', dark: '#452a14' } },
  ],
  embercharm: () => [   // a coal in a wire cage, still red at the heart
    { kind: 'ball', at: [7.0, 6.2, 0], r: 3.0, ry: 3.0, rz: 2.2, skin: { base: '#3a2420', lit: '#6a4238', dark: '#160a08' } },
    { kind: 'ball', at: [7.0, 6.2, 1.4], r: 1.5, ry: 1.5, rz: 0.8, skin: { base: '#ff6a2a', lit: '#ffd08a' } },
    { kind: 'rod', a: [4.0, 4.0, 1.0], b: [10.0, 8.4, 1.0], r: 0.35, skin: { base: '#8a8a8a', lit: '#cccccc' } },
    { kind: 'rod', a: [10.0, 4.0, 1.0], b: [4.0, 8.4, 1.0], r: 0.35, skin: { base: '#8a8a8a', lit: '#cccccc' } },
    { kind: 'rod', a: [7.0, 9.2, 0], b: [7.0, 12.8, 0], r: 0.45, skin: { base: '#8a6b3a', dark: '#452a14' } },
  ],
  keel: () => [   // a little carved hull, keel down, on a cord
    { kind: 'ball', at: [7.0, 6.0, 0], r: 4.4, ry: 1.9, rz: 1.6, skin: { base: '#7a5c30', lit: '#c49a54', dark: '#3a2410' } },
    { kind: 'rod', a: [2.8, 5.2, 0], b: [11.2, 5.2, 0], r: 0.5, skin: { base: '#a07c44', lit: '#dcb878' } },
    { kind: 'rod', a: [7.0, 4.2, 0], b: [7.0, 1.4, 0], r: 0.5, skin: { base: '#8a6b3a', dark: '#452a14' } },
    { kind: 'rod', a: [7.0, 8.0, 0], b: [7.0, 12.8, 0], r: 0.45, skin: { base: '#8a6b3a', dark: '#452a14' } },
  ],
  anchor: () => [   // a bone anchor: a shank, a ring, two flukes
    { kind: 'rod', a: [7.0, 3.6, 0], b: [7.0, 10.4, 0], r: 0.9, skin: { base: '#d8d2c4', lit: '#fbf6ea', dark: '#6e6858' } },
    { kind: 'rod', a: [3.6, 4.6, 0], b: [7.0, 3.6, 0], r: 0.8, skin: { base: '#d8d2c4', lit: '#fbf6ea', dark: '#6e6858' } },
    { kind: 'rod', a: [10.4, 4.6, 0], b: [7.0, 3.6, 0], r: 0.8, skin: { base: '#d8d2c4', lit: '#fbf6ea', dark: '#6e6858' } },
    { kind: 'ball', at: [7.0, 11.4, 0], r: 1.4, ry: 1.4, rz: 0.8, skin: { base: '#c8c2b4', lit: '#f0ece0', dark: '#5e5848' } },
    { kind: 'rod', a: [3.0, 6.6, 0], b: [11.0, 6.6, 0], r: 0.5, skin: { base: '#d8d2c4', dark: '#6e6858' } },
  ],
  lodestone: () => [   // a dark lodestone with a bright north face, on a cord
    { kind: 'ball', at: [7.0, 6.6, 0], r: 3.4, ry: 3.0, rz: 2.2, skin: { base: '#3a3f4e', lit: '#6e768e', dark: '#161a24' } },
    { kind: 'ball', at: [7.8, 5.2, 1.4], r: 1.4, ry: 1.2, rz: 0.8, skin: { base: '#c8d4ff', lit: '#ffffff' } },
    { kind: 'rod', a: [7.0, 9.6, 0], b: [7.0, 12.8, 0], r: 0.45, skin: { base: '#8a6b3a', dark: '#452a14' } },
  ],
  miser: () => [   // a purse drawn tight, with one glim showing at its mouth
    { kind: 'ball', at: [7.0, 6.0, 0], r: 3.6, ry: 3.4, rz: 2.4, skin: { base: '#7a4b2e', lit: '#c08a56', dark: '#361a08' } },
    { kind: 'rod', a: [4.4, 9.2, 0.4], b: [9.6, 9.2, 0.4], r: 0.5, skin: { base: '#452a14', lit: '#8a6b3a' } },
    { kind: 'ball', at: [7.0, 9.8, 1.2], r: 1.0, ry: 1.0, rz: 0.7, skin: { base: '#f2c14e', lit: '#ffe9a8' } },
    { kind: 'rod', a: [7.0, 10.6, 0], b: [7.0, 12.8, 0], r: 0.45, skin: { base: '#8a6b3a', dark: '#452a14' } },
  ],
  frost: () => [   // a six-armed crystal, pale blue
    { kind: 'ball', at: [7.0, 6.4, 0], r: 1.4, ry: 1.4, rz: 1.0, skin: { base: '#c8e8ff', lit: '#ffffff', dark: '#5a8ab0' } },
    { kind: 'rod', a: [7.0, 3.0, 0], b: [7.0, 9.8, 0], r: 0.5, skin: { base: '#a8d0f0', lit: '#e8f6ff', dark: '#4a7a9a' } },
    { kind: 'rod', a: [4.0, 4.7, 0], b: [10.0, 8.1, 0], r: 0.5, skin: { base: '#a8d0f0', lit: '#e8f6ff', dark: '#4a7a9a' } },
    { kind: 'rod', a: [4.0, 8.1, 0], b: [10.0, 4.7, 0], r: 0.5, skin: { base: '#a8d0f0', lit: '#e8f6ff', dark: '#4a7a9a' } },
    { kind: 'rod', a: [7.0, 9.8, 0], b: [7.0, 12.8, 0], r: 0.45, skin: { base: '#8a6b3a', dark: '#452a14' } },
  ],
  salt: () => [   // a knot of white salt in a twist of cloth
    { kind: 'ball', at: [7.0, 6.6, 0], r: 3.2, ry: 3.0, rz: 2.2, skin: { base: '#e6e2d8', lit: '#ffffff', dark: '#8a867c' } },
    { kind: 'ball', at: [6.0, 5.4, 1.4], r: 1.0, ry: 0.8, rz: 0.6, skin: { base: '#ffffff' } },
    { kind: 'rod', a: [5.8, 9.4, 0], b: [8.2, 9.4, 0], r: 0.6, skin: { base: '#b8a888', dark: '#6a5a3a' } },
    { kind: 'rod', a: [7.0, 10.0, 0], b: [7.0, 12.8, 0], r: 0.45, skin: { base: '#8a6b3a', dark: '#452a14' } },
  ],
  /* FOUR CONSUMABLES (LS-335, 2026-09-16), each a handful or a flask, nothing aimed. */
  quickroot: () => [   // a pale root with a red heart and two rootlets
    { kind: 'ball', at: [7.0, 6.6, 0], r: 2.6, ry: 4.4, rz: 1.8, skin: { base: '#d9c9a0', lit: '#fff4d8', dark: '#7a6a44' } },
    { kind: 'ball', at: [7.0, 6.4, 1.2], r: 1.1, ry: 1.9, rz: 0.7, skin: { base: '#c8422e', lit: '#ff8a6a' } },
    { kind: 'rod', a: [5.6, 3.0, 0], b: [3.6, 0.8, 0], r: 0.5, skin: { base: '#c2b08a', dark: '#6a5a3a' } },
    { kind: 'rod', a: [8.4, 3.2, 0], b: [10.2, 1.0, 0], r: 0.5, skin: { base: '#c2b08a', dark: '#6a5a3a' } },
    { kind: 'rod', a: [7.0, 11.0, 0], b: [7.4, 13.2, 0], r: 0.6, skin: { base: '#6a9a4a', lit: '#a8d878', dark: '#2e4a1e' } },
  ],
  stonemilk: () => [   // a stoppered flask of something grey and thick
    { kind: 'ball', at: [7.0, 5.4, 0], r: 3.6, ry: 3.9, rz: 2.4, skin: { base: '#8e8f96', lit: '#dcdde4', dark: '#3c3d44' } },
    { kind: 'rod', a: [7.0, 9.0, 0], b: [7.0, 11.6, 0], r: 1.4, skin: { base: '#8e8f96', lit: '#c9cad0', dark: '#3c3d44' } },
    { kind: 'ball', at: [7.0, 12.4, 0], r: 1.5, ry: 0.9, rz: 1.0, skin: { base: '#6a4a2c', lit: '#a07a50', dark: '#2e1c0e' } },
    { kind: 'ball', at: [5.8, 6.6, 1.6], r: 1.0, ry: 1.4, rz: 0.6, skin: { base: '#eef0f6' } },
  ],
  honeycomb: () => [   // a wedge of comb, amber, dripping
    { kind: 'ball', at: [7.0, 7.0, 0], r: 4.4, ry: 3.4, rz: 1.6, skin: { base: '#d9a032', lit: '#ffd86a', dark: '#6b4410' } },
    { kind: 'ball', at: [5.4, 6.2, 1.4], r: 1.0, skin: { base: '#8a5a14', lit: '#c48a2e' } }, { kind: 'ball', at: [8.4, 7.4, 1.4], r: 1.0, skin: { base: '#8a5a14', lit: '#c48a2e' } },
    { kind: 'ball', at: [7.0, 11.0, 0.6], r: 1.2, ry: 1.6, rz: 0.8, skin: { base: '#f0b93a', lit: '#ffe68a' } },
  ],
  saltcake: () => [   // a pale grey cake, crusted white
    { kind: 'box', at: [7.0, 7.4, 0], w: 8.0, h: 5.0, d: 3.0, skin: { base: '#b9bcc4', lit: '#eef0f4', dark: '#5c6068' } },
    { kind: 'ball', at: [5.6, 5.6, 1.8], r: 0.9, skin: { base: '#ffffff', lit: '#ffffff' } }, { kind: 'ball', at: [8.8, 6.4, 1.8], r: 0.8, skin: { base: '#ffffff', lit: '#ffffff' } },
  ],
  bitterleaf: () => [   // a long dark leaf with a pale rib
    { kind: 'ball', at: [7.0, 7.0, 0], r: 2.6, ry: 5.0, rz: 0.7, skin: { base: '#2f5a3a', lit: '#6f9a70', dark: '#122a18' } },
    { kind: 'rod', a: [7.0, 2.6, 0.5], b: [7.0, 11.4, 0.5], r: 0.35, skin: { base: '#a9c99a', lit: '#e0f0d8' } },
  ],
  emberseed: () => [   // a black seed with a live ember glowing in its split
    { kind: 'ball', at: [7.0, 7.4, 0], r: 3.6, ry: 4.2, rz: 2.0, skin: { base: '#2a1e1a', lit: '#5a4a44', dark: '#0e0908' } },
    { kind: 'ball', at: [7.0, 7.0, 1.6], r: 1.4, ry: 2.2, rz: 0.8, skin: { base: '#ff7a1e', lit: '#ffd070', dark: '#a03000' } },
  ],
  nightcap: () => [   // a dark blue cap on a pale stalk, gills showing
    { kind: 'rod', a: [7.0, 1.0, 0], b: [7.0, 6.4, 0], r: 1.15, skin: { base: '#d8d2c4', lit: '#fbf6ea', dark: '#6e6858' } },
    { kind: 'ball', at: [7.0, 8.4, 0], r: 4.6, ry: 2.9, rz: 2.4, skin: { base: '#2c3a6e', lit: '#6a80c8', dark: '#101838' } },
    { kind: 'ball', at: [7.0, 7.0, -0.4], r: 3.6, ry: 0.9, rz: 1.2, skin: { base: '#b8a8c8', dark: '#5a4a6a' } },
    { kind: 'ball', at: [5.4, 9.6, 1.6], r: 0.8, ry: 0.8, rz: 0.5, skin: { base: '#9ab0ff', lit: '#e0e8ff' } },
  ],
  windfall: () => [   // a seed pod split open with its down streaming to the right
    { kind: 'ball', at: [4.6, 6.8, 0], r: 2.2, ry: 3.2, rz: 1.6, skin: { base: '#8a6a3a', lit: '#c8a262', dark: '#3e2c14' } },
    { kind: 'ball', at: [6.4, 7.4, 0.6], r: 1.6, ry: 2.2, rz: 1.0, skin: { base: '#f0ece0', lit: '#ffffff', dark: '#8a8878' } },
    { kind: 'rod', a: [7.2, 8.0, 0], b: [12.6, 10.2, 0], r: 0.55, skin: { base: '#f4f0e6', lit: '#ffffff' } },
    { kind: 'rod', a: [7.0, 7.0, 0], b: [12.8, 7.2, 0], r: 0.5, skin: { base: '#f4f0e6', lit: '#ffffff' } },
    { kind: 'rod', a: [7.2, 6.0, 0], b: [12.2, 3.8, 0], r: 0.5, skin: { base: '#f4f0e6', lit: '#ffffff' } },
  ],
  shell: () => [   // a crab's shell, domed, olive and pale-rimmed
    { kind: 'ball', at: [7.0, 7.0, 0], r: 4.2, ry: 3.2, rz: 2.0, skin: { base: '#6b7a4a', lit: '#a9b787', dark: '#2e361c' } },
    { kind: 'ball', at: [7.0, 8.6, 0.6], r: 3.2, ry: 1.0, rz: 0.8, skin: { base: '#c9c3a0', lit: '#eeeacc' } },
  ],
  chitin: () => [   // a hopper's plate, grey with a pale ridge
    { kind: 'ball', at: [7.0, 7.0, 0], r: 4.0, ry: 3.6, rz: 1.6, skin: { base: '#5d5f66', lit: '#9a9ca6', dark: '#24252b' } },
    { kind: 'rod', a: [4.0, 5.4, 1.2], b: [10.0, 5.4, 1.2], r: 0.6, skin: { base: '#c8cad2', lit: '#ffffff' } },
  ],
  scale: () => [   // three sun scales, amber, fanned
    { kind: 'ball', at: [5.2, 7.6, 0], r: 2.2, ry: 3.0, rz: 0.6, skin: { base: '#c7822e', lit: '#f2c25a', dark: '#5a3410' } },
    { kind: 'ball', at: [7.6, 6.4, 0.4], r: 2.2, ry: 3.0, rz: 0.6, skin: { base: '#d18f36', lit: '#f7cc66', dark: '#5a3410' } },
    { kind: 'ball', at: [9.8, 7.6, 0.8], r: 2.2, ry: 3.0, rz: 0.6, skin: { base: '#c7822e', lit: '#f2c25a', dark: '#5a3410' } },
  ],
  moss: () => [   // a wet clump of moss, dark green, beaded
    { kind: 'ball', at: [7.0, 7.4, 0], r: 4.0, ry: 3.0, rz: 2.2, skin: { base: '#3f6b3a', lit: '#7fb06f', dark: '#17301a' } },
    { kind: 'ball', at: [5.2, 5.8, 2.2], r: 0.7, skin: { base: '#bfe6ff', lit: '#ffffff' } }, { kind: 'ball', at: [8.6, 6.4, 2.4], r: 0.7, skin: { base: '#bfe6ff', lit: '#ffffff' } },
  ],
  greaves: () => [   // two shin plates on a strap, hopper grey with a shell rim
    { kind: 'rod', a: [4.6, 4.0, 0], b: [4.6, 11.0, 0], r: 1.4, skin: { base: '#5d5f66', lit: '#9a9ca6', dark: '#24252b' } },
    { kind: 'rod', a: [9.4, 4.0, 0], b: [9.4, 11.0, 0], r: 1.4, skin: { base: '#5d5f66', lit: '#9a9ca6', dark: '#24252b' } },
    { kind: 'rod', a: [3.0, 10.6, 0.6], b: [11.0, 10.6, 0.6], r: 0.6, skin: { base: '#c9c3a0', lit: '#eeeacc' } },
  ],
  scalecharm: () => [   // one amber scale on a cord
    { kind: 'ball', at: [7.0, 7.4, 0], r: 3.0, ry: 4.0, rz: 0.8, skin: { base: '#d18f36', lit: '#f7cc66', dark: '#5a3410' } },
    { kind: 'rod', a: [7.0, 2.4, 0], b: [7.0, 4.0, 0], r: 0.45, skin: { base: '#8a6b3a', dark: '#452a14' } },
  ],
  poultice: () => [   // a moss pad bound in a leaf
    { kind: 'ball', at: [7.0, 7.4, 0], r: 4.2, ry: 2.6, rz: 1.6, skin: { base: '#7a9a4a', lit: '#b8d488', dark: '#2e4a1c' } },
    { kind: 'ball', at: [7.0, 7.0, 1.2], r: 2.6, ry: 1.6, rz: 1.0, skin: { base: '#3f6b3a', lit: '#7fb06f', dark: '#17301a' } },
  ],
  greatshell: () => [   // a plate of the old shell, ridged, dark green
    { kind: 'ball', at: [7.0, 7.4, 0], r: 4.8, ry: 4.0, rz: 2.2, skin: { base: '#3f5a3a', lit: '#7a9a70', dark: '#16281a' } },
    { kind: 'rod', a: [3.4, 7.4, 1.6], b: [10.6, 7.4, 1.6], r: 0.6, skin: { base: '#a9b787', lit: '#e0eacc' } },
  ],
  brambleheart: () => [   // a red heart of thorn, alive
    { kind: 'ball', at: [7.0, 7.6, 0], r: 3.8, ry: 4.2, rz: 2.4, skin: { base: '#a8302a', lit: '#e86a5a', dark: '#4a1010' } },
    { kind: 'rod', a: [4.2, 4.6, 1.2], b: [2.6, 2.6, 1.8], r: 0.5, skin: { base: '#2e4a1c', lit: '#6a9a3a' } }, { kind: 'rod', a: [9.8, 4.6, 1.2], b: [11.4, 2.6, 1.8], r: 0.5, skin: { base: '#2e4a1c', lit: '#6a9a3a' } },
  ],
  sowtusk: () => [   // the sow's tusk, thick ivory curving to a point
    { kind: 'rod', a: [3.0, 11.6, 0], b: [8.0, 7.0, 0], r: 1.8, skin: { base: '#e6dcbc', lit: '#fff8e6', dark: '#7a6a48' } },
    { kind: 'rod', a: [8.0, 7.0, 0], b: [11.6, 2.8, 0], r: 1.0, skin: { base: '#d9cfae', lit: '#f6efd8', dark: '#7a6a48' } },
  ],
  tuskpike: () => [   // THE TUSK PIKE: a darker, heavier haft and the sow's thick tusk for a head
    { kind: 'rod', a: [0.7, 0.4, 0], b: [9.2, 8.8, 0], r: 0.95, skin: { base: '#5a3f28', lit: '#9a7048', dark: '#2a1a0c' } },
    { kind: 'rod', a: [8.8, 8.4, 0], b: [11.6, 11.6, 0], r: 1.4, skin: { base: '#e6dcbc', lit: '#fff8e6', dark: '#7a6a48' } },
    { kind: 'rod', a: [11.6, 11.6, 0], b: [13.4, 13.6, 0], r: 0.8, skin: { base: '#e6dcbc', lit: '#fff8e6', dark: '#7a6a48' } },
  ],
  thornwhip: () => [   // THE THORNWHIP: a short grip and a living bramble lash, green with red thorns, the heart's red at the tip
    { kind: 'rod', a: [2.4, 0.8, 0], b: [4.4, 3.6, 0], r: 1.1, skin: WOOD },
    { kind: 'rod', a: [4.6, 3.8, 0], b: [8.6, 7.4, 0], r: 0.7, skin: { base: '#2e4a1c', lit: '#6a9a3a', dark: '#12240c' } },
    { kind: 'rod', a: [8.6, 7.4, 0], b: [12.4, 11.8, 0], r: 0.6, skin: { base: '#2e4a1c', lit: '#6a9a3a', dark: '#12240c' } },
    { kind: 'ball', at: [6.2, 5.9, 0.6], r: 0.65, skin: { base: '#a8302a', lit: '#e86a5a', dark: '#4a1010' } }, { kind: 'ball', at: [9.6, 8.2, 0.6], r: 0.65, skin: { base: '#a8302a', lit: '#e86a5a', dark: '#4a1010' } }, { kind: 'ball', at: [11.2, 11.0, 0.6], r: 0.6, skin: { base: '#a8302a', lit: '#e86a5a', dark: '#4a1010' } },
    { kind: 'ball', at: [12.9, 12.6, 0.4], r: 1.0, skin: { base: '#a8302a', lit: '#e86a5a', dark: '#4a1010' } },
  ],
  shellcrown: () => [   // a ring of the old shell's ridge on a cord
    { kind: 'ball', at: [7.0, 7.4, 0], r: 4.0, ry: 3.2, rz: 1.0, skin: { base: '#3f5a3a', lit: '#7a9a70', dark: '#16281a' } },
    { kind: 'ball', at: [7.0, 7.4, 0.6], r: 2.2, ry: 1.7, rz: 1.2, skin: { base: '#1e1a20', lit: '#1e1a20' } },
    { kind: 'rod', a: [7.0, 2.6, 0], b: [7.0, 4.2, 0], r: 0.45, skin: { base: '#8a6b3a', dark: '#452a14' } },
  ],
  heartdraught: () => [   // a red draught in a bulb of glass
    { kind: 'ball', at: [7.0, 8.4, 0], r: 3.8, ry: 3.6, rz: 2.4, skin: { base: '#b83a30', lit: '#f08070', dark: '#4a1010' } },
    { kind: 'rod', a: [7.0, 2.4, 0], b: [7.0, 5.2, 0], r: 1.1, skin: { base: '#cfd8e0', lit: '#ffffff', dark: '#5a6470' } },
  ],
  tuskbroth: () => [   // a bowl of pale broth with the tusk's point showing
    { kind: 'ball', at: [7.0, 8.0, 0], r: 4.6, ry: 2.6, rz: 1.8, skin: { base: '#8a6b3a', lit: '#c49a5a', dark: '#452a14' } },
    { kind: 'ball', at: [7.0, 7.2, 1.4], r: 3.4, ry: 1.6, rz: 0.6, skin: { base: '#e6dcbc', lit: '#fff8e6' } },
  ],
  shellsoup: () => [   // a bowl of green soup in half a shell
    { kind: 'ball', at: [7.0, 8.0, 0], r: 4.6, ry: 2.6, rz: 1.8, skin: { base: '#3f5a3a', lit: '#7a9a70', dark: '#16281a' } },
    { kind: 'ball', at: [7.0, 7.2, 1.4], r: 3.4, ry: 1.6, rz: 0.6, skin: { base: '#7a9a4a', lit: '#b8d488' } },
  ],
  motelamp: () => [   // a mote in a bulb of glass on a short handle
    { kind: 'ball', at: [7.0, 6.4, 0], r: 3.4, ry: 3.6, rz: 2.4, skin: { base: '#9fd0ff', lit: '#ffffff', dark: '#3a6a9a' } },
    { kind: 'rod', a: [7.0, 9.8, 0], b: [7.0, 13.0, 0], r: 0.9, skin: { base: '#8a6b3a', lit: '#c49a5a', dark: '#452a14' } },
  ],
  mote: () => [   // a wisp's mote, a soft blue-white glow
    { kind: 'ball', at: [7.0, 7.0, 0], r: 3.0, skin: { base: '#9fd0ff', lit: '#ffffff', dark: '#3a6a9a' } },
    { kind: 'ball', at: [7.0, 7.0, 1.6], r: 1.4, skin: { base: '#ffffff', lit: '#ffffff' } },
  ],
  gloamink: () => [   // a dark drop with a violet sheen
    { kind: 'ball', at: [7.0, 8.0, 0], r: 3.4, ry: 4.0, rz: 2.0, skin: { base: '#2b1e3a', lit: '#6a4a8a', dark: '#0e0814' } },
    { kind: 'ball', at: [5.8, 6.2, 1.6], r: 0.9, skin: { base: '#b08ad0', lit: '#e8d0ff' } },
  ],
  cinderash: () => [   // a heap of grey ash with two live embers
    { kind: 'ball', at: [7.0, 8.0, 0], r: 4.4, ry: 2.8, rz: 1.6, skin: { base: '#6c6a66', lit: '#a8a5a0', dark: '#2c2a28' } },
    { kind: 'ball', at: [5.6, 7.0, 1.4], r: 0.8, skin: { base: '#ff7a1e', lit: '#ffd070' } }, { kind: 'ball', at: [8.6, 8.2, 1.4], r: 0.7, skin: { base: '#ff7a1e', lit: '#ffd070' } },
  ],
  eelfin: () => [   // an eel's fin, a dark green fan with a pale edge
    { kind: 'ball', at: [7.0, 7.4, 0], r: 4.0, ry: 2.4, rz: 0.7, skin: { base: '#2f5a4a', lit: '#6f9a88', dark: '#12281e' } },
    { kind: 'rod', a: [3.4, 6.0, 0.5], b: [10.6, 6.0, 0.5], r: 0.4, skin: { base: '#bfe0d0', lit: '#ffffff' } },
  ],
  shellmail: () => [   // three shells lapped over a leather vest
    { kind: 'ball', at: [7.0, 8.4, 0], r: 4.4, ry: 4.0, rz: 1.6, skin: { base: '#6b4a2e', lit: '#9a7048', dark: '#2e1c0e' } },
    { kind: 'ball', at: [5.2, 6.4, 1.2], r: 2.0, ry: 1.6, rz: 1.0, skin: { base: '#6b7a4a', lit: '#a9b787', dark: '#2e361c' } }, { kind: 'ball', at: [8.8, 6.4, 1.2], r: 2.0, ry: 1.6, rz: 1.0, skin: { base: '#6b7a4a', lit: '#a9b787', dark: '#2e361c' } },
    { kind: 'ball', at: [7.0, 9.4, 1.4], r: 2.0, ry: 1.6, rz: 1.0, skin: { base: '#6b7a4a', lit: '#a9b787', dark: '#2e361c' } },
  ],
  fincharm: () => [   // an eel fin on a cord
    { kind: 'ball', at: [7.0, 7.8, 0], r: 3.4, ry: 2.2, rz: 0.7, skin: { base: '#2f5a4a', lit: '#6f9a88', dark: '#12281e' } },
    { kind: 'rod', a: [7.0, 2.6, 0], b: [7.0, 5.0, 0], r: 0.45, skin: { base: '#8a6b3a', dark: '#452a14' } },
  ],
  ashpaste: () => [   // a grey paste in a leaf, an ember's warmth in it
    { kind: 'ball', at: [7.0, 7.6, 0], r: 4.2, ry: 2.8, rz: 1.4, skin: { base: '#7a9a4a', lit: '#b8d488', dark: '#2e4a1c' } },
    { kind: 'ball', at: [7.0, 7.2, 1.2], r: 2.6, ry: 1.6, rz: 1.0, skin: { base: '#8c8a86', lit: '#c8c5c0', dark: '#3c3a38' } },
  ],
  spore: () => [   // a pale puff, three spores on a stem
    { kind: 'ball', at: [5.6, 8.4, 0], r: 2.2, skin: { base: '#b9a98a', lit: '#eadfc4', dark: '#5a4a34' } }, { kind: 'ball', at: [8.6, 7.0, 0.3], r: 2.0, skin: { base: '#c4b394', lit: '#f0e6cc', dark: '#5a4a34' } }, { kind: 'ball', at: [7.0, 4.8, 0.6], r: 1.8, skin: { base: '#b9a98a', lit: '#eadfc4', dark: '#5a4a34' } },
  ],
  horn: () => [   // a charger's horn, curved, ivory to a dark tip
    { kind: 'rod', a: [4.0, 11.4, 0], b: [8.0, 6.0, 0], r: 1.4, skin: { base: '#d9cfae', lit: '#f6efd8', dark: '#6b5f42' } },
    { kind: 'rod', a: [8.0, 6.0, 0], b: [11.0, 2.8, 0], r: 0.8, skin: { base: '#5a4a34', lit: '#8a7a5a', dark: '#2a2016' } },
  ],
  meat: () => [   // a joint of raw meat: dark red, a pale knuckle of bone at one end (LS-519)
    { kind: 'ball', at: [7.2, 7.8, 0], r: 4.2, ry: 3.2, rz: 2.0, skin: { base: '#9a3a32', lit: '#cf6a5a', dark: '#4a1a16' } },
    { kind: 'rod', a: [10.4, 5.0, 0.6], b: [12.6, 3.2, 0.6], r: 0.9, skin: { base: '#e6dcbc', lit: '#fff8e6', dark: '#7a6a48' } },
  ],
  cookedmeat: () => [   // the same joint, browned at a fire
    { kind: 'ball', at: [7.2, 7.8, 0], r: 4.2, ry: 3.2, rz: 2.0, skin: { base: '#7a4a26', lit: '#b07a44', dark: '#3a2212' } },
    { kind: 'rod', a: [10.4, 5.0, 0.6], b: [12.6, 3.2, 0.6], r: 0.9, skin: { base: '#e6dcbc', lit: '#fff8e6', dark: '#7a6a48' } },
  ],
  sporecake: () => [   // a flat pale cake dusted with spores
    { kind: 'ball', at: [7.0, 7.6, 0], r: 4.4, ry: 3.0, rz: 1.4, skin: { base: '#c4b394', lit: '#f0e6cc', dark: '#5a4a34' } },
    { kind: 'ball', at: [5.4, 6.4, 1.4], r: 0.8, skin: { base: '#8a7a5a', lit: '#b9a98a' } }, { kind: 'ball', at: [8.6, 7.8, 1.4], r: 0.8, skin: { base: '#8a7a5a', lit: '#b9a98a' } },
  ],
  hornpike: () => [   // THE HORN PIKE: the pike's shaft with a charger's horn for a head -- ivory, curved, darkening to the tip
    { kind: 'rod', a: [0.7, 0.4, 0], b: [9.6, 9.2, 0], r: 0.8, skin: WOOD },
    { kind: 'rod', a: [9.2, 8.8, 0], b: [11.6, 11.8, 0], r: 1.0, skin: { base: '#e6dcbc', lit: '#fff8e6', dark: '#7a6a48' } },
    { kind: 'rod', a: [11.6, 11.8, 0], b: [13.2, 13.6, 0], r: 0.6, skin: { base: '#5a4a34', lit: '#8a7a5a', dark: '#2a2016' } },
  ],
  shellblade: () => [   // THE SHELL SAW: a stub of handle and a broad, curved, pale-green blade with teeth along its edge (grip bottom-left, head top-right, like every blade)
    { kind: 'rod', a: [3.2, 1.2, 0], b: [5.0, 4.0, 0], r: 1.15, skin: WOOD },
    { kind: 'rod', a: [5.2, 4.4, 0], b: [10.4, 10.8, 0], r: 1.9, skin: { base: '#8a9a62', lit: '#d0dcb0', dark: '#3a4a24' } },
    { kind: 'rod', a: [5.8, 4.0, 0.9], b: [10.6, 9.8, 0.9], r: 0.4, skin: { base: '#eef4dc' } },
    { kind: 'ball', at: [6.4, 6.6, 0.6], r: 0.7, skin: { base: '#eef4dc' } }, { kind: 'ball', at: [8.2, 8.6, 0.6], r: 0.7, skin: { base: '#eef4dc' } }, { kind: 'ball', at: [10.0, 10.6, 0.6], r: 0.7, skin: { base: '#eef4dc' } },
  ],
  spike: () => [   // THE CHITIN SPIKE: the pike's thin shaft corner to corner, and a long DARK point with no langet -- the silhouette is the pike's, the head is not steel
    { kind: 'rod', a: [0.7, 0.4, 0], b: [9.8, 9.4, 0], r: 0.75, skin: WOOD },
    { kind: 'rod', a: [9.4, 9.0, 0], b: [13.2, 13.2, 0], r: 0.7, skin: { base: '#4a4c55', lit: '#9a9ca6', dark: '#1a1b20' } },
    { kind: 'rod', a: [9.6, 9.6, 0.7], b: [12.6, 12.8, 0.7], r: 0.25, skin: { base: '#c8cad2' } },
  ],
  scalewhip: () => [   // THE WHIP: a short grip and a lash of amber scales curving up and right, each smaller than the last
    { kind: 'rod', a: [2.4, 0.8, 0], b: [4.4, 3.6, 0], r: 1.1, skin: WOOD },
    { kind: 'ball', at: [5.6, 4.8, 0], r: 1.25, skin: { base: '#d18f36', lit: '#f7cc66', dark: '#5a3410' } }, { kind: 'ball', at: [7.0, 6.4, 0.2], r: 1.15, skin: { base: '#d18f36', lit: '#f7cc66', dark: '#5a3410' } }, { kind: 'ball', at: [8.6, 7.8, 0.4], r: 1.05, skin: { base: '#d18f36', lit: '#f7cc66', dark: '#5a3410' } },
    { kind: 'ball', at: [10.2, 9.4, 0.6], r: 0.95, skin: { base: '#d18f36', lit: '#f7cc66', dark: '#5a3410' } }, { kind: 'ball', at: [11.6, 11.2, 0.8], r: 0.85, skin: { base: '#d18f36', lit: '#f7cc66', dark: '#5a3410' } }, { kind: 'ball', at: [12.8, 12.9, 1.0], r: 0.7, skin: { base: '#d18f36', lit: '#f7cc66', dark: '#5a3410' } },
  ],
  stonehammer: () => [   // THE HAMMER: the maul's haft with a squared grey stone for a head, not the maul's round iron
    { kind: 'rod', a: [5.0, 0.6, 0], b: [6.6, 9.4, 0], r: 1.1, skin: WOOD },
    { kind: 'box', at: [7.0, 11.0, 0], w: 6.4, h: 4.2, d: 3.2, skin: { base: '#7a7d86', lit: '#b9bcc4', dark: '#33353b' } },
    { kind: 'ball', at: [8.8, 12.2, 1.4], r: 0.8, skin: { base: '#d0d3da' } },
  ],
  scrap: () => [   // RUSTED plates of iron, folded over one another. Cool grey read as the whetstone and as the flint
                   // beside it -- three grey slabs in one row of the sheet -- so the iron is warm and eaten, which
                   // is also what scrap IS: what the shell left behind, months in the weather.
    { kind: 'ball', at: [5.6, 5.0, 0], r: 3.4, ry: 1.5, rz: 1.0, skin: { base: '#8a5a3a', lit: '#c99366', dark: '#3a2014' } },
    { kind: 'ball', at: [8.4, 7.2, 0.8], r: 2.8, ry: 1.3, rz: 0.9, skin: { base: '#9d6a42', lit: '#dba874', dark: '#43261a' } },
    { kind: 'ball', at: [6.2, 8.6, 0], r: 2.0, ry: 1.0, rz: 0.8, skin: { base: '#6f4a30', lit: '#a2764e', dark: '#2e190f' } },
    { kind: 'rod', a: [3.6, 6.4, 1.2], b: [9.8, 4.2, 1.2], r: 0.45, skin: { base: '#c9a06a', lit: '#f0d6a8' } },   // one edge still bright where it was beaten
  ],
  flint: () => [   // a knapped nodule: one pale conchoidal face and a dark rind
    { kind: 'ball', at: [6.8, 6.2, 0], r: 3.9, ry: 3.6, rz: 2.2, skin: { base: '#4e4a52', lit: '#7d7884', dark: '#211f26' } },
    { kind: 'ball', at: [7.8, 7.4, 1.5], r: 2.1, ry: 2.4, rz: 1.0, skin: { base: '#b9c2cb', lit: '#f2f6fb', dark: '#5c646e' } },
    { kind: 'ball', at: [4.6, 4.4, 0.6], r: 1.1, ry: 1.0, rz: 0.7, skin: { base: '#8f98a2' } },
  ],
  sinew: () => {   // a hank of cord, coiled: three turns and a loose end
    const out: M3.Solid[] = [];
    for (let k = 0; k < 3; k++) {
      const y = 4.2 + k * 2.3;
      for (let j = 0; j <= 6; j++) { const a = j / 6 * Math.PI; out.push({ kind: 'ball', at: [7 + Math.cos(a) * 4.0, y + Math.sin(a) * 0.9, 0], r: 0.75, skin: { base: '#c8b088', lit: '#f0e0c0', dark: '#6a5636' } }); }
    }
    out.push({ kind: 'rod', a: [10.6, 4.6, 0], b: [12.2, 2.2, 0], r: 0.6, skin: { base: '#c8b088', lit: '#f0e0c0', dark: '#6a5636' } });
    return out;
  },
  knife: () => [   // a short blade: a stub of handle, a straight edge, a bright ridge
    { kind: 'rod', a: [3.4, 1.4, 0], b: [5.2, 4.2, 0], r: 1.15, skin: WOOD },
    { kind: 'rod', a: [5.4, 4.6, 0], b: [10.2, 11.0, 0], r: 1.25, skin: STEEL },
    { kind: 'rod', a: [5.8, 4.2, 0.8], b: [10.0, 10.2, 0.8], r: 0.35, skin: { base: STEEL.lit } },
  ],
  billhook: () => {   // a haft with a hook on its head: the sickle's crescent, up a pole
    const out: M3.Solid[] = [{ kind: 'rod', a: [2.2, 0.8, 0], b: [7.2, 8.4, 0], r: 1.0, skin: WOOD }];
    const pts: M3.V3[] = [];
    for (let k = 0; k <= 5; k++) { const a = -0.7 + k * (2.5 / 5); pts.push([8.6 + 3.2 * Math.cos(a), 9.8 + 3.0 * Math.sin(a), 0]); }
    for (let k = 0; k < 5; k++) out.push({ kind: 'rod', a: pts[k], b: pts[k + 1], r: 1.05 - k * 0.12, skin: STEEL });
    return out;
  },
  maul: () => [   // a long haft and a head with weight in it: two balls, not a box
    { kind: 'rod', a: [5.0, 0.6, 0], b: [6.6, 9.4, 0], r: 1.1, skin: WOOD },
    { kind: 'ball', at: [6.8, 10.8, 0], r: 3.4, ry: 2.6, rz: 2.2, skin: { base: '#8f8a84', lit: '#cfc9c0', dark: '#3e3a36' } },
    { kind: 'ball', at: [8.6, 11.2, 0.6], r: 1.5, ry: 1.6, rz: 1.2, skin: { base: '#a9a49c', lit: '#e6e0d6' } },
  ],
  pike: () => [   // all reach, and it must not read as the spear: a thinner shaft corner to corner, a long narrow
                  // spike instead of a leaf, and a LANGET across the neck, which is the silhouette a pike has
    { kind: 'rod', a: [0.7, 0.4, 0], b: [10.6, 10.2, 0], r: 0.75, skin: WOOD },
    { kind: 'rod', a: [9.6, 9.2, 0], b: [12.8, 13.0, 0], r: 0.55, skin: STEEL },
    { kind: 'rod', a: [8.8, 10.4, 0], b: [11.0, 8.6, 0], r: 0.5, skin: { base: '#8f8a84', lit: '#cfc9c0', dark: '#3e3a36' } },
    { kind: 'ball', at: [12.9, 13.1, 0], r: 0.7, ry: 0.9, rz: 0.6, skin: { base: STEEL.lit } },
  ],
  firebrand: () => [   // it must not read as the torch either: the head is FORKED, two prongs with a flame on each,
                       // which is a different silhouette at fourteen pixels however alike the colour is
    { kind: 'rod', a: [6.6, 0.5, 0], b: [6.8, 5.2, 0], r: 1.4, skin: WOOD },
    { kind: 'rod', a: [6.8, 5.2, 0], b: [4.4, 9.0, 0], r: 0.85, skin: { base: '#3e2a1e', lit: '#6a4630', dark: '#1c0e06' } },
    { kind: 'rod', a: [6.8, 5.2, 0], b: [9.4, 9.0, 0], r: 0.85, skin: { base: '#3e2a1e', lit: '#6a4630', dark: '#1c0e06' } },
    { kind: 'ball', at: [4.2, 10.6, 0], r: 1.9, ry: 2.6, rz: 1.2, skin: { base: '#e2651c', lit: '#ffd06a', dark: '#6e1e04' } },
    { kind: 'ball', at: [9.6, 10.6, 0], r: 1.9, ry: 2.6, rz: 1.2, skin: { base: '#e2651c', lit: '#ffd06a', dark: '#6e1e04' } },
    { kind: 'ball', at: [4.2, 10.2, 1.0], r: 0.9, ry: 1.5, rz: 0.7, skin: { base: '#ffc850', lit: '#fff6dc' } },
    { kind: 'ball', at: [9.6, 10.2, 1.0], r: 0.9, ry: 1.5, rz: 0.7, skin: { base: '#ffc850', lit: '#fff6dc' } },
  ],
  shard: () => [   // a knapped spike bound at the grip: fast, and it will not last
    { kind: 'rod', a: [4.0, 1.0, 0], b: [5.6, 3.6, 0], r: 1.3, skin: { base: '#5a4a3a', lit: '#8e765c', dark: '#241a12' } },
    { kind: 'rod', a: [5.6, 3.8, 0], b: [8.6, 11.6, 0], r: 1.4, skin: { base: '#9fb0bd', lit: '#eef6ff', dark: '#46525f' } },
    { kind: 'rod', a: [6.2, 5.2, 0.9], b: [8.2, 10.4, 0.9], r: 0.4, skin: { base: '#ffffff' } },
    { kind: 'ball', at: [7.2, 7.6, -0.6], r: 1.0, ry: 1.4, rz: 0.6, skin: { base: '#7e8d99', dark: '#39424b' } },
  ],
  club: () => [   // one piece of wood, thin at the grip and heavy at the end: nothing to break
    { kind: 'rod', a: [3.6, 1.0, 0], b: [6.4, 6.0, 0], r: 1.15, skin: WOOD },
    { kind: 'rod', a: [6.4, 6.0, 0], b: [9.8, 11.4, 0], r: 2.2, skin: { base: '#7a5c30', lit: '#c49a54', dark: '#3a2410' } },
    { kind: 'ball', at: [10.2, 12.0, 0], r: 1.6, ry: 1.4, rz: 1.3, skin: { base: '#6e5228', lit: '#b08c48', dark: '#33200c' } },
  ],
  harpoon: () => [   // a spear with barbs: the shaft, the head, and two hooks turned back down it
    { kind: 'rod', a: [1.8, 0.8, 0], b: [10.0, 9.4, 0], r: 0.9, skin: WOOD },
    { kind: 'ball', at: [11.6, 11.2, 0], r: 1.2, ry: 2.2, rz: 0.9, skin: STEEL },
    { kind: 'rod', a: [10.6, 10.0, 0], b: [8.6, 10.8, 0], r: 0.5, skin: STEEL },
    { kind: 'rod', a: [10.2, 9.4, 0], b: [10.8, 7.4, 0], r: 0.5, skin: STEEL },
  ],
  sickle: () => {
    // a short handle held down and to the left, and a thin crescent opening up and left over it
    const out: M3.Solid[] = [{ kind: 'rod', a: [2.8, 0.9, 0.8], b: [5.6, 5.4, 0.2], r: 1.25, skin: WOOD }];
    const pts: M3.V3[] = [];
    for (let k = 0; k <= 7; k++) { const a = -0.95 + k * (3.5 / 7); pts.push([7.0 + 4.3 * Math.cos(a), 7.6 + 4.1 * Math.sin(a), 0]); }
    for (let k = 0; k < 7; k++) out.push({ kind: 'rod', a: pts[k], b: pts[k + 1], r: 1.15 - k * 0.11, skin: STEEL });
    return out;
  },
  spear: () => [   // shaft from the bottom left to the top right, a leaf head ON THE TOP END, bound with cord
    { kind: 'rod', a: [1.6, 1.2, 1], b: [10.4, 9.8, -0.4], r: 0.9, skin: WOOD },
    { kind: 'rod', a: [9.4, 8.8, 0], b: [10.6, 10.0, 0], r: 1.1, skin: { base: '#4a3226', lit: '#7a5238', dark: '#241208' } },
    { kind: 'ball', at: [11.8, 11.4, 0], r: 1.35, ry: 2.5, rz: 0.9, skin: STEEL },
    { kind: 'rod', a: [11.0, 9.8, 0.7], b: [12.6, 12.8, 0.7], r: 0.35, skin: { base: STEEL.lit } },   // the blade's ridge, catching the light
  ],
  axe: () => [   // a wedge head AT THE TOP of the haft, its bit facing right: halfway up it read as a flag
    { kind: 'rod', a: [4.4, 0.6, 0], b: [5.4, 12.0, 0], r: 1.05, skin: WOOD },
    /* THE HEAD IS A BEARDED WEDGE, WIDE AT THE TOP AND TAPERING DOWN, hung on the top of the haft. Three boxes of
     * one height beside a stick read as a flag; the taper and the pale cutting edge are what say axe at fourteen
     * pixels (the reference icon sheet, 2026-09-05). */
    { kind: 'box', at: [8.2, 10.8, 0], w: 5.0, h: 2.4, d: 2.2, skin: STEEL },
    { kind: 'box', at: [7.4, 8.8, 0], w: 3.4, h: 2.2, d: 2.1, skin: STEEL },
    { kind: 'box', at: [10.6, 10.6, 0], w: 1.4, h: 3.0, d: 1.8, skin: { base: '#dfe4ec', lit: '#ffffff', dark: '#7d8290' } },
    { kind: 'rod', a: [4.8, 8.8, 1.1], b: [5.4, 11.4, 1.1], r: 0.55, skin: { base: '#4a3226', lit: '#7a5238', dark: '#241208' } },   // the binding under the head
  ],
  torch: () => [   // a brand with a real flame: a teardrop with a bright heart and a wisp off the top
    { kind: 'rod', a: [7, 0.6, 0], b: [7, 6.0, 0], r: 1.15, skin: WOOD },
    { kind: 'ball', at: [7, 6.6, 0], r: 1.9, ry: 1.2, rz: 1.5, skin: { base: '#4a3226', lit: '#7a5238', dark: '#241208' } },
    { kind: 'ball', at: [7, 9.4, 0], r: 2.4, ry: 3.2, rz: 1.4, skin: { base: '#e07a2a', lit: '#ffd88a', dark: '#7a2a08' } },
    { kind: 'ball', at: [7, 9.0, 1.0], r: 1.15, ry: 2.0, rz: 0.8, skin: { base: '#ffd070', lit: '#fff2cf' } },
    { kind: 'ball', at: [7.4, 12.4, 0], r: 0.7, ry: 1.1, rz: 0.6, skin: { base: '#ffb85c', lit: '#ffe08a' } },
  ],
  hide: () => [   // the sow's hide: a jerkin, not a pelt-shaped blob -- shoulders, a dark spine, a belt
    { kind: 'ball', at: [7, 5.8, 0], r: 4.4, ry: 4.6, rz: 1.5, skin: { base: '#7a4b2e', lit: '#c08a56', dark: '#361a08' } },
    { kind: 'ball', at: [3.4, 8.4, 0.2], r: 1.9, ry: 2.2, rz: 1.1, skin: { base: '#6a3f26', lit: '#8f5c38', dark: '#42260f' } },
    { kind: 'ball', at: [10.6, 8.4, 0.2], r: 1.9, ry: 2.2, rz: 1.1, skin: { base: '#6a3f26', lit: '#8f5c38', dark: '#42260f' } },
    { kind: 'ball', at: [7, 10.2, 0.6], r: 1.8, ry: 1.2, rz: 1.2, skin: { base: '#8f5c38', lit: '#b07a4a', dark: '#4a2c1a' } },
    { kind: 'rod', a: [7, 2.4, 1.3], b: [7, 9.4, 1.3], r: 0.5, skin: { base: '#4a2c1a', lit: '#5c3a24', dark: '#2e1a10' } },
  ],
  plate: () => [   // the old shell's plate as a BREASTPLATE (round 52's asset pass): a squared steel front with a waist, a low centre ridge, two shoulder bosses, three rivets
    { kind: 'box', at: [7, 6.6, 0], w: 7.6, h: 7.4, d: 2.0, skin: { base: '#8c9097', lit: '#dfe5ec', dark: '#2a2f38' } },
    { kind: 'box', at: [7, 3.2, 0.4], w: 5.2, h: 2.0, d: 2.0, skin: { base: '#7d8188', lit: '#c9ced6', dark: '#2a2f38' } },
    { kind: 'rod', a: [7, 3.4, 2.1], b: [7, 9.2, 2.1], r: 0.45, skin: { base: '#5a6068', dark: '#2a2f38' } },
    { kind: 'ball', at: [3.6, 10.2, 1.2], r: 1.6, ry: 1.2, rz: 1.1, skin: { base: '#6a6f78', lit: '#c9ced6', dark: '#2a2f38' } },
    { kind: 'ball', at: [10.4, 10.2, 1.2], r: 1.6, ry: 1.2, rz: 1.1, skin: { base: '#6a6f78', lit: '#c9ced6', dark: '#2a2f38' } },
    { kind: 'ball', at: [4.6, 7.4, 2.2], r: 0.5, skin: { base: '#f0f4f8' } },
    { kind: 'ball', at: [9.4, 7.4, 2.2], r: 0.5, skin: { base: '#f0f4f8' } },
    { kind: 'ball', at: [7, 11.2, 2.0], r: 0.6, skin: { base: '#f0f4f8' } },
  ],
  mantle: () => [   // the bramble mother's mantle as a hooded CLOAK (round 52's asset pass): the hood at the top, the cloth falling wider to the hem, a red bloom at the clasp, thorns along the hem
    { kind: 'ball', at: [7, 11.0, 0], r: 2.6, ry: 2.2, rz: 1.8, skin: { base: '#4f6b2f', lit: '#9cc466', dark: '#1a2a10' } },
    { kind: 'box', at: [7, 6.2, 0], w: 6.0, h: 5.6, d: 1.6, skin: { base: '#3f5a26', lit: '#8fb45a', dark: '#141f0a' } },
    { kind: 'box', at: [7, 2.8, 0], w: 9.0, h: 2.6, d: 1.6, skin: { base: '#36501f', lit: '#7fa04a', dark: '#141f0a' } },
    { kind: 'ball', at: [7, 8.6, 1.7], r: 0.95, skin: { base: '#b8323a', lit: '#f07a70' } },
    { kind: 'rod', a: [3.0, 1.8, 1.0], b: [2.0, 0.2, 1.2], r: 0.45, skin: { base: '#2f4520', dark: '#141f0a' } },
    { kind: 'rod', a: [7, 1.6, 1.0], b: [7, 0.0, 1.2], r: 0.45, skin: { base: '#2f4520', dark: '#141f0a' } },
    { kind: 'rod', a: [11.0, 1.8, 1.0], b: [12.0, 0.2, 1.2], r: 0.45, skin: { base: '#2f4520', dark: '#141f0a' } },
  ],
  treads: () => [   // BOOTS, y up: two tall shafts with a fold at the top, dark leather, pale soles under, toes forward
    { kind: 'box', at: [4.2, 7.6, 0], w: 3.4, h: 7.2, d: 2.6, skin: { base: '#6e4a2a', lit: '#b8844e', dark: '#2c1808' } },
    { kind: 'box', at: [9.8, 7.6, 0], w: 3.4, h: 7.2, d: 2.6, skin: { base: '#6e4a2a', lit: '#b8844e', dark: '#2c1808' } },
    { kind: 'box', at: [4.2, 11.6, 0.4], w: 4.0, h: 1.4, d: 2.8, skin: { base: '#8a6238', lit: '#d0a066', dark: '#3a2410' } },
    { kind: 'box', at: [9.8, 11.6, 0.4], w: 4.0, h: 1.4, d: 2.8, skin: { base: '#8a6238', lit: '#d0a066', dark: '#3a2410' } },
    { kind: 'box', at: [4.8, 2.6, 0.4], w: 5.2, h: 1.6, d: 3.0, skin: { base: '#c9b07a', lit: '#eadca8', dark: '#6a5a34' } },
    { kind: 'box', at: [10.4, 2.6, 0.4], w: 5.2, h: 1.6, d: 3.0, skin: { base: '#c9b07a', lit: '#eadca8', dark: '#6a5a34' } },
    { kind: 'rod', a: [2.8, 8.6, 2.7], b: [5.6, 8.6, 2.7], r: 0.35, skin: { base: '#2c1808' } },
    { kind: 'rod', a: [8.4, 8.6, 2.7], b: [11.2, 8.6, 2.7], r: 0.35, skin: { base: '#2c1808' } },
  ],
  bell: () => [   // the wisp bell: a loop, a flared skirt, a clapper under it
    { kind: 'rod', a: [5.8, 11.6, 0], b: [7.0, 12.8, 0], r: 0.45, skin: { base: '#8a7a3a', lit: '#b8a860', dark: '#5a4e26' } },
    { kind: 'rod', a: [7.0, 12.8, 0], b: [8.2, 11.6, 0], r: 0.45, skin: { base: '#8a7a3a', lit: '#b8a860', dark: '#5a4e26' } },
    { kind: 'ball', at: [7, 8.8, 0], r: 2.2, ry: 2.8, rz: 2.0, skin: { base: '#d9c27a', lit: '#fff3c8', dark: '#8a7a3a' } },
    { kind: 'ball', at: [7, 5.2, 0], r: 4.4, ry: 2.4, rz: 3.0, skin: { base: '#d9c27a', lit: '#fffce8', dark: '#6a5a26' } },
    { kind: 'rod', a: [2.8, 4.0, 1.0], b: [11.2, 4.0, 1.0], r: 0.6, skin: { base: '#a89050', lit: '#e8d69a', dark: '#5a4e26' } },
    { kind: 'ball', at: [7, 2.4, 0.4], r: 1.0, ry: 1.2, rz: 0.9, skin: { base: '#5a4e26', lit: '#8a7a3a', dark: '#3a3018' } },
  ],
  /* THE FOUR CHARMS OF LS-335. Each reads at a glance as the thing it does:
   * bone for the ward, a honed edge for the hone, a shell and its pin for the
   * clasp, a lump with a live core for the coalheart. They share the charm
   * slot with the wisp bell, so they are drawn to the same weight as it. */
  ward: () => [   // the bone ward: a ring of bone beads on a thong, a dark stone bead held in the middle
    { kind: 'rod', a: [5.6, 12.2, 0], b: [7, 13.4, 0], r: 0.5, skin: { base: '#8a6b3a', lit: '#d8ac62', dark: '#452a14' } },
    { kind: 'rod', a: [8.4, 12.2, 0], b: [7, 13.4, 0], r: 0.5, skin: { base: '#8a6b3a', lit: '#d8ac62', dark: '#452a14' } },
    { kind: 'ball', at: [7, 11.0, 0], r: 1.25, ry: 1.25, rz: 1.1, skin: { base: '#c9b79a', lit: '#f6ecd6', dark: '#5c4b43' } },
    { kind: 'ball', at: [3.6, 8.6, 0], r: 1.25, ry: 1.25, rz: 1.1, skin: { base: '#b19b7f', lit: '#e6d6b8', dark: '#5c4b43' } },
    { kind: 'ball', at: [10.4, 8.6, 0], r: 1.25, ry: 1.25, rz: 1.1, skin: { base: '#c9b79a', lit: '#f6ecd6', dark: '#5c4b43' } },
    { kind: 'ball', at: [3.6, 4.8, 0], r: 1.25, ry: 1.25, rz: 1.1, skin: { base: '#a18b6f', lit: '#d6c6a8', dark: '#4c3b33' } },
    { kind: 'ball', at: [10.4, 4.8, 0], r: 1.25, ry: 1.25, rz: 1.1, skin: { base: '#b19b7f', lit: '#e6d6b8', dark: '#5c4b43' } },
    { kind: 'ball', at: [7, 2.6, 0], r: 1.25, ry: 1.25, rz: 1.1, skin: { base: '#c9b79a', lit: '#f6ecd6', dark: '#5c4b43' } },
    { kind: 'ball', at: [7, 6.8, 1.4], r: 2.0, ry: 2.0, rz: 1.6, skin: { base: '#38343f', lit: '#8f8a9c', dark: '#14121a' } },
  ],
  hone: () => [   // the hone charm: a pale grit stone, leather round its waist, one edge honed white
    { kind: 'rod', a: [5.8, 11.8, 0], b: [7, 13.0, 0], r: 0.5, skin: { base: '#8a6b3a', lit: '#d8ac62', dark: '#452a14' } },
    { kind: 'rod', a: [8.2, 11.8, 0], b: [7, 13.0, 0], r: 0.5, skin: { base: '#8a6b3a', lit: '#d8ac62', dark: '#452a14' } },
    { kind: 'ball', at: [7, 6.6, 0], r: 3.1, ry: 4.6, rz: 2.0, skin: { base: '#9a959d', lit: '#eae5da', dark: '#38343f' } },
    { kind: 'rod', a: [4.0, 6.0, 1.8], b: [10.0, 6.0, 1.8], r: 1.0, skin: { base: '#7a5a34', lit: '#bc9058', dark: '#38240e' } },
    { kind: 'rod', a: [9.3, 3.2, 1.3], b: [9.3, 10.4, 1.3], r: 0.55, skin: { base: '#e4e9ee', lit: '#ffffff', dark: '#9099a0' } },
  ],
  clasp: () => [   // the shell clasp: a fan of pale shell hinged at the foot, a bronze pin driven across it
    { kind: 'ball', at: [7, 7.8, 0], r: 5.4, ry: 4.6, rz: 2.0, skin: { base: '#d8c9a8', lit: '#f8f0dc', dark: '#6a5a42' } },
    { kind: 'rod', a: [7, 3.8, 2.0], b: [3.6, 10.6, 0.9], r: 0.5, skin: { base: '#7d6b50', dark: '#4a3d2c' } },
    { kind: 'rod', a: [7, 3.8, 2.2], b: [7, 11.6, 1.0], r: 0.5, skin: { base: '#7d6b50', dark: '#4a3d2c' } },
    { kind: 'rod', a: [7, 3.8, 2.0], b: [10.4, 10.6, 0.9], r: 0.5, skin: { base: '#7d6b50', dark: '#4a3d2c' } },
    { kind: 'ball', at: [7, 3.4, 1.4], r: 1.6, ry: 1.3, rz: 1.3, skin: { base: '#c2ab88', lit: '#eee2c6', dark: '#6a5a44' } },
    { kind: 'rod', a: [2.8, 9.0, 2.4], b: [11.2, 5.8, 2.4], r: 0.8, skin: { base: '#9e6d48', lit: '#efc887', dark: '#5a3a28' } },
    { kind: 'ball', at: [11.4, 5.6, 2.6], r: 1.2, ry: 1.1, rz: 1.05, skin: { base: '#c9945b', lit: '#f6dcaa', dark: '#5a3a28' } },
  ],
  coal: () => [   // the coalheart: a burnt lump, live in the middle, two sparks off it
    { kind: 'ball', at: [7, 7.2, 0], r: 4.8, ry: 4.4, rz: 3.0, skin: { base: '#2e2622', lit: '#5c4c42', dark: '#14100e' } },
    { kind: 'ball', at: [6.2, 8.0, 1.8], r: 2.4, ry: 2.2, rz: 1.6, skin: { base: '#8c3312', lit: '#e0702a', dark: '#3a1408' } },
    { kind: 'ball', at: [6.6, 7.6, 2.8], r: 1.2, ry: 1.1, rz: 0.9, skin: { base: '#e0702a', lit: '#ffd27a', dark: '#8c3312' } },
    { kind: 'ball', at: [10.2, 4.4, 1.2], r: 0.8, ry: 0.8, rz: 0.7, skin: { base: '#c8541e', lit: '#ffd27a' } },
    { kind: 'ball', at: [4.0, 3.6, 1.0], r: 0.6, ry: 0.6, rz: 0.5, skin: { base: '#8c3312', lit: '#e0702a' } },
  ],
  fins: () => [   // REED FINS, y up: two long blades widening downward with a dark midrib, straps at the top
    { kind: 'box', at: [4.2, 6.2, 0], w: 3.2, h: 9.0, d: 1.2, skin: { base: '#5f8a52', lit: '#a8d68c', dark: '#1e3418' } },
    { kind: 'box', at: [9.8, 6.2, 0], w: 3.2, h: 9.0, d: 1.2, skin: { base: '#547b49', lit: '#98c67c', dark: '#1e3418' } },
    { kind: 'rod', a: [4.2, 1.8, 1.3], b: [4.2, 10.4, 1.3], r: 0.4, skin: { base: '#2e4a26', dark: '#16261a' } },
    { kind: 'rod', a: [9.8, 1.8, 1.3], b: [9.8, 10.4, 1.3], r: 0.4, skin: { base: '#2e4a26', dark: '#16261a' } },
    { kind: 'box', at: [4.2, 11.4, 0.2], w: 4.2, h: 1.6, d: 2.0, skin: { base: '#8a6b3a', lit: '#d8ac62', dark: '#452a14' } },
    { kind: 'box', at: [9.8, 11.4, 0.2], w: 4.2, h: 1.6, d: 2.0, skin: { base: '#8a6b3a', lit: '#d8ac62', dark: '#452a14' } },
  ],
  soles: () => [   // ash soles as SANDALS, y up: two thick pale soles seen from above, a dark strap across the toe and one over the instep
    { kind: 'box', at: [4.2, 6.6, 0], w: 4.2, h: 9.0, d: 1.4, skin: { base: '#b9b4ab', lit: '#efeade', dark: '#4e4a44' } },
    { kind: 'box', at: [9.8, 6.6, 0], w: 4.2, h: 9.0, d: 1.4, skin: { base: '#aea9a0', lit: '#e4dfd3', dark: '#4e4a44' } },
    { kind: 'rod', a: [2.2, 9.2, 1.6], b: [6.2, 9.2, 1.6], r: 0.55, skin: { base: '#4a3c2e', lit: '#7a6650', dark: '#241c14' } },
    { kind: 'rod', a: [7.8, 9.2, 1.6], b: [11.8, 9.2, 1.6], r: 0.55, skin: { base: '#4a3c2e', lit: '#7a6650', dark: '#241c14' } },
    { kind: 'rod', a: [2.4, 5.2, 1.6], b: [6.0, 6.6, 1.6], r: 0.5, skin: { base: '#4a3c2e', lit: '#7a6650', dark: '#241c14' } },
    { kind: 'rod', a: [8.0, 6.6, 1.6], b: [11.6, 5.2, 1.6], r: 0.5, skin: { base: '#4a3c2e', lit: '#7a6650', dark: '#241c14' } },
  ],
  oilskin: () => [   // OILSKIN, y up: a hooded coat hanging, the hood at the top, a bright wet sheen down one side, a dark hem
    { kind: 'ball', at: [7, 11.2, 0], r: 2.4, ry: 2.0, rz: 1.8, skin: { base: '#6d5a2e', lit: '#c8a94e', dark: '#221c0a' } },
    { kind: 'box', at: [7, 6.4, 0], w: 7.4, h: 7.6, d: 1.8, skin: { base: '#6d5a2e', lit: '#c8a94e', dark: '#221c0a' } },
    { kind: 'box', at: [7, 2.2, 0], w: 8.6, h: 1.8, d: 1.8, skin: { base: '#4e4020', lit: '#8a7436', dark: '#221c0a' } },
    { kind: 'rod', a: [4.4, 9.6, 2.0], b: [4.4, 3.2, 2.0], r: 0.5, skin: { base: '#f2e2a0', lit: '#fff8d8' } },
    { kind: 'rod', a: [7, 9.6, 2.0], b: [7, 3.6, 2.0], r: 0.35, skin: { base: '#3d3216', dark: '#221c0a' } },
  ],
  tusk: () => [   // the tusk charm: one curved tusk, thick at the thong and tapering to a point
    { kind: 'rod', a: [5.6, 12.2, 0], b: [7.2, 13.2, 0], r: 0.45, skin: { base: '#8a6b3a', lit: '#d8ac62', dark: '#452a14' } },
    { kind: 'rod', a: [8.8, 12.2, 0], b: [7.2, 13.2, 0], r: 0.45, skin: { base: '#8a6b3a', lit: '#d8ac62', dark: '#452a14' } },
    { kind: 'ball', at: [7.2, 10.4, 0], r: 1.9, ry: 1.6, rz: 1.5, skin: { base: '#d8cbae', lit: '#fbf4e2', dark: '#7a6f58' } },
    { kind: 'ball', at: [6.0, 7.6, 0], r: 1.7, ry: 1.9, rz: 1.4, skin: { base: '#e2d6bd', lit: '#fdf8ea', dark: '#847a62' } },
    { kind: 'ball', at: [5.4, 4.8, 0], r: 1.25, ry: 1.7, rz: 1.1, skin: { base: '#d8cbae', lit: '#f8f1de', dark: '#7a6f58' } },
    { kind: 'ball', at: [6.4, 2.6, 0], r: 0.8, ry: 1.2, rz: 0.7, skin: { base: '#c9bb9c', lit: '#eee6d2', dark: '#6e6450' } },
    { kind: 'ball', at: [7.6, 1.2, 0], r: 0.45, ry: 0.6, rz: 0.4, skin: { base: '#bdae8e', lit: '#e2d8c2' } },
  ],
  quill: () => [   // the quill charm: three spines bound at the foot, dark banded on pale
    { kind: 'rod', a: [7, 2.6, 0], b: [4.2, 12.4, 0], r: 0.55, skin: { base: '#d8cdb4', lit: '#fbf6e8', dark: '#6a6050' } },
    { kind: 'rod', a: [7, 2.6, 0.4], b: [7.2, 12.8, 0.4], r: 0.6, skin: { base: '#e4dcc6', lit: '#fffbf0', dark: '#7a7060' } },
    { kind: 'rod', a: [7, 2.6, 0], b: [10.2, 12.4, 0], r: 0.55, skin: { base: '#d8cdb4', lit: '#fbf6e8', dark: '#6a6050' } },
    { kind: 'rod', a: [5.4, 8.0, 0.8], b: [8.8, 8.0, 0.8], r: 0.5, skin: { base: '#2e2622', lit: '#5c4c42', dark: '#14100e' } },
    { kind: 'ball', at: [7, 2.6, 0.6], r: 1.5, ry: 1.2, rz: 1.2, skin: { base: '#7a5a34', lit: '#bc9058', dark: '#38240e' } },
  ],
  hare: () => [   // the hare foot: a small foot on a thong, three toes and a tuft
    { kind: 'rod', a: [5.8, 12.4, 0], b: [7, 13.4, 0], r: 0.45, skin: { base: '#8a6b3a', lit: '#d8ac62', dark: '#452a14' } },
    { kind: 'rod', a: [8.2, 12.4, 0], b: [7, 13.4, 0], r: 0.45, skin: { base: '#8a6b3a', lit: '#d8ac62', dark: '#452a14' } },
    { kind: 'ball', at: [7, 10.4, 0], r: 1.7, ry: 1.5, rz: 1.3, skin: { base: '#f0eade', lit: '#ffffff', dark: '#9a9286' } },
    { kind: 'rod', a: [7, 9.6, 0.4], b: [6.4, 5.0, 0.4], r: 1.5, skin: { base: '#b8a68e', lit: '#eee4d2', dark: '#5e5446' } },
    { kind: 'ball', at: [5.0, 3.4, 0.4], r: 1.15, ry: 1.5, rz: 1.0, skin: { base: '#c9b79a', lit: '#f2e8d6', dark: '#6a6050' } },
    { kind: 'ball', at: [7.0, 2.8, 0.4], r: 1.15, ry: 1.6, rz: 1.0, skin: { base: '#d8c9ac', lit: '#f8efdd', dark: '#6a6050' } },
    { kind: 'ball', at: [9.0, 3.6, 0.4], r: 1.05, ry: 1.4, rz: 0.9, skin: { base: '#c9b79a', lit: '#f2e8d6', dark: '#6a6050' } },
  ],
  spoon: () => [   // the horn spoon, y up: a long dark handle from the bottom-right, a wide pale bowl at the top-left with a shadowed hollow
    { kind: 'rod', a: [10.6, 1.4, 0], b: [6.6, 8.2, 0.4], r: 1.0, skin: { base: '#3e3228', lit: '#8a7460', dark: '#160f0a' } },
    { kind: 'ball', at: [5.0, 9.8, 0], r: 3.6, ry: 3.0, rz: 1.4, skin: { base: '#d6c8a8', lit: '#fbf5e2', dark: '#5e5240' } },
    { kind: 'ball', at: [5.2, 10.0, 1.3], r: 2.3, ry: 1.8, rz: 0.8, skin: { base: '#8a7f68', lit: '#b8ab90', dark: '#403828' } },
    { kind: 'ball', at: [10.9, 1.2, 0.2], r: 1.1, skin: { base: '#5c4a38', lit: '#9a8064', dark: '#2a2018' } },
  ],
  moth: () => [   // MOTH, y up: two broad pale wings held wide, a dark body down the middle, two feelers at the top, an eyespot on each wing
    { kind: 'ball', at: [3.8, 6.6, 0], r: 3.2, ry: 4.2, rz: 0.9, skin: { base: '#b8a98e', lit: '#efe5d0', dark: '#4e463a' } },
    { kind: 'ball', at: [10.2, 6.6, 0], r: 3.2, ry: 4.2, rz: 0.9, skin: { base: '#ab9c82', lit: '#e6dcc6', dark: '#463f34' } },
    { kind: 'ball', at: [3.8, 7.2, 1.0], r: 0.9, skin: { base: '#5a4e42', lit: '#8a7c6c' } },
    { kind: 'ball', at: [10.2, 7.2, 1.0], r: 0.9, skin: { base: '#5a4e42', lit: '#8a7c6c' } },
    { kind: 'rod', a: [7, 2.4, 1.2], b: [7, 10.8, 1.2], r: 1.0, skin: { base: '#3a332c', lit: '#6e6458', dark: '#1a1712' } },
    { kind: 'rod', a: [7, 10.8, 1.2], b: [5.0, 13.2, 1.2], r: 0.4, skin: { base: '#3a332c', lit: '#6e6458' } },
    { kind: 'rod', a: [7, 10.8, 1.2], b: [9.0, 13.2, 1.2], r: 0.4, skin: { base: '#3a332c', lit: '#6e6458' } },
  ],
  pearl: () => [   // the silt pearl: a pale bead in a half shell, still wet
    { kind: 'ball', at: [7, 5.4, 0], r: 5.0, ry: 3.4, rz: 1.6, skin: { base: '#6f7f86', lit: '#c3d2d6', dark: '#2c3a40' } },
    { kind: 'rod', a: [2.6, 5.0, 1.4], b: [11.4, 5.0, 1.4], r: 0.45, skin: { base: '#54666e', dark: '#28363c' } },
    { kind: 'rod', a: [7, 2.2, 1.6], b: [3.8, 7.6, 0.8], r: 0.4, skin: { base: '#54666e', dark: '#28363c' } },
    { kind: 'rod', a: [7, 2.2, 1.6], b: [10.2, 7.6, 0.8], r: 0.4, skin: { base: '#54666e', dark: '#28363c' } },
    { kind: 'ball', at: [7, 7.4, 2.0], r: 2.6, ry: 2.6, rz: 2.2, skin: { base: '#dfe8ec', lit: '#ffffff', dark: '#8fa3ab' } },
    { kind: 'ball', at: [6.2, 8.4, 3.4], r: 0.7, ry: 0.7, rz: 0.6, skin: { base: '#ffffff', lit: '#ffffff' } },
  ],
  pod: () => [   // the rattle pod: it HANGS -- a loop above, a ribbed pod below
    { kind: 'rod', a: [6.4, 12.2, 0], b: [7.6, 12.2, 0], r: 0.5, skin: WOOD },
    { kind: 'rod', a: [7, 11.8, 0], b: [7, 10.2, 0], r: 0.6, skin: WOOD },
    { kind: 'ball', at: [7, 6.4, 0], r: 3.5, ry: 4.2, rz: 3.0, skin: { base: '#6f8f4a', lit: '#b4d47e', dark: '#2c4418' } },
    { kind: 'rod', a: [5.2, 8.6, 2.2], b: [5.6, 3.4, 1.6], r: 0.4, skin: { base: '#3f5a26' } },
    { kind: 'rod', a: [8.8, 8.6, 2.2], b: [8.4, 3.4, 1.6], r: 0.4, skin: { base: '#3f5a26' } },
  ],
  tide: () => [   // the silt lung as a SPIRAL SHELL, y up: whorls shrinking toward the top-right, a dark mouth at the bottom-left, the pearl in it
    { kind: 'ball', at: [6.4, 6.0, 0], r: 5.0, ry: 4.6, rz: 2.8, skin: { base: '#8fc4cf', lit: '#f0fdff', dark: '#1a3640' } },
    { kind: 'ball', at: [8.6, 8.4, 1.6], r: 3.2, ry: 3.0, rz: 2.0, skin: { base: '#79b3bf', lit: '#e6f8fa', dark: '#1a3640' } },
    { kind: 'ball', at: [10.2, 10.2, 2.8], r: 1.9, ry: 1.8, rz: 1.4, skin: { base: '#66a2af', lit: '#d4eef2', dark: '#1a3640' } },
    { kind: 'ball', at: [11.2, 11.4, 3.6], r: 1.0, skin: { base: '#5a92a0', lit: '#c8e6ec', dark: '#1a3640' } },
    { kind: 'ball', at: [3.6, 3.8, 2.0], r: 2.4, ry: 2.2, rz: 1.4, skin: { base: '#1e3a44', lit: '#3d6470', dark: '#0e1c22' } },
    { kind: 'ball', at: [3.8, 3.8, 3.2], r: 1.0, skin: { base: '#f0f8fc', lit: '#ffffff', dark: '#8fc4cf' } },
  ],
  ember: () => [   // the emberwake: a flame that curls, drawn inside the cell
    { kind: 'ball', at: [7, 5.6, 0], r: 3.6, ry: 4.4, rz: 3.0, skin: { base: '#c8642a', lit: '#ffd88a', dark: '#4a1a08' } },
    { kind: 'ball', at: [7, 5.0, 1.8], r: 1.7, ry: 2.2, rz: 1.2, skin: { base: '#ffd070', lit: '#fff2cf' } },
    { kind: 'ball', at: [8.4, 10.4, 0], r: 1.0, ry: 1.6, rz: 0.9, skin: { base: '#e07a2a', lit: '#ffb85c' } },
    { kind: 'ball', at: [5.4, 11.6, 0], r: 0.6, skin: { base: '#ffd070' } },
  ],
  rivet: () => [   // a shell rivet: a domed stud with a square shank, warmer than the whetstone's grey
    { kind: 'ball', at: [7, 6.2, 1.2], r: 2.4, ry: 2.2, rz: 1.6, skin: { base: '#b08a52', lit: '#e8c88e', dark: '#5a4020' } },
    { kind: 'rod', a: [7, 8.2, 0.4], b: [7, 11.0, 0.4], r: 0.9, skin: { base: '#8a6a3c', dark: '#4a3418' } },
    { kind: 'ball', at: [6.2, 5.4, 2.4], r: 0.7, skin: { base: '#f2dcae' } },
  ],
  whetstone: () => [   // a flat river stone, darker than glim and matt where glim glitters
    { kind: 'ball', at: [7, 7.4, 0], r: 3.4, ry: 2.0, rz: 1.4, skin: { base: '#8d8a93', lit: '#c3c0c9', dark: '#4a4852' } },
    { kind: 'ball', at: [6.0, 6.4, 1.1], r: 1.5, ry: 0.9, rz: 0.5, skin: { base: '#a8a5ae', lit: '#d8d5de' } },
    { kind: 'rod', a: [4.2, 8.2, 0.6], b: [9.8, 6.8, 0.6], r: 0.35, skin: { base: '#6a6772', dark: '#3a3842' } },
  ],
  glim: () => [   // glim: a CLUSTER of cut nuggets, not one lump
    { kind: 'ball', at: [4.8, 4.2, 0], r: 2.3, ry: 2.1, rz: 1.8, skin: { base: '#e8c85a', lit: '#fffbe8', dark: '#8a6818' } },
    { kind: 'ball', at: [9.2, 4.8, 0], r: 2.1, ry: 1.9, rz: 1.6, skin: { base: '#e8c85a', lit: '#fffbe8', dark: '#8a6818' } },
    { kind: 'ball', at: [7, 8.0, 0.6], r: 2.4, ry: 2.2, rz: 2.0, skin: { base: '#e8c85a', lit: '#fffbe8', dark: '#8a6818' } },
    { kind: 'ball', at: [6.2, 9.0, 2.4], r: 0.7, skin: { base: '#fff8e0' } },
    { kind: 'ball', at: [4.0, 5.0, 2.0], r: 0.5, skin: { base: '#fff8e0' } },
  ],
  /* THE FOUR CONSUMABLES (LS-335, fourth batch). Each is a VESSEL, which is
   * what separates them at a glance from the charms (a bead, a shell, a
   * feather) and the materials: a stopped pot reads as something you spend.
   * Built from balls rather than boxes, which is what the icon renderer lights
   * -- the first hone charm was drawn from boxes and read as a dark blob. */
  salve: () => [   // bone salve: a cloth poultice, gathered and tied, green through the weave
    /* Y IS UP IN A SCENE (2026-09-10). The first cut of these four put the
     * jars' bodies high and their stoppers, wicks and embers LOW, and the
     * sheet showed four upside-down vessels -- the pitch pot read as a burnt
     * lump with a spark under it. `pod` says which way round it is in its own
     * comment: "a loop above" is the rod at y 12.2, and the pod hangs below it
     * at 6.4. The moth's feelers agree, running 10.6 to 12.8.
     *
     * AND THE SALVE IS NOT A VESSEL AT ALL, because three of these four are.
     * As a jar it went through a boulder with lichen on it and then, with the
     * rim widened enough to read, a little table -- and even drawn properly it
     * would have been a third stoppered pot beside the pitch and the brine. A
     * tied cloth bundle is a silhouette none of the others share, and the
     * green through the weave is what says it is a medicine. */
    { kind: 'ball', at: [7, 4.6, 0], r: 4.0, ry: 3.4, rz: 3.0, skin: { base: '#cdc3a8', lit: '#f4eeda', dark: '#635b48' } },
    { kind: 'ball', at: [5.8, 3.4, 2.4], r: 1.9, ry: 1.5, rz: 0.9, skin: { base: '#9ecfad', lit: '#e2f6e6', dark: '#35603f' } },
    { kind: 'rod', a: [5.4, 8.0, 1.4], b: [8.6, 8.0, 1.4], r: 1.0, skin: { base: '#8d7a5e', lit: '#c0a982', dark: '#3c3222' } },
    { kind: 'rod', a: [7, 8.6, 1.4], b: [4.8, 11.4, 1.0], r: 0.7, skin: { base: '#ddd3bb', lit: '#fffbee', dark: '#776e58' } },
    { kind: 'rod', a: [7, 8.6, 1.4], b: [9.4, 11.2, 1.0], r: 0.7, skin: { base: '#d3c9b1', lit: '#f6f0dd', dark: '#6e6553' } },
    { kind: 'ball', at: [7.6, 5.8, 2.8], r: 0.9, skin: { base: '#fffbee' } },
  ],
  pitch: () => [   // pitch pot: a black-glazed pot, a rag wick, one ember at the top
    { kind: 'ball', at: [7, 4.6, 0], r: 3.8, ry: 3.4, rz: 3.0, skin: { base: '#2e2a30', lit: '#5e5660', dark: '#121016' } },
    { kind: 'ball', at: [7, 8.0, 0.4], r: 2.3, ry: 1.2, rz: 1.6, skin: { base: '#3a343c', lit: '#6c626e', dark: '#161320' } },
    { kind: 'rod', a: [7, 8.4, 2.0], b: [8.6, 11.2, 2.0], r: 0.6, skin: { base: '#a89578', lit: '#ddc9a4', dark: '#5a4c34' } },
    { kind: 'ball', at: [8.8, 11.8, 2.4], r: 1.2, ry: 1.2, rz: 1.0, skin: { base: '#ffb85c', lit: '#fff2cf', dark: '#a05a12' } },
    { kind: 'ball', at: [5.2, 3.4, 2.6], r: 0.9, skin: { base: '#4e4652' } },
  ],
  brine: () => [   // brine flask: green glass, corked, the water line showing
    { kind: 'ball', at: [7, 4.4, 0], r: 3.5, ry: 3.0, rz: 2.8, skin: { base: '#5f8f86', lit: '#a8ded0', dark: '#22443e' } },
    { kind: 'rod', a: [7, 7.0, 0.8], b: [7, 10.4, 0.8], r: 1.2, skin: { base: '#6d9c92', lit: '#b6e6d8', dark: '#2a4c46' } },
    { kind: 'ball', at: [7, 11.0, 0.8], r: 1.5, ry: 0.9, rz: 1.1, skin: { base: '#a08a5e', lit: '#d8c390', dark: '#544527' } },
    { kind: 'rod', a: [4.3, 4.8, 2.2], b: [9.7, 4.8, 2.2], r: 0.45, skin: { base: '#cfeee4', lit: '#ffffff' } },
    { kind: 'ball', at: [5.5, 3.4, 2.4], r: 0.8, skin: { base: '#e6fbf4' } },
  ],
  draught: () => [   // ash draught: a footed horn cup of grey liquor, a wisp coming off it
    { kind: 'rod', a: [4.4, 1.8, 1.2], b: [9.6, 1.8, 1.2], r: 0.8, skin: { base: '#7e6d52', lit: '#b8a380', dark: '#3c3222' } },
    { kind: 'rod', a: [7, 2.4, 1.2], b: [7, 4.6, 1.2], r: 0.9, skin: { base: '#8d7a5e', lit: '#c8b28a', dark: '#463a26' } },
    { kind: 'ball', at: [7, 7.0, 0], r: 4.1, ry: 2.8, rz: 2.6, skin: { base: '#9c8a6c', lit: '#dcc79e', dark: '#4a3d2c' } },
    { kind: 'rod', a: [2.9, 8.6, 1.6], b: [11.1, 8.6, 1.6], r: 0.8, skin: { base: '#ab9878', lit: '#e8d4ac', dark: '#544530' } },
    { kind: 'ball', at: [7, 9.0, 2.0], r: 3.0, ry: 1.4, rz: 0.8, skin: { base: '#67636b', lit: '#a6a2ab', dark: '#2c2833' } },
    { kind: 'rod', a: [7.4, 9.8, 2.6], b: [6.0, 12.4, 2.6], r: 0.7, skin: { base: '#9a96a2', lit: '#d6d2de' } },
  ],
  cuttings: () => [   // thorn cuttings: three cut stems bound at the middle, barbs out
    { kind: 'rod', a: [3.2, 2.0, 0], b: [9.6, 11.4, 0], r: 0.75, skin: { base: '#5f7a3e', lit: '#9ab86a', dark: '#2a3a1a' } },
    { kind: 'rod', a: [4.6, 1.6, 0.6], b: [11.0, 10.6, 0.6], r: 0.7, skin: { base: '#55703a', lit: '#8fae62', dark: '#24321a' } },
    { kind: 'rod', a: [2.4, 4.0, -0.4], b: [8.4, 12.4, -0.4], r: 0.7, skin: { base: '#66804a', lit: '#a3bf74', dark: '#2c3c1e' } },
    { kind: 'rod', a: [5.2, 6.0, 1.6], b: [8.6, 7.6, 1.6], r: 0.55, skin: { base: '#8d7a5e', lit: '#c0a982', dark: '#3c3222' } },
    { kind: 'ball', at: [10.4, 4.2, 0.8], r: 0.6, skin: { base: '#c9d98a', lit: '#f0f8c0' } },
    { kind: 'ball', at: [3.6, 9.4, 0.8], r: 0.6, skin: { base: '#c9d98a', lit: '#f0f8c0' } },
  ],
  rod: () => [   // the lightning rod (LS-381): a dark shaft with a copper tip, on a stone foot -- one straight line
    { kind: 'box', at: [7, 1.2, 0], w: 5.0, h: 2.0, d: 3.0, skin: { base: '#5a5664', lit: '#8a8694', dark: '#2a2630' } },
    { kind: 'rod', a: [7, 2.0, 0], b: [7, 12.6, 0], r: 0.9, skin: { base: '#33303d', lit: '#5a5668', dark: '#16141c' } },
    { kind: 'ball', at: [7, 12.8, 0], r: 1.5, skin: { base: '#d08a4a', lit: '#ffd39a', dark: '#6a3c14' } },
  ],
  log: () => [   // a log (LS-384): a felled trunk lying across the cell, pale rings on its cut ends
    { kind: 'rod', a: [1.6, 4.5, 0], b: [12.4, 4.5, 0], r: 2.4, skin: { base: '#6a4a2a', lit: '#9a7a4a', dark: '#3a2412' } },
    { kind: 'ball', at: [12.6, 4.5, 0], r: 2.3, rz: 2.3, ry: 2.3, skin: { base: '#c9a56a', lit: '#f0d8a0', dark: '#8a6a3a' } },
    { kind: 'ball', at: [1.4, 4.5, 0], r: 2.1, skin: { base: '#5a3d22', lit: '#8a6a3a', dark: '#2a1a0c' } },
  ],
  raft: () => [   // the raft (LS-383): four lashed planks on two cross-poles, flat on the water
    { kind: 'box', at: [7, 3.2, 0], w: 12.0, h: 1.6, d: 8.0, skin: { base: '#8a6a3a', lit: '#c9a56a', dark: '#4a3218' } },
    { kind: 'rod', a: [1.6, 4.2, -2.6], b: [12.4, 4.2, -2.6], r: 0.5, skin: { base: '#5a3d22', dark: '#2a1a0c' } },
    { kind: 'rod', a: [1.6, 4.2, 2.6], b: [12.4, 4.2, 2.6], r: 0.5, skin: { base: '#5a3d22', dark: '#2a1a0c' } },
    { kind: 'rod', a: [4.4, 4.3, -4.2], b: [4.4, 4.3, 4.2], r: 0.4, skin: { base: '#c9a56a', dark: '#4a3218' } },
    { kind: 'rod', a: [9.6, 4.3, -4.2], b: [9.6, 4.3, 4.2], r: 0.4, skin: { base: '#c9a56a', dark: '#4a3218' } },
  ],
  boat: () => [   // the boat (LS-383): a dark hull with a pale gunwale and a thwart across it
    { kind: 'ball', at: [7, 4.0, 0], r: 6.4, ry: 2.8, rz: 3.6, skin: { base: '#5a3d22', lit: '#8a6a3a', dark: '#2a1a0c' } },
    { kind: 'ball', at: [7, 5.6, 0], r: 5.4, ry: 1.2, rz: 2.6, skin: { base: '#2a1e22', lit: '#3a2e32', dark: '#16101a' } },
    { kind: 'rod', a: [1.2, 6.2, 0], b: [12.8, 6.2, 0], r: 0.6, skin: { base: '#c9a56a', lit: '#f0d8a0', dark: '#6a4a1a' } },
    { kind: 'box', at: [7, 6.0, 0], w: 1.6, h: 0.8, d: 5.0, skin: { base: '#a07a44', lit: '#d0b070', dark: '#4a3218' } },
  ],
  stone: () => [   // stone as a hewn BLOCK that fills the cell (round 52's asset pass): a squared face, a chipped corner, a chisel line
    { kind: 'box', at: [7, 6.0, 0], w: 8.0, h: 6.4, d: 4.2, skin: { base: '#8a8894', lit: '#d0cedb', dark: '#33313d' } },
    { kind: 'ball', at: [11.0, 9.2, 2.2], r: 1.3, ry: 0.9, rz: 1.0, skin: { base: '#6e6c78', lit: '#a3a1ad', dark: '#33313d' } },
    { kind: 'rod', a: [3.6, 5.6, 4.4], b: [9.4, 5.0, 4.4], r: 0.35, skin: { base: '#5e5c68', dark: '#33313d' } },
    { kind: 'ball', at: [4.0, 8.4, 4.4], r: 0.7, skin: { base: '#e2e0ea', lit: '#ffffff' } },
  ],
  sap: () => [   // sap: a bead of amber resin with a bright facet
    { kind: 'ball', at: [7, 6.0, 0], r: 3.8, ry: 4.2, rz: 3.0, skin: { base: '#d9a44a', lit: '#ffe8a8', dark: '#6a4610' } },
    { kind: 'ball', at: [7, 10.2, 0], r: 1.6, ry: 1.4, rz: 1.4, skin: { base: '#c8944a', lit: '#e8bc6a', dark: '#7a5620' } },
    { kind: 'ball', at: [5.6, 7.4, 2.4], r: 1.0, skin: { base: '#fff2cf' } },
    { kind: 'ball', at: [9.0, 4.4, 1.6], r: 0.6, skin: { base: '#8a6220' } },
  ],
  skull: () => [
    { kind: 'box', at: [7, 0, 1.2], w: 4.2, h: 1.7, d: 1.6, skin: { base: '#c9c0aa', lit: '#e6dfcc', dark: '#8a8170' } },
    { kind: 'ball', at: [7, 3.4, 0], r: 3.5, ry: 3.3, rz: 3, skin: { base: '#d8d0bc', lit: '#f2ecdc', dark: '#9a917e' } },
    { kind: 'ball', at: [5.6, 3.6, 2.8], r: 0.95, ry: 1.1, rz: 0.6, skin: { base: '#241a24' } },
    { kind: 'ball', at: [8.4, 3.6, 2.8], r: 0.95, ry: 1.1, rz: 0.6, skin: { base: '#241a24' } },
    { kind: 'ball', at: [7, 2.2, 3.1], r: 0.5, skin: { base: '#241a24' } },
  ],
};
const ICON_W = 14, ICON_H = 14;
const ICON_CACHE = new Map<string, M3.Sheet>();
function mixTo(hex: string, to: string, k: number): string {
  const a = parseInt(hex.slice(1, 7), 16), b = parseInt(to.slice(1, 7), 16);
  const ch = (sh: number) => Math.round(((a >> sh) & 255) * (1 - k) + ((b >> sh) & 255) * k);
  return '#' + [16, 8, 0].map(sh => ch(sh).toString(16).padStart(2, '0')).join('');
}
export function itemIcon(kind: string, lit = true): M3.Sheet | null {
  const key = kind + (lit ? '' : '~');
  const hit = ICON_CACHE.get(key); if (hit) return hit;
  const build = ICON_SCENES[kind]; if (!build) return null;
  const sheet = M3.renderScene(ICON_W, ICON_H, build(), 0, ICON_H - 1, '#241a24');
  if (!lit) sheet.px = sheet.px.map(c => c && mixTo(c, '#332c40', 0.6));
  ICON_CACHE.set(key, sheet);
  return sheet;
}
export function drawWeapon(g, kind, x, y, lit = true) {
  const sheet = itemIcon(kind, lit); if (!sheet) return;
  M3.blitRuns(sheet, (px2, py, w, c) => { g.fillStyle = c; g.fillRect(x + px2, y + py, w, 1); });
}

export function drawGlim(g, x, y, s = 16) {
  const u = 1;   // always 1x: at u=2 this was the only double-density art
  const rows = [
    '...##...',
    '..####..',
    '.######.',
    '########',
    '.######.',
    '..####..',
    '...##...',
    '....#...',
  ];
  // outline first, then the body, then one bright facet: a cut shard reads as
  // treasure where a four-point star read as a medical cross
  g.fillStyle = '#241a24';
  for (let ry = 0; ry < rows.length; ry++) {
    for (let rx = 0; rx < 8; rx++) {
      if (rows[ry][rx] !== '#') continue;
      g.fillRect(x + rx * u - 1, y + ry * u - 1, u + 2, u + 2);
    }
  }
  g.fillStyle = '#f2c14e';
  for (let ry = 0; ry < rows.length; ry++) {
    for (let rx = 0; rx < 8; rx++) {
      if (rows[ry][rx] === '#') g.fillRect(x + rx * u, y + ry * u, u, u);
    }
  }
  g.fillStyle = '#fff3c8';
  g.fillRect(x + 3 * u, y + 1 * u, u, u);
  g.fillRect(x + 2 * u, y + 2 * u, u, u);
  g.fillStyle = '#b8862f';
  g.fillRect(x + 4 * u, y + 4 * u, u, u);
  g.fillRect(x + 3 * u, y + 5 * u, u * 2, u);
}

/** Sigil: a rune tag that opens one sealed door. */
export function drawSigil(g, x, y) {
  /* A TAG IS A THIN SLAB, AND A SLAB STANDS (LS-199). The rune stayed a flat
   * rectangle because the mark cut into it has to stay legible at eight
   * pixels -- but a thing standing in the world takes the same light as
   * everything else: a lit top edge where it faces the sky, a shaded right
   * side, and its own shadow on the ground under it. */
  const putSg = (px2: number, py: number, c: string) => { g.fillStyle = c; g.fillRect(px2, py, 1, 1); };
  M3.groundShadow([8, 0, 1.5], 4, x, y + 14, putSg, '#00000030');
  g.fillStyle = '#241a24';
  g.fillRect(x + 3, y + 1, 10, 13);
  g.fillStyle = '#9a7bd8';
  g.fillRect(x + 4, y + 2, 8, 11);
  g.fillStyle = M3.lighten('#9a7bd8', 0.3);
  g.fillRect(x + 4, y + 2, 8, 1);                       // the top edge, to the sky
  g.fillStyle = '#00000045';
  g.fillRect(x + 11, y + 3, 1, 10);                     // the shaded side
  g.fillStyle = '#e6dcff';
  g.fillRect(x + 7, y + 3, 2, 8);
  g.fillRect(x + 5, y + 6, 6, 2);
  g.fillStyle = '#241a24';
  g.fillRect(x + 7, y + 6, 2, 2);
}

/** Seed pod: the bomb. */
/* A POD LYING ON THE GROUND (LS-199). It was a square of green with a lighter
 * square inside it, which at this size is a crate again -- and a pickup that
 * reads as a box in a world of round things is exactly the kind of small
 * disagreement this pass is here to end. A ball with a woody stalk, through
 * the one camera, sitting in its own shadow. */
export function drawPod(g, x, y) {
  const put = (px2: number, py: number, c: string) => { g.fillStyle = c; g.fillRect(x + px2, y + py, 1, 1); };
  M3.groundShadow([8, 0, 1], 4.2, 0, 14, put, '#00000030');
  M3.blit(M3.renderScene(16, 16, [
    { kind: 'ball', at: [8, 3.4, 0], r: 4.4, rz: 3.6, ry: 4, skin: { base: '#6f8f4a', lit: '#95b862', dark: '#5b7a3a' } },
    { kind: 'rod', a: [8, 6.5, 0], b: [8.6, 9.5, -0.5], r: 1.1, skin: { base: '#8a6b3a', lit: '#c8a05a', dark: '#5f4a28' } },
  ], 0, 14, '#241a24'), put);
}

// The win screen and the title drew the seed with `u = round(s / 16)`, so at
// s=32 the trophy was a 16x16 sprite in 2x2 blocks — the whole picture at half
// the resolution of the 1px text sitting under it. A bigger seed is a bigger
// DRAWING now: 26x31, one pixel per pixel, sprout and shading included.
const SEED_BIG = [
  '..........................',
  '........oG......Go........',
  '.......oGGo....oGGo.......',
  '........oGGo..oGGo........',
  '.........oGGooGGo.........',
  '..........oGGGGo..........',
  '.........oooooooo.........',
  '.......oohhyyyyyyoo.......',
  '.......ohhhhyyyyyyo.......',
  '......ohhhhhyyyyyyyo......',
  '......ohhhhhyyyyyyyo......',
  '.....oyhhhhhyyyyyyyyo.....',
  '.....oyhhhhhyyyyyyyyo.....',
  '.....oyhhhhhyyyyyyyyo.....',
  '.....oyyhhhyyyyyyyyyo.....',
  '.....oyyyhyyyyyyyyyyo.....',
  '.....oyyyyyyyyyyyyyyo.....',
  '.....oyyyyyyyyyyyyydo.....',
  '......oyyyyyyyyyyydo......',
  '......oyyyyyyyyyyydo......',
  '......oyyyyyyyyyyddo......',
  '......oyyyyyyyyydddo......',
  '.......oyyyyyyyyddo.......',
  '.......oyyyyyyydddo.......',
  '.......oyyyyyyddddo.......',
  '........oyyyyydddo........',
  '........oyyyyddddo........',
  '.........oyyddddo.........',
  '..........oydddo..........',
  '..........oddddo..........',
  '...........oooo...........',
];
export const SEED_BIG_W = 26, SEED_BIG_H = SEED_BIG.length;

/** The 26x31 seed, for the title and the win screen. */
export function drawSeedBig(g, x, y, glow = false) {
  const col = {
    o: '#241a24',
    G: glow ? '#a8e07a' : '#6f8f4a',
    h: glow ? '#ffffff' : '#fff3c8',
    y: glow ? '#fff3c8' : '#e8c05a',
    d: glow ? '#e8c05a' : '#b8862f',
  };
  for (let ry = 0; ry < SEED_BIG.length; ry++) {
    for (let rx = 0; rx < SEED_BIG_W; rx++) {
      const ch = SEED_BIG[ry][rx];
      if (ch === '.') continue;
      g.fillStyle = col[ch];
      g.fillRect(x + rx, y + ry, 1, 1);
    }
  }
}

/** Seed: the goal, one per hollow. */
export function drawSeed(g, x, y, s = 16, glow = false) {
  // Always 1x. `s` is kept for call-site compatibility and for the assertion:
  // anything wanting a bigger seed wants `drawSeedBig`, not a bigger pixel.
  if (s > 16) throw new Error('drawSeed is 16px art; use drawSeedBig for ' + s);
  const u = 1;
  g.fillStyle = '#241a24';
  g.fillRect(x + 4 * u, y + 2 * u, 8 * u, 12 * u);
  g.fillRect(x + 3 * u, y + 5 * u, 10 * u, 6 * u);
  g.fillStyle = glow ? '#fff3c8' : '#e8c05a';
  g.fillRect(x + 5 * u, y + 3 * u, 6 * u, 10 * u);
  g.fillRect(x + 4 * u, y + 6 * u, 8 * u, 4 * u);
  g.fillStyle = glow ? '#e8c05a' : '#b8862f';
  g.fillRect(x + 6 * u, y + 8 * u, 4 * u, 4 * u);
  g.fillStyle = '#fff3c8';
  g.fillRect(x + 6 * u, y + 4 * u, 2 * u, 2 * u);
  g.fillStyle = '#6f8f4a';
  g.fillRect(x + 7 * u, y, 2 * u, 3 * u);
}

/** A pixel-snapped crescent swung from the hand. Drawn with `g.rotate` and
 *  `fillRect` it was the only anti-aliased geometry in the game — soft grey
 *  edges, then upscaled 2-3x by `image-rendering: pixelated`. */
export function drawSickle(g, cx, cy, angle, reach, upgraded, charged) {
  const edge = charged ? '#ffffff' : upgraded ? '#ffe9a8' : '#e2e8ec';
  const back = upgraded ? '#ff8c3c' : '#96a2ac';
  const grip = upgraded ? '#c85820' : '#8a6b3a';
  // the hand sits ahead of the body, offset across the swing
  const hx = cx + Math.cos(angle) * 5, hy = cy + Math.sin(angle) * 5;
  const blob = (x, y, w, h, col) => {
    g.fillStyle = col;
    g.fillRect(Math.round(x), Math.round(y), w, h);
  };
  blob(hx - 1, hy - 1, 3, 3, '#241a24');
  blob(hx - 1, hy - 1, 2, 2, grip);
  for (let i = 0; i <= 9; i++) {
    const t = i / 9;
    const r = 5 + t * (reach - 5);
    const a = angle + (t - 0.45) * 1.05;          // the blade curves as it sweeps
    const x = cx + Math.cos(a) * r, y = cy + Math.sin(a) * r;
    blob(x - 1.5, y - 1.5, 3, 3, '#241a24');
    blob(x - 1, y - 1, 2, 2, i > 6 ? edge : back);
  }
  // the tip hooks back on itself
  const ta = angle + 0.62, tr = reach - 1;
  blob(cx + Math.cos(ta) * tr - 1, cy + Math.sin(ta) * tr - 1, 2, 2, edge);
  const ha = angle + 0.5, hr = reach - 4;
  blob(cx + Math.cos(ha) * hr - 1, cy + Math.sin(ha) * hr - 1, 2, 2, edge);
  // a charged blade throws: show it on the weapon rather than captioning it
  if (charged) {
    for (let i = 0; i < 3; i++) {
      const a = angle + 0.62 - i * 0.22, r = reach + 1 + i;
      blob(cx + Math.cos(a) * r - 1, cy + Math.sin(a) * r - 1, 2, 2, i ? '#8fd6ff' : '#ffffff');
    }
  }
}


/* --- THE EIGHT-FRAME ROSTER WINS.
 *
 * Assigned at the END of this file, after every `S.<actor>_0` / `_1` above,
 * so the modules in `src/sprites/` override the two-frame keyframes rather
 * than being overwritten by them. Those keyframes are not dead: every cycle
 * was authored around them and phase 0 is generally the same matrix. What is
 * gone is the game only ever showing two of the eight.
 */
Object.assign(S, SPRITES);
